import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  BookOpen,
  Coins,
  Code2,
  Dumbbell,
  Globe2,
  Hammer,
  Home as HomeIcon,
  LampDesk,
  PawPrint,
  Scale,
  Sparkles,
  Stethoscope,
  Search as SearchIcon,
  Facebook,
  BriefcaseBusiness,
  List,
  Send,
  Bell,
  CheckCircle2,
  ClipboardList,
  HandCoins,
  Users,
  ShieldCheck,
  Star,
  Clock,
  UserCheck,
  Filter,
  Check,
  Info,
  ArrowRight,
  Mail,
  MapPin
} from 'lucide-react'
import { Link, Routes, Route, useLocation, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import ReviewPage from './pages/Review'
import Turnstile from './components/Turnstile'
import { UI_LANGUAGE_OPTIONS, useUiLanguage, type UiLanguageCode } from './i18n'
import './styles.css'
import './banner.css'

const SITE_URL = 'https://eucamatch.com'
const GA_MEASUREMENT_ID = 'G-PH8P0Y2GK7'
const SEARCH_HINTS = ['Tax', 'Tutor', 'Lawyer', 'Dietitian', 'Music', 'Physio', 'Migration', 'Nurse', 'IELTS', 'Mortgage']
const AI_SEARCH_COUNTRIES = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'US', label: 'the US' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'the UK' },
  { value: 'IE', label: 'Ireland' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
]
const CONTACT_SLOT = 'REQUEST_BOOKING'
const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SHOW_AI_SEARCH_BUTTON = true
const MAX_SHOWCASE_CARDS = 8
const REVIEWS_PAGE_SIZE = 10
const REVIEWS_FETCH_LIMIT = 50
const MAX_REVIEWS = 500
const LICENSE_VERIFIED_TOOLTIP =
  'License Verified: This professional’s license has been verified by EUCA Match.'

const shouldUseLiteMobileMode = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const nav = navigator as any
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection
  const saveData = Boolean(connection?.saveData)
  const effectiveType = String(connection?.effectiveType || '').toLowerCase()
  const slowNetwork = effectiveType.includes('2g')
  const deviceMemory = Number(nav.deviceMemory || 0)
  const lowMemory = Number.isFinite(deviceMemory) && deviceMemory > 0 && deviceMemory <= 4
  const cores = Number(nav.hardwareConcurrency || 0)
  const lowCpu = Number.isFinite(cores) && cores > 0 && cores <= 4
  const mobileViewport = window.matchMedia('(max-width: 900px)').matches
  return mobileViewport && (saveData || slowNetwork || lowMemory || lowCpu)
}

const preferredScrollBehavior = (): ScrollBehavior =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('perf-lite')
    ? 'auto'
    : 'smooth'

function useRotatingPlaceholder(enabled: boolean, hints: string[], delay = 1000) {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    if (!enabled || !hints.length) return undefined
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % hints.length)
    }, delay)
    return () => clearInterval(t)
  }, [enabled, hints, delay])
  return hints.length ? hints[idx] : ''
}

function usePageMeta(title: string, description: string, path = '', image?: string) {
  useEffect(() => {
    if (title) {
      document.title = title
    }
    const ensureMeta = (selector: string, attr: 'content' | 'href', value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null
      if (!el) {
        if (selector.startsWith('meta')) {
          el = document.createElement('meta') as any
          const match = selector.match(/\[(name|property)=\"([^\"]+)\"\]/)
          if (match) {
            el.setAttribute(match[1], match[2])
          }
          document.head.appendChild(el)
        } else if (selector.startsWith('link')) {
          el = document.createElement('link') as any
          el.setAttribute('rel', 'canonical')
          document.head.appendChild(el)
        }
      }
      if (el && value) {
        el.setAttribute(attr, value)
      }
    }

    if (description) {
      ensureMeta('meta[name="description"]', 'content', description)
      ensureMeta('meta[property="og:description"]', 'content', description)
      ensureMeta('meta[name="twitter:description"]', 'content', description)
    }
    if (title) {
      ensureMeta('meta[property="og:title"]', 'content', title)
      ensureMeta('meta[name="twitter:title"]', 'content', title)
    }
    const canonicalUrl = path ? `${SITE_URL}${path}` : SITE_URL
    ensureMeta('link[rel="canonical"]', 'href', canonicalUrl)
    ensureMeta('meta[property="og:url"]', 'content', canonicalUrl)
    const img = image || `${SITE_URL}/home1.png`
    ensureMeta('meta[property="og:image"]', 'content', img)
    ensureMeta('meta[name="twitter:image"]', 'content', img)
    ensureMeta('meta[name="twitter:card"]', 'content', 'summary_large_image')
  }, [title, description, path])
}

function useManagedToolVideos(selector: string) {
  useEffect(() => {
    const videos = Array.from(document.querySelectorAll<HTMLVideoElement>(selector))
    if (!videos.length) return undefined
    const liteMode = typeof document !== 'undefined' && document.documentElement.classList.contains('perf-lite')

    const ensureMetadata = (video: HTMLVideoElement) => {
      if (video.dataset.metadataReady === 'true') return
      video.preload = 'metadata'
      video.load()
      video.dataset.metadataReady = 'true'
    }

    const pauseOthers = (active: HTMLVideoElement) => {
      videos.forEach((video) => {
        if (video !== active && !video.paused) video.pause()
      })
    }

    const onPlay = (event: Event) => {
      const current = event.currentTarget as HTMLVideoElement | null
      if (!current) return
      ensureMetadata(current)
      pauseOthers(current)
    }

    const onIntent = (event: Event) => {
      const target = event.currentTarget as HTMLVideoElement | null
      if (!target) return
      ensureMetadata(target)
    }

    if (liteMode) {
      videos.forEach((video) => {
        video.muted = true
        video.playsInline = true
        video.autoplay = false
        video.preload = 'none'
        video.pause()
        video.addEventListener('play', onPlay)
        video.addEventListener('pointerdown', onIntent)
        video.addEventListener('focus', onIntent)
      })
      return () => {
        videos.forEach((video) => {
          video.removeEventListener('play', onPlay)
          video.removeEventListener('pointerdown', onIntent)
          video.removeEventListener('focus', onIntent)
        })
      }
    }

    const autoplayObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement
          if (entry.isIntersecting) {
            ensureMetadata(video)
            pauseOthers(video)
            const playPromise = video.play()
            if (playPromise) playPromise.catch(() => undefined)
          } else if (!video.paused) {
            video.pause()
          }
        })
      },
      { threshold: 0.45, rootMargin: '0px 0px -12% 0px' }
    )

    const preloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const video = entry.target as HTMLVideoElement
          ensureMetadata(video)
          preloadObserver.unobserve(video)
        })
      },
      { threshold: 0.01, rootMargin: '320px 0px' }
    )

    videos.forEach((video) => {
      video.muted = true
      video.playsInline = true
      video.autoplay = true
      video.preload = 'none'
      video.addEventListener('play', onPlay)
      video.addEventListener('pointerdown', onIntent)
      video.addEventListener('focus', onIntent)
      preloadObserver.observe(video)
      autoplayObserver.observe(video)
    })

    return () => {
      preloadObserver.disconnect()
      autoplayObserver.disconnect()
      videos.forEach((video) => {
        video.removeEventListener('play', onPlay)
        video.removeEventListener('pointerdown', onIntent)
        video.removeEventListener('focus', onIntent)
      })
    }
  }, [selector])
}

const heroImage = `${import.meta.env.BASE_URL}home1.png`
const secondaryImage = `${import.meta.env.BASE_URL}home2.png`
const test2HeroImage = `${import.meta.env.BASE_URL}Screen2.png`
const test2WhyImage = `${import.meta.env.BASE_URL}Screen2.png`
const invoiceVideoPoster = `${import.meta.env.BASE_URL}1-invoice-poster.jpg`
const calendarVideoPoster = `${import.meta.env.BASE_URL}2-calendar-poster.jpg`
const notesVideoPoster = `${import.meta.env.BASE_URL}3-notes-poster.jpg`
const meetingVideoPoster = `${import.meta.env.BASE_URL}4-meeting-poster.jpg`
const invoiceVideoSrc = `${import.meta.env.BASE_URL}1-invoice.mp4`
const calendarVideoSrc = `${import.meta.env.BASE_URL}2-calendar.mp4`
const notesVideoSrc = `${import.meta.env.BASE_URL}3-notes.mp4`
const meetingVideoSrc = `${import.meta.env.BASE_URL}4-meeting.mp4`
const industriesImage = `${import.meta.env.BASE_URL}home1.png`
const test2IndustriesImage = `${import.meta.env.BASE_URL}Professionals.png`
const professionalsHeroImage = `${import.meta.env.BASE_URL}Screen2.png`
const professionalImage = `${import.meta.env.BASE_URL}easyprofessional.png`
const clientImage = `${import.meta.env.BASE_URL}easyclient.png`
const doctorImage = `${import.meta.env.BASE_URL}doctor.png`
const vetsImage = `${import.meta.env.BASE_URL}vet.png`
const lawImage = `${import.meta.env.BASE_URL}law.png`
const migrationImage = `${import.meta.env.BASE_URL}migration.png`
const financialImage = `${import.meta.env.BASE_URL}financial.png`
const educationImage = `${import.meta.env.BASE_URL}teacher.png`
const realEstateImage = `${import.meta.env.BASE_URL}realestate.png`
const homeDesignImage = `${import.meta.env.BASE_URL}housedesign.png`
const techCreativeImage = `${import.meta.env.BASE_URL}IT.png`
const wellnessImage = `${import.meta.env.BASE_URL}wellnesscoach.png`
const beautyImage = `${import.meta.env.BASE_URL}beautystyling.png`
const cofounderImage = `${import.meta.env.BASE_URL}cofounder.png`
const logoImage = `${import.meta.env.BASE_URL}logo.png`
const googleIconImage = `${import.meta.env.BASE_URL}gicon.png`
const newWebDesignBackgroundImage = `${import.meta.env.BASE_URL}today-collage-2-1024.jpg`
const newWebDesignBackgroundImageMobile = `${import.meta.env.BASE_URL}today-collage-2-640.jpg`
const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" role="img" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 8.5c-1.9 0-3.7-.6-5.2-1.8v7.3c0 3-2.3 5.5-5.3 5.5-1.1 0-2-.3-2.8-.8-.9-.6-1.7-1.5-2-2.6-.3-1.1-.3-2.3.1-3.4.8-2.1 2.8-3.4 4.9-3.4.3 0 .7 0 1 .1v3a2 2 0 0 0-1-.3c-1.1 0-2 .7-2.3 1.8-.2.5-.2 1 .1 1.5.3.4.7.7 1.2.7 1.1 0 2-.9 2-2V3h3.2c.3 1.8 1.8 3.2 3.6 3.5v2z"
    />
  </svg>
)

const FacebookBrandIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" role="img" aria-hidden="true">
    <rect width="48" height="48" rx="10" fill="#1877F2" />
    <path
      fill="#fff"
      d="M28.6 15.5H32V9h-4.3c-5 0-7.5 3.7-7.5 7.9v3H16v6h4.2V39h6.3V25.9h4.5l.7-6h-5.2v-2.8c0-1.4.4-1.6 1.6-1.6z"
    />
  </svg>
)

const ProCta: React.FC<{ className?: string; onClick?: () => void }> = ({ className = '', onClick }) => (
  <a className={`btn cta pro-cta ${className}`.trim()} href={providerSignupUrl} onClick={onClick}>
    For Professionals
  </a>
)

const navLinks = [
  { label: 'Product', href: '#' },
  { label: 'Resources', href: '#' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Company', href: '#' },
  { label: 'Sign In', href: 'https://app.eucamatch.com/login' }
] as const

const linkedinBrandImage = `${import.meta.env.BASE_URL}LI.png`

const loginUrl = 'https://app.eucamatch.com/login'
const clientSignupUrl = 'https://app.eucamatch.com/login?exp=client&next=/clients'
const marketingSearchUrl = 'https://eucamatch.com/search'
const providerSignupUrl = 'https://app.eucamatch.com/login?exp=provider&next=%2Fprofessionals%23categories'
const DOCTOR_PUBLIC_API = (
  import.meta.env.VITE_DOCTOR_API_BASE ||
  import.meta.env.VITE_API_DOCTOR_BASE ||
  'https://eirbp7nixi.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const EDUCATION_PUBLIC_API = (
  import.meta.env.VITE_EDUCATION_API_BASE ||
  'https://z4mvsuxejd.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const ALLIED_PUBLIC_API = (
  import.meta.env.VITE_ALLIED_API_BASE ||
  import.meta.env.VITE_API_ALLIED_BASE ||
  'https://qrkhzu0ood.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const MIGRATION_PUBLIC_API = (
  import.meta.env.VITE_MIGRATION_API_BASE ||
  import.meta.env.VITE_API_MIGRATION_BASE ||
  'https://h1edp1rb30.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const FINANCIAL_PUBLIC_API = (
  import.meta.env.VITE_FINANCIAL_API_BASE ||
  import.meta.env.VITE_API_FINANCIAL_BASE ||
  'https://2o867cmckl.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const LEGAL_PUBLIC_API = (
  import.meta.env.VITE_LEGAL_API_BASE ||
  import.meta.env.VITE_API_LEGAL_BASE ||
  'https://n57svpmya0.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const TECH_PUBLIC_API = (
  import.meta.env.VITE_TECH_API_BASE ||
  import.meta.env.VITE_API_TECH_BASE ||
  'https://vkok3p97il.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const PROPERTY_PUBLIC_API = (
  import.meta.env.VITE_PROPERTY_API_BASE ||
  import.meta.env.VITE_API_PROPERTY_BASE ||
  'https://7whrbgdtlb.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const SEARCH_PUBLIC_API = (
  import.meta.env.VITE_SEARCH_API_BASE ||
  'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const STACK_API_BASES: Record<string, string> = {
  doctor: DOCTOR_PUBLIC_API,
  allied: ALLIED_PUBLIC_API,
  financial: FINANCIAL_PUBLIC_API,
  migration: MIGRATION_PUBLIC_API,
  legal: LEGAL_PUBLIC_API,
  education: EDUCATION_PUBLIC_API,
  property: PROPERTY_PUBLIC_API,
  tech: TECH_PUBLIC_API,
}
const PROFESSION_TO_STACK: Record<string, string> = {
  'Medical Doctors': 'doctor',
  'Allied Health': 'allied',
  'Migration & Visa': 'migration',
  'Legal, Governance & Compliance': 'legal',
  'Education & Tutoring': 'education',
  'Property, Design & Building': 'property',
  Financial: 'financial',
  'Tech, Creative & Media': 'tech',
}

type ShowcaseItem = {
  userId?: string
  name: string
  profession: string
  displayProfession?: string | null
  stack?: string
  specialty?: string | null
  title?: string | null
  specialties?: string[]
  location?: string
  country?: string | null
  countryCode?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  price?: string
  rating?: string
  ratingCount?: number | null
  image?: string | null
  profile?: string | null
  pricingNotes?: string | null
  slug: string
  availabilitySlots?: string[]
  responseTimeHours?: number | null
  responseTimeScore?: number | null
  responseTimeSampleSize?: number | null
}

type ShowcaseGroup = {
  title: string
  items: ShowcaseItem[]
  stack?: string
  qualifiedBookingCount?: number
}

type ShowcaseFetchResult = {
  items: ShowcaseItem[]
  qualifiedBookingCount: number
}

const STACK_TITLE_MAP: Record<string, string> = {
  doctor: 'Medical Doctors',
  allied: 'Allied Health',
  financial: 'Financial',
  education: 'Education & Tutoring',
  migration: 'Migration & Visa',
  property: 'Property, Design & Building',
  legal: 'Legal, Governance & Compliance',
  tech: 'Tech, Creative & Media',
}

const AVAILABILITY_STACK_KEYS = ['doctor', 'allied', 'financial', 'migration', 'legal', 'education', 'property', 'tech'] as const
const availabilityIndexByStack: Record<string, Map<string, string[]>> = Object.fromEntries(
  AVAILABILITY_STACK_KEYS.map((stack) => [stack, new Map<string, string[]>()])
)
const availabilityIndexLoadedByStack: Record<string, boolean> = Object.fromEntries(
  AVAILABILITY_STACK_KEYS.map((stack) => [stack, false])
)
const availabilityHydrationByStack: Record<string, Promise<ShowcaseFetchResult> | undefined> = {}

const parseQualifiedBookingCount = (json: any) =>
  Math.max(0, Number(json?.stackMetrics?.qualifiedBookingCount) || 0)

const normalizeAvailabilitySlots = (value: any): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((slot) => String(slot || '').trim())
    .filter(Boolean)
}

const rememberAvailabilityFromStackItems = (stack: string, items: any[]) => {
  if (!STACK_TITLE_MAP[stack]) return
  availabilityIndexLoadedByStack[stack] = true
  const bucket = availabilityIndexByStack[stack]
  if (!bucket) return
  items.forEach((item) => {
    const userId = String(item?.userId || '').trim()
    if (!userId) return
    const slots = normalizeAvailabilitySlots(item?.availabilitySlots || item?.availability)
    if (!slots.length) return
    bucket.set(userId, slots)
  })
}

const STATE_OPTIONS = [
  { value: 'NSW', label: 'NSW' },
  { value: 'VIC', label: 'VIC' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'WA', label: 'WA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'ACT', label: 'ACT' },
  { value: 'NT', label: 'NT' },
]

const showcaseAnchorId = (title: string) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const shuffle = <T,>(arr: T[]): T[] => {
  const copy = [...arr]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

const orderShowcase = (items: ShowcaseItem[]): ShowcaseItem[] => {
  const hasReviews = (item: ShowcaseItem) => (Number(item.ratingCount) || 0) > 0

  const rated = shuffle(items.filter(hasReviews))
  const unrated = items.filter((i) => !hasReviews(i))

  const orderByAvailability = (list: ShowcaseItem[]) => {
    const withAvailability = shuffle(list.filter((item) => hasAvailabilityListed(item)))
    const withoutAvailability = shuffle(list.filter((item) => !hasAvailabilityListed(item)))
    return [...withAvailability, ...withoutAvailability]
  }
  const unratedVerified = unrated.filter((item) => hasLicenseVerifiedBadge(item))
  const unratedOther = unrated.filter((item) => !hasLicenseVerifiedBadge(item))
  const orderedUnrated = [...orderByAvailability(unratedVerified), ...orderByAvailability(unratedOther)]
  return [...rated, ...orderedUnrated]
}

const renderStars = (rating?: string | number | null) => {
  const num = Number(rating)
  if (!Number.isFinite(num) || num <= 0) return null
  const val = Math.max(0, Math.min(5, num))
  const stars = Array.from({ length: 5 }, (_, idx) => {
    const n = idx + 1
    if (val >= n - 0.01) return 'full'
    if (val >= n - 0.5) return 'half'
    return 'empty'
  })
  return (
    <span className="star-row" aria-hidden="true">
      {stars.map((state, idx) => (
        <span key={idx} className={`star ${state}`}>★</span>
      ))}
    </span>
  )
}

const reviewCountLabel = (value?: string | number | null) => {
  const count = Number(value) || 0
  return `${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`
}

function FeaturedShowcase({
  groups,
  loading,
  error,
  onOpenReviews,
}: {
  groups: ShowcaseGroup[]
  loading?: boolean
  error?: string | null
  onOpenReviews?: (pro: ShowcaseItem) => void
}) {
  const hasContent = groups.some((g) => g.items.length > 0)
  const duplicateSlugs = React.useMemo(() => {
    const counts = new Map<string, number>()
    groups.forEach((group) => {
      ;(group.items || []).forEach((pro: any) => {
        if (!pro || pro.__placeholder) return
        if (!pro.slug) return
        const cleanSlug = String(pro.slug).replace(/-/g, '')
        counts.set(cleanSlug, (counts.get(cleanSlug) || 0) + 1)
      })
    })
    return new Set(Array.from(counts.entries()).filter(([, c]) => c > 1).map(([slug]) => slug))
  }, [groups])
  const scrollers = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollByAmount = (anchorId: string, dir: number) => {
    const el = scrollers.current[anchorId]
    if (el) el.scrollBy({ left: dir * 320, behavior: preferredScrollBehavior() })
  }
  return (
    <section className="showcase-section" id="featured">
      {error && <div className="banner error" role="alert">{error}</div>}
      {loading && !hasContent && <div className="muted">Loading professionals…</div>}
      {(loading || hasContent) && (
        <div className="showcase-stacks">
          {groups.map((group) => {
            if (!group.items.length) return null
            const anchorId = showcaseAnchorId(group.stack ? STACK_TITLE_MAP[group.stack] || group.stack : group.title)
            const visible = group.items.slice(0, MAX_SHOWCASE_CARDS)
            const showSeeMore = group.items.length > MAX_SHOWCASE_CARDS && !!group.stack
            const placeholders = visible.length ? [] : Array.from({ length: 6 }, (_, i) => ({ __placeholder: true, id: i }))
            const carouselItems = visible.length ? visible : placeholders
            return (
              <div className="showcase-stack" id={anchorId} key={group.stack || group.title}>
                <span id={anchorId} className="anchor-target" aria-hidden="true" />
                <div className="showcase-stack-head">
                  {group.stack ? (
                    <Link
                      className="showcase-title-link"
                      to={`/search?profession=${encodeURIComponent(group.stack)}`}
                    >
                      <span className="showcase-title">{group.title}</span>
                      <ArrowRight className="showcase-title-arrow" aria-hidden="true" />
                    </Link>
                  ) : (
                    <h3 className="showcase-title">{group.title}</h3>
                  )}
                </div>
                <div className="showcase-carousel-wrap">
                  <button
                    className="showcase-nav"
                    aria-label="Scroll left"
                    onClick={() => scrollByAmount(anchorId, -1)}
                  >
                    ‹
                  </button>
                  <div className="showcase-carousel" role="list" ref={(el) => (scrollers.current[anchorId] = el)}>
                    {carouselItems.map((pro: any, idx) =>
                      pro.__placeholder ? (
                        <div className="showcase-card placeholder" role="listitem" key={`ph-${idx}`}>
                          <div className="showcase-photo skeleton" />
                          <div className="showcase-body">
                            <div className="showcase-row">
                              <div className="skeleton skeleton-text" style={{ width: '60%' }} />
                              <div className="skeleton skeleton-text" style={{ width: 50 }} />
                            </div>
                            <div className="skeleton skeleton-text" style={{ width: '90%', marginTop: 8 }} />
                            <div className="skeleton skeleton-text" style={{ width: '70%', marginTop: 6 }} />
                          </div>
                        </div>
                      ) : (
                        <Link
                          className="showcase-card"
                          key={(pro.userId || pro.name) + pro.profession}
                          role="listitem"
                          to={`/${(pro.slug || '').replace(/-/g, '')}${
                            duplicateSlugs.has((pro.slug || '').replace(/-/g, '')) && pro.stack
                              ? `?bio=${encodeURIComponent(pro.stack)}`
                              : ''
                          }`}
                          state={{ provider: pro }}
                        >
                          <div className="showcase-photo">
                            <img src={pro.image || logoImage} alt={`${pro.name} — ${pro.profession}`} loading="lazy" />
                            <span className="showcase-pill">{pro.profession}</span>
                          </div>
                          <div className="showcase-body">
                            <div className="showcase-row">
                              <h3 className="provider-name-line">
                                <span>{pro.name}</span>
                                <LicenseVerifiedBadge item={pro} />
                              </h3>
                            </div>
                            {Number(pro.rating) > 0 && (
                              <button
                                type="button"
                                className="showcase-rating reviews-link"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  onOpenReviews?.(pro)
                                }}
                              >
                                <span className="rating-row">
                                  <span className="value">{Number(pro.rating).toFixed(1)}</span>
                                  {renderStars(pro.rating)}
                                </span>
                                <span className="reviews-count">{reviewCountLabel(pro.ratingCount)}</span>
                              </button>
                            )}
                            <p className="showcase-specialty">
                              {pro.specialty ||
                                (pro.specialties || [])
                                  .filter(Boolean)
                                  .slice(0, 3)
                                  .join(' · ')}
                            </p>
                          </div>
                        </Link>
                      )
                    )}
                    {showSeeMore && group.stack ? (
                      <Link
                        className="showcase-card cta-card"
                        role="listitem"
                        to={`/search?profession=${encodeURIComponent(group.stack)}`}
                      >
                        <div className="showcase-cta">
                          <div className="showcase-cta-icon" aria-hidden="true">
                            <ArrowRight />
                          </div>
                          <div className="showcase-cta-title">See more professionals</div>
                          <div className="showcase-cta-sub">View all {group.title}</div>
                        </div>
                      </Link>
                    ) : null}
                  </div>
                  <button
                    className="showcase-nav"
                    aria-label="Scroll right"
                    onClick={() => scrollByAmount(anchorId, 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {!loading && !hasContent && !error && <div className="muted">No approved providers to show yet.</div>}
    </section>
  )
}

const industries = [
  { icon: Sparkles, label: 'Allied Health', value: 'allied', description: 'Physio, OT, dietetics, speech, diabetes educators, psychology/counselling.' },
  { icon: Stethoscope, label: 'Medical Doctors', value: 'doctor', description: 'Australian-registered doctors and specialists.' },
  { icon: BookOpen, label: 'Education & Tutoring', value: 'education', description: 'Tutors, language, exam prep, teachers — AU timezones.' },
  { icon: Coins, label: 'Financial', value: 'financial', description: 'Accountants, tax, financial planning, business advisory.' },
  { icon: Scale, label: 'Legal, Governance & Compliance', value: 'legal', description: 'Lawyers, contracts, disputes, NAATI/document translation, attestations.' },
  { icon: Globe2, label: 'Migration & Visa', value: 'migration', description: 'MARA agents, immigration lawyers, visa case advice.' },
  { icon: HomeIcon, label: 'Property, Design & Building', value: 'property', description: 'Buyers’ agents, property advisory, renovation consults.' },
  { icon: Code2, label: 'Tech, Creative & Media', value: 'tech', description: 'Developers, designers, product and growth specialists.' },
] as const

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
const cleanSlug = (value?: string | null) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
const addSamuelHartleyPerthLocation = (item: any, location?: string | null) => {
  const key = cleanSlug(item?.slug || item?.userId || item?.name || '')
  if (key !== 'samuelhartley') return location || null
  const base = String(location || '').replace(/\s+/g, ' ').trim()
  if (!base) return 'Perth, WA'
  if (/\bPerth,\s*WA\b/i.test(base)) return base
  if (/\bMelbourne,\s*VIC\b/i.test(base)) {
    return base.replace(/\bMelbourne,\s*VIC\b/i, (match) => `${match} · Perth, WA`)
  }
  return `${base} · Perth, WA`
}
const providerSlug = (value: string, fallback?: string) => {
  const base = cleanSlug(value)
  if (base) return base
  const fb = cleanSlug(fallback)
  return fb || 'provider'
}
const slugsEqual = (a?: string | null, b?: string | null) => cleanSlug(a) === cleanSlug(b) && cleanSlug(a) !== ''
const normalizeCity = (raw?: string | null) => {
  if (!raw) return null
  const val = raw.toString().trim()
  if (!val) return null
  return val
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ')
}
const normalizeStateToken = (raw?: string | null) => {
  const val = String(raw || '').trim()
  if (!val) return null
  if (/^[A-Za-z]{2,3}$/.test(val)) return val.toUpperCase()
  return val
}
const splitLocationParts = (value?: string | null): string[] =>
  String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
const dedupeLocationDisplayParts = (parts: Array<unknown>): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of parts) {
    const value = String(part || '').trim()
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}
const COUNTRY_CODE_BY_NAME: Record<string, string> = {
  AU: 'AU',
  AUSTRALIA: 'AU',
  NZ: 'NZ',
  'NEW ZEALAND': 'NZ',
  US: 'US',
  USA: 'US',
  'THE US': 'US',
  'THE UNITED STATES': 'US',
  'THE UNITED STATES OF AMERICA': 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  CA: 'CA',
  CANADA: 'CA',
  GB: 'GB',
  UK: 'GB',
  'THE UK': 'GB',
  'THE UNITED KINGDOM': 'GB',
  'UNITED KINGDOM': 'GB',
  IE: 'IE',
  IRELAND: 'IE',
  SG: 'SG',
  SINGAPORE: 'SG',
  HK: 'HK',
  'HONG KONG': 'HK',
}
const COUNTRY_LABEL_BY_CODE: Record<string, string> = {
  AU: 'Australia',
  NZ: 'New Zealand',
  US: 'United States of America',
  CA: 'Canada',
  GB: 'United Kingdom',
  IE: 'Ireland',
  SG: 'Singapore',
  HK: 'Hong Kong',
}
const AU_REGION_CODES = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'])
const normalizeCountryCode = (value?: unknown): string | null => {
  const key = String(value || '').trim().toUpperCase()
  if (!key) return null
  return COUNTRY_CODE_BY_NAME[key] || null
}
const countryLabelFromRecord = (countryCode: unknown, country: unknown): string | null => {
  const explicit = String(country || '').trim()
  if (explicit) return explicit
  const code = normalizeCountryCode(countryCode)
  if (!code) return null
  return COUNTRY_LABEL_BY_CODE[code] || null
}
const formatProviderLocation = (item: any) => {
  const locationParts = splitLocationParts(item?.location)
  const locationCountryCandidate = locationParts.length >= 3 ? locationParts[locationParts.length - 1] : null
  const cityRaw = item?.city || item?.suburb || locationParts[0] || null
  const countryCode =
    normalizeCountryCode(item?.countryCode || item?.address?.countryCode) ||
    normalizeCountryCode(item?.country || item?.address?.country) ||
    normalizeCountryCode(locationCountryCandidate) ||
    null
  const country =
    countryLabelFromRecord(
    countryCode || item?.countryCode || item?.address?.countryCode,
    item?.country || item?.address?.country
    ) || (locationCountryCandidate ? String(locationCountryCandidate).trim() : null)
  const stateRaw = item?.state || item?.region || item?.locationState || locationParts[1] || null
  const city = normalizeCity(cityRaw)
  let state = normalizeStateToken(stateRaw)
  if (country && state && country.toLowerCase() === state.toLowerCase()) state = null
  if (state && AU_REGION_CODES.has(state.toUpperCase()) && countryCode && countryCode !== 'AU') state = null
  const display = dedupeLocationDisplayParts([city, state, country]).join(', ') || null
  return { city, state, country, countryCode, display }
}
const isDirectEucaMd = (item: any) => {
  const directIds = new Set(['bep3A8SsfmXCNDP8wtaqzKBjcJc2', 'dXHl0qkHWbWKMzSiH1AEhkHzsd63'])
  const directNames = ['lily vu', 'dong zhao']
  const name = (item?.name || '').toLowerCase()
  if (directNames.some((n) => name.includes(n))) return true
  if (item?.userId && directIds.has(item.userId)) return true
  return false
}
const shouldHideLocation = (item: any) => {
  const hiddenNames = ['paul ledgerwood']
  const name = (item?.name || '').toLowerCase()
  return hiddenNames.some((n) => name.includes(n))
}
const mapEducationShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Education & Tutoring'
  return {
    userId: it.userId,
    name: it.name || 'Education professional',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'education',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapDoctorShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const hideLocation = isDirectEucaMd(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Medical Doctors'
  return {
    userId: it.userId,
    name: it.name || 'Doctor',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'doctor',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: hideLocation ? null : locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapAlliedShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Allied Health'
  return {
    userId: it.userId,
    name: it.name || 'Allied Health provider',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'allied',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapMigrationShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Migration & Visa'
  return {
    userId: it.userId,
    name: it.name || 'Migration professional',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'migration',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapFinancialShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Financial'
  return {
    userId: it.userId,
    name: it.name || 'Financial professional',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'financial',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapPropertyShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const label = it.displayProfession || 'Property, Design & Building'
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  return {
    userId: it.userId,
    name: it.name || 'Property professional',
    profession: label,
    displayProfession: it.displayProfession || null,
    stack: 'property',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapLegalShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  const professionLabel = it.displayProfession || 'Legal, Governance & Compliance'
  return {
    userId: it.userId,
    name: it.name || 'Legal professional',
    profession: professionLabel,
    displayProfession: it.displayProfession || null,
    stack: 'legal',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const mapTechShowcaseItem = (it: any): ShowcaseItem => {
  const { display: locationDisplay } = formatProviderLocation(it)
  const label = it.displayProfession || 'Tech, Creative & Media'
  const minFee = typeof it.pricing?.minAud === 'number' ? it.pricing.minAud : null
  const maxFee = typeof it.pricing?.maxAud === 'number' ? it.pricing.maxAud : null
  const priceNotes = typeof it.pricing?.notes === 'string' ? it.pricing.notes : null
  const price =
    minFee === 0
      ? 'Contact for quote'
      : minFee !== null && maxFee !== null
        ? `$${minFee.toFixed(0)}–$${maxFee.toFixed(0)} AUD`
        : null
  const rating =
    typeof it.ratingAverage === 'number'
      ? String(Number(it.ratingAverage).toFixed(1))
      : null
  return {
    userId: it.userId,
    name: it.name || 'Tech & creative professional',
    profession: label,
    displayProfession: it.displayProfession || null,
    stack: 'tech',
    specialty: it.specialty || null,
    title: it.title || null,
    specialties: Array.isArray(it.specialties) ? it.specialties : [],
    profile: it.profile || null,
    location: locationDisplay || null,
    price: price || undefined,
    pricingNotes: priceNotes,
    rating: rating || undefined,
    ratingCount: typeof it.ratingCount === 'number' ? it.ratingCount : null,
    image: it.photoPublicUrl || it.photoUrl || null,
    slug: providerSlug(it.name || it.userId || ''),
    availabilitySlots: Array.isArray(it.availability) ? it.availability : undefined,
  }
}

const fetchJsonWithRetry = async (url: string, label: string, attempts = 3): Promise<any> => {
  let lastErr: any = null
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, { cache: 'no-store' })
      let json: any = {}
      try { json = await res.json() } catch {}
      if (res.ok) return json
      const msg = json?.error || res.statusText || `Failed to load ${label}`
      if (res.status >= 500 && i < attempts - 1) {
        await new Promise(r => setTimeout(r, 250 * (i + 1)))
        continue
      }
      throw new Error(msg)
    } catch (e: any) {
      lastErr = e
      if (i < attempts - 1) {
        await new Promise(r => setTimeout(r, 250 * (i + 1)))
        continue
      }
    }
  }
  throw lastErr || new Error(`Failed to load ${label}`)
}

const postJsonWithRetry = async (url: string, body: any, label: string, attempts = 2): Promise<any> => {
  let lastErr: any = null
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
        cache: 'no-store',
      })
      let json: any = {}
      try { json = await res.json() } catch {}
      if (res.ok) return json
      const msg = json?.error || res.statusText || `Failed to load ${label}`
      const err: any = new Error(msg)
      err.statusCode = res.status
      throw err
    } catch (e: any) {
      lastErr = e
      const statusCode = Number(e?.statusCode || e?.status || 0)
      const shouldRetry =
        statusCode >= 500 ||
        statusCode === 429 ||
        e?.name === 'TypeError' ||
        e?.name === 'AbortError'
      if (i < attempts - 1 && shouldRetry) {
        await new Promise((r) => setTimeout(r, 250 * (i + 1)))
        continue
      }
      throw e
    }
  }
  throw lastErr || new Error(`Failed to load ${label}`)
}

const getAiSearchSessionId = () => {
  if (typeof window === 'undefined') return ''
  const key = 'euca_ai_search_session'
  try {
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing
    const id =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `ai_${Math.random().toString(36).slice(2)}${Date.now()}`
    window.sessionStorage.setItem(key, id)
    return id
  } catch {
    return ''
  }
}

const AI_SEARCH_STATE_KEY = 'euca_ai_search_state_v1'
const AI_SEARCH_STATE_TTL_MS = 30 * 60 * 1000

type AiSearchPersistedState = {
  savedAt: number
  query: string
  country?: string
  aiResult: any | null
  results: ShowcaseItem[]
  error: string | null
  logId: string
  hasSearched: boolean
}

const loadAiSearchState = (): AiSearchPersistedState | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(AI_SEARCH_STATE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw || '{}') as AiSearchPersistedState
    if (!parsed || typeof parsed !== 'object') return null
    const savedAt = Number(parsed.savedAt || 0)
    if (!savedAt || Date.now() - savedAt > AI_SEARCH_STATE_TTL_MS) {
      window.sessionStorage.removeItem(AI_SEARCH_STATE_KEY)
      return null
    }
    return {
      savedAt,
      query: String(parsed.query || ''),
      country: String(parsed.country || ''),
      aiResult: parsed.aiResult || null,
      results: Array.isArray(parsed.results) ? parsed.results : [],
      error: parsed.error ? String(parsed.error) : null,
      logId: String(parsed.logId || ''),
      hasSearched: !!parsed.hasSearched,
    }
  } catch {
    return null
  }
}

const persistAiSearchState = (state: Omit<AiSearchPersistedState, 'savedAt'>) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      AI_SEARCH_STATE_KEY,
      JSON.stringify({
        ...state,
        country: state.country || '',
        savedAt: Date.now(),
      })
    )
  } catch {
    // ignore storage errors
  }
}

const fetchAiSearch = async (query: string, sessionId: string, country = '') => {
  if (!SEARCH_PUBLIC_API) throw new Error('Search API unavailable')
  const url = `${SEARCH_PUBLIC_API}/public/ai-search`
  return await postJsonWithRetry(url, { query, sessionId, country }, 'AI search')
}

const logAiSearchClick = async (logId: string, providerId?: string | null) => {
  if (!SEARCH_PUBLIC_API || !logId || !providerId) return
  try {
    await fetch(`${SEARCH_PUBLIC_API}/public/ai-search/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logId, providerId }),
      keepalive: true,
    })
  } catch {
    // best-effort logging
  }
}

const fetchShowcasePayload = async (
  apiBase: string | undefined,
  path: string,
  label: string,
  mapper: (item: any) => ShowcaseItem
): Promise<ShowcaseFetchResult> => {
  if (!apiBase) return { items: [], qualifiedBookingCount: 0 }
  const url = new URL(`${apiBase}${path}`)
  url.searchParams.set('ts', Date.now().toString())
  const json = await fetchJsonWithRetry(url.toString(), `${label} showcase`)
  const items = Array.isArray(json?.items) ? json.items : []
  rememberAvailabilityFromStackItems(label, items)
  return {
    items: items.map(mapper),
    qualifiedBookingCount: parseQualifiedBookingCount(json),
  }
}

const fetchEducationShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(EDUCATION_PUBLIC_API, '/public/education/showcase', 'education', mapEducationShowcaseItem)

const fetchDoctorShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(DOCTOR_PUBLIC_API, '/public/doctors/showcase', 'doctor', mapDoctorShowcaseItem)

const fetchAlliedShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(ALLIED_PUBLIC_API, '/public/allied/showcase', 'allied', mapAlliedShowcaseItem)

const fetchMigrationShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(MIGRATION_PUBLIC_API, '/public/migration/showcase', 'migration', mapMigrationShowcaseItem)

const fetchFinancialShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(FINANCIAL_PUBLIC_API, '/public/financial/showcase', 'financial', mapFinancialShowcaseItem)

const fetchLegalShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(LEGAL_PUBLIC_API, '/public/legal/showcase', 'legal', mapLegalShowcaseItem)

const fetchTechShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(TECH_PUBLIC_API, '/public/tech/showcase', 'tech', mapTechShowcaseItem)

const fetchPropertyShowcaseData = async (): Promise<ShowcaseFetchResult> =>
  fetchShowcasePayload(PROPERTY_PUBLIC_API, '/public/property/showcase', 'property', mapPropertyShowcaseItem)

const fetchEducationShowcase = async (): Promise<ShowcaseItem[]> => (await fetchEducationShowcaseData()).items
const fetchDoctorShowcase = async (): Promise<ShowcaseItem[]> => (await fetchDoctorShowcaseData()).items
const fetchAlliedShowcase = async (): Promise<ShowcaseItem[]> => (await fetchAlliedShowcaseData()).items
const fetchMigrationShowcase = async (): Promise<ShowcaseItem[]> => (await fetchMigrationShowcaseData()).items
const fetchFinancialShowcase = async (): Promise<ShowcaseItem[]> => (await fetchFinancialShowcaseData()).items
const fetchLegalShowcase = async (): Promise<ShowcaseItem[]> => (await fetchLegalShowcaseData()).items
const fetchTechShowcase = async (): Promise<ShowcaseItem[]> => (await fetchTechShowcaseData()).items
const fetchPropertyShowcase = async (): Promise<ShowcaseItem[]> => (await fetchPropertyShowcaseData()).items

const hydrateAvailabilityForStack = async (stack: string) => {
  if (!STACK_TITLE_MAP[stack]) return
  if (availabilityIndexLoadedByStack[stack]) return
  if (!availabilityHydrationByStack[stack]) {
    const fetcher =
      stack === 'doctor' ? fetchDoctorShowcaseData :
      stack === 'allied' ? fetchAlliedShowcaseData :
      stack === 'financial' ? fetchFinancialShowcaseData :
      stack === 'migration' ? fetchMigrationShowcaseData :
      stack === 'legal' ? fetchLegalShowcaseData :
      stack === 'education' ? fetchEducationShowcaseData :
      stack === 'property' ? fetchPropertyShowcaseData :
      stack === 'tech' ? fetchTechShowcaseData :
      null
    if (!fetcher) return
    availabilityHydrationByStack[stack] = fetcher().finally(() => {
      availabilityHydrationByStack[stack] = undefined
    })
  }
  await availabilityHydrationByStack[stack]
}

const mapSearchApiItem = (item: any): ShowcaseItem => {
  const { city, state, country, countryCode, display } = formatProviderLocation(item)
  const location = display || undefined
  const stackLabel = item.displayProfession || item.profession || STACK_TITLE_MAP[item.stack || ''] || 'Professional'
  const slug = providerSlug(item.slug || item.name || item.userId || '')
  const price = item.priceLabel || undefined
  const rating =
    typeof item.rating === 'number'
      ? item.rating.toFixed(1)
      : typeof item.rating === 'string'
        ? item.rating
        : undefined
  const postcodeRaw =
    item.postcode ?? item.postCode ?? item.postalCode ?? item.publicPostcode ?? item.publicPostCode
  const postcode = postcodeRaw ? String(postcodeRaw) : null
  const parsedResponseTimeHours = Number(item.responseTimeHours)
  const parsedResponseTimeScore = Number(item.responseTimeScore)
  const parsedResponseTimeSampleSize = Number(item.responseTimeSampleSize)
  return {
    userId: item.userId,
    name: item.name || stackLabel,
    profession: stackLabel,
    displayProfession: item.displayProfession || null,
    stack: item.stack,
    specialty: item.specialty || null,
    title: item.title || null,
    specialties: Array.isArray(item.specialties) ? item.specialties : [],
    profile: item.profile || null,
    location,
    country: country || null,
    countryCode: countryCode || null,
    city: city || null,
    state: state || null,
    postcode,
    price,
    pricingNotes: item.pricingNotes || null,
    rating,
    ratingCount: typeof item.ratingCount === 'number' ? item.ratingCount : null,
    image: item.image || null,
    slug,
    responseTimeHours:
      Number.isFinite(parsedResponseTimeHours) && parsedResponseTimeHours >= 0
        ? Number(parsedResponseTimeHours.toFixed(2))
        : null,
    responseTimeScore:
      Number.isFinite(parsedResponseTimeScore)
        ? Math.max(0, Math.min(parsedResponseTimeScore, 1))
        : null,
    responseTimeSampleSize:
      Number.isFinite(parsedResponseTimeSampleSize) && parsedResponseTimeSampleSize >= 0
        ? Math.round(parsedResponseTimeSampleSize)
        : null,
    availabilitySlots: Array.isArray(item.availabilitySlots)
      ? item.availabilitySlots
      : Array.isArray(item.availability)
        ? item.availability
        : [],
  }
}

const matchesSearchCountry = (item: ShowcaseItem, country: string): boolean => {
  const normalized = normalizeCountryCode(country)
  if (!normalized) return true
  const itemCode = normalizeCountryCode(item.countryCode || item.country)
  if (itemCode) return itemCode === normalized
  const locationValue = String(item.location || '').toLowerCase()
  const explicitCountry = String(item.country || '').toLowerCase()
  const targetLabel = COUNTRY_LABEL_BY_CODE[normalized] || ''
  const targetAliases = new Set([
    normalized.toLowerCase(),
    targetLabel.toLowerCase(),
    targetLabel.toLowerCase().replace(/ of america$/, '').trim(),
  ])
  if (explicitCountry && Array.from(targetAliases).some((alias) => alias && explicitCountry.includes(alias))) {
    return true
  }
  return Array.from(targetAliases).some((alias) => alias && locationValue.includes(alias))
}

const fetchSearchApi = async (
  q: string,
  profession: string,
  country = '',
  options?: { attempts?: number; hydrateAvailability?: boolean }
): Promise<ShowcaseItem[]> => {
  if (!SEARCH_PUBLIC_API) return []
  const attempts = Number(options?.attempts) > 0 ? Number(options?.attempts) : 3
  const hydrateAvailability = options?.hydrateAvailability !== false
  const url = new URL(`${SEARCH_PUBLIC_API}/public/search`)
  if (q.trim()) url.searchParams.set('q', q.trim())
  if (profession) url.searchParams.set('profession', profession)
  if (country) url.searchParams.set('country', country)
  const json = await fetchJsonWithRetry(url.toString(), 'search', attempts)
  const items = Array.isArray(json?.items) ? json.items : []
  const mapped = items.map(mapSearchApiItem)
  const filtered = country ? mapped.filter((item) => matchesSearchCountry(item, country)) : mapped
  if (!hydrateAvailability) return filtered
  const stacksToHydrate = new Set<string>()
  filtered.forEach((item) => {
    if (hasAvailabilityListed(item) || !item.userId) return
    const stack = resolveShowcaseStack(item)
    if (stack && !availabilityIndexLoadedByStack[stack]) stacksToHydrate.add(stack)
  })
  if (stacksToHydrate.size) {
    await Promise.all(Array.from(stacksToHydrate).map((stack) => hydrateAvailabilityForStack(stack)))
  }
  return filtered.map((item) => {
    if (hasAvailabilityListed(item)) return item
    const userId = String(item.userId || '').trim()
    if (!userId) return item
    const stack = resolveShowcaseStack(item)
    if (!stack) return item
    const slots = availabilityIndexByStack[stack]?.get(userId) || []
    if (!slots.length) return item
    return { ...item, availabilitySlots: [...slots] }
  })
}

const resolveShowcaseStack = (item?: ShowcaseItem | null): string => {
  if (!item) return ''
  const explicit = String(item.stack || '').toLowerCase()
  if (explicit && STACK_TITLE_MAP[explicit]) return explicit
  const inferred = PROFESSION_TO_STACK[item.profession || ''] || ''
  return inferred && STACK_TITLE_MAP[inferred] ? inferred : ''
}

const AI_SEARCH_RANK_STOPWORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'help', 'i', 'in', 'is',
  'it', 'me', 'my', 'need', 'of', 'on', 'or', 'please', 'the', 'to', 'with', 'looking', 'want',
  'someone', 'some', 'about',
])

const normalizeAiRankText = (value: unknown) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const tokenizeAiRankText = (value: string) =>
  normalizeAiRankText(value)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token && token.length > 1 && !AI_SEARCH_RANK_STOPWORDS.has(token))

const buildAiRankCorpus = (item: ShowcaseItem) =>
  normalizeAiRankText([
    item.profession,
    item.displayProfession,
    item.specialty,
    item.title,
    item.name,
    item.profile,
    ...(Array.isArray(item.specialties) ? item.specialties : []),
  ].filter(Boolean).join(' '))

const buildAiRankNameCorpus = (item: ShowcaseItem) =>
  normalizeAiRankText([item.name, item.title].filter(Boolean).join(' '))

const aiRankPhraseTokens = (tokens: string[]) => {
  const list: string[] = []
  for (let i = 0; i < tokens.length - 1; i += 1) {
    const pair = `${tokens[i]} ${tokens[i + 1]}`.trim()
    if (pair.split(' ').length === 2) list.push(pair)
  }
  return list
}

const aiRankTokenVariants = (token: string) => {
  const base = normalizeAiRankText(token)
  if (!base) return []
  const variants = new Set([base])
  if (base.endsWith('s') && base.length > 3) variants.add(base.slice(0, -1))
  const accountingIntent =
    base.includes('tax') ||
    base.includes('account') ||
    base.includes('bookkeep') ||
    base === 'ato' ||
    base === 'gst' ||
    base === 'bas'
  if (accountingIntent) {
    variants.add('tax')
    variants.add('taxation')
    variants.add('account')
    variants.add('accountant')
    variants.add('accounting')
    variants.add('bookkeeper')
    variants.add('bookkeeping')
    variants.add('ato')
    variants.add('gst')
    variants.add('bas')
  }
  if (base.includes('loan') || base.includes('mortgage') || base.includes('broker') || base.includes('refinance')) {
    variants.add('loan')
    variants.add('mortgage')
    variants.add('broker')
    variants.add('refinance')
    variants.add('finance')
  }
  if (base.includes('legal') || base === 'law' || base === 'lawyer' || base === 'solicitor') {
    variants.add('law')
    variants.add('legal')
    variants.add('lawyer')
    variants.add('solicitor')
  }
  return Array.from(variants)
}

const providerCanonicalKey = (item: ShowcaseItem) => {
  const stack = resolveShowcaseStack(item) || normalizeAiRankText(item.profession)
  const userId = String(item.userId || '').trim().toLowerCase()
  if (userId) return `${stack || 'any'}#${userId}`
  const slug = String(item.slug || '').trim().toLowerCase()
  if (slug) return `${stack || 'any'}#slug#${slug}`
  const name = normalizeAiRankText(item.name)
  const location = normalizeAiRankText([item.city, item.state, item.countryCode || item.country].filter(Boolean).join(' '))
  return `${stack || 'any'}#name#${name}#${location}`
}

const dedupeAiProviders = (items: ShowcaseItem[]) => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = providerCanonicalKey(item)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const aiReviewScore = (item: ShowcaseItem) => {
  const rating = Number(item.rating)
  if (!Number.isFinite(rating) || rating <= 0) return 0
  const count = Math.max(0, Number(item.ratingCount) || 0)
  const quality = Math.max(0, Math.min(rating / 5, 1))
  const confidence = count > 0 ? Math.min(1, Math.log10(count + 1)) : 0.15
  return quality * (0.55 + 0.45 * confidence)
}

const aiResponseScore = (item: ShowcaseItem) => {
  const explicit = Number(item.responseTimeScore)
  let score = Number.isFinite(explicit) ? Math.max(0, Math.min(explicit, 1)) : null
  if (score == null) {
    const hours = Number(item.responseTimeHours)
    if (Number.isFinite(hours) && hours >= 0) {
      const capped = Math.min(hours, 72)
      score = Math.max(0, Math.min(1, 1 - capped / 72))
    }
  }
  if (score == null) return 0.5
  const sampleSize = Math.max(0, Number(item.responseTimeSampleSize) || 0)
  if (sampleSize >= 5) return score
  const confidence = sampleSize > 0 ? Math.max(0.25, sampleSize / 5) : 0.35
  return 0.5 + (score - 0.5) * confidence
}

const aiKeywordScore = (item: ShowcaseItem, queryTokens: string[]) => {
  if (!queryTokens.length) return { score: 0.5, matchedTokens: 0, matchedPhrases: 0 }
  const corpus = buildAiRankCorpus(item)
  if (!corpus) return { score: 0, matchedTokens: 0, matchedPhrases: 0 }
  const paddedCorpus = ` ${corpus} `
  const nameCorpus = buildAiRankNameCorpus(item)
  const tokenSet = new Set(tokenizeAiRankText(corpus))
  const phraseTokens = aiRankPhraseTokens(queryTokens)
  let matchedTokens = 0
  let matchedPhrases = 0
  for (const token of queryTokens) {
    const variants = aiRankTokenVariants(token)
    const hasHit = variants.some((variant) => {
      if (!variant) return false
      if (variant.includes(' ')) return paddedCorpus.includes(` ${variant} `)
      return tokenSet.has(variant)
    })
    if (hasHit) matchedTokens += 1
  }
  for (const phrase of phraseTokens) {
    if (paddedCorpus.includes(` ${phrase} `)) matchedPhrases += 1
  }
  const tokenRatio = matchedTokens / queryTokens.length
  const phraseRatio = phraseTokens.length ? matchedPhrases / phraseTokens.length : 0
  const queryPhrase = queryTokens.join(' ').trim()
  const exactPhraseBonus = queryPhrase && paddedCorpus.includes(` ${queryPhrase} `) ? 0.15 : 0
  const nameBoost =
    nameCorpus && queryTokens.some((token) => nameCorpus.includes(token))
      ? 0.05
      : 0
  const score = Math.max(0, Math.min(tokenRatio * 0.72 + phraseRatio * 0.28 + exactPhraseBonus + nameBoost, 1))
  return { score, matchedTokens, matchedPhrases }
}

const rankAiSearchProviders = (items: ShowcaseItem[], queryText = '') => {
  const deduped = dedupeAiProviders(items)
  if (deduped.length <= 1) return deduped
  const queryTokens = tokenizeAiRankText(queryText)
  const hasQuery = queryTokens.length > 0
  return deduped
    .map((item) => {
      const keyword = aiKeywordScore(item, queryTokens)
      const keywordScore = keyword.score
      const reviewScore = aiReviewScore(item)
      const responseScore = aiResponseScore(item)
      let composite = hasQuery
        ? keywordScore * 0.55 + reviewScore * 0.3 + responseScore * 0.15
        : reviewScore * 0.65 + responseScore * 0.35
      if (hasQuery) {
        if (keyword.matchedTokens === 0) composite *= 0.18
        else if (queryTokens.length >= 3 && keyword.matchedTokens === 1) composite *= 0.62
      }
      return {
        item,
        composite,
        keywordScore,
        reviewScore,
        responseScore,
        matchedTokens: keyword.matchedTokens,
        matchedPhrases: keyword.matchedPhrases,
      }
    })
    .sort((a, b) => {
      if (b.matchedTokens !== a.matchedTokens) return b.matchedTokens - a.matchedTokens
      if (b.matchedPhrases !== a.matchedPhrases) return b.matchedPhrases - a.matchedPhrases
      if (b.composite !== a.composite) return b.composite - a.composite
      if (b.keywordScore !== a.keywordScore) return b.keywordScore - a.keywordScore
      if (b.reviewScore !== a.reviewScore) return b.reviewScore - a.reviewScore
      if (b.responseScore !== a.responseScore) return b.responseScore - a.responseScore
      const bReviews = Number(b.item.ratingCount) || 0
      const aReviews = Number(a.item.ratingCount) || 0
      if (bReviews !== aReviews) return bReviews - aReviews
      const aHours = Number(a.item.responseTimeHours)
      const bHours = Number(b.item.responseTimeHours)
      const aHas = Number.isFinite(aHours) && aHours >= 0
      const bHas = Number.isFinite(bHours) && bHours >= 0
      if (aHas && bHas && aHours !== bHours) return aHours - bHours
      return String(a.item.name || '').localeCompare(String(b.item.name || ''))
    })
    .map((row) => row.item)
}

const normalizeBadgeToken = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const inferBadgeStack = (item?: ShowcaseItem | null) => {
  const explicit = String(item?.stack || '').toLowerCase()
  if (explicit && STACK_TITLE_MAP[explicit]) return explicit
  const mapped = PROFESSION_TO_STACK[item?.profession || ''] || PROFESSION_TO_STACK[item?.displayProfession || '']
  if (mapped && STACK_TITLE_MAP[mapped]) return mapped
  const text = `${item?.profession || ''} ${item?.displayProfession || ''}`.toLowerCase()
  if (text.includes('doctor')) return 'doctor'
  if (text.includes('allied')) return 'allied'
  if (text.includes('migration') || text.includes('visa')) return 'migration'
  if (text.includes('financial') || text.includes('account')) return 'financial'
  if (text.includes('legal') || text.includes('law')) return 'legal'
  if (text.includes('property') || text.includes('real')) return 'property'
  return ''
}

const hasLicenseVerifiedBadge = (item?: ShowcaseItem | null) => {
  const stack = inferBadgeStack(item)
  if (stack === 'doctor' || stack === 'allied' || stack === 'financial' || stack === 'migration') return true
  const tokens = [
    item?.profession,
    item?.displayProfession,
    item?.specialty,
    item?.title,
    ...(Array.isArray(item?.specialties) ? item.specialties : []),
  ]
    .filter(Boolean)
    .map((value) => normalizeBadgeToken(String(value)))
    .filter(Boolean)
  if (stack === 'legal') {
    if (tokens.some((token) => token.includes('principal lawyer'))) return true
    if (tokens.some((token) => token.includes('nationally accredited mediator'))) return true
    if (tokens.some((token) => token.includes('naati') && token.includes('translator'))) return true
    return false
  }
  if (stack === 'property') {
    if (tokens.some((token) => token.includes('forensic engineer'))) return true
    if (tokens.some((token) => token.includes('real estate agent'))) return true
    if (tokens.some((token) => /\barchitect\b/.test(token))) return true
    return false
  }
  if (stack === 'education') {
    const labelTokens = [
      item?.profession,
      item?.displayProfession,
      item?.specialty,
      item?.title,
    ]
      .filter(Boolean)
      .map((value) => normalizeBadgeToken(String(value)))
      .filter(Boolean)
    if (labelTokens.some((token) => token.includes('education advisor'))) return true
    if (labelTokens.some((token) => /\blecturer\b/.test(token))) return true
    if (labelTokens.some((token) => /\bteacher\b/.test(token))) return true
    return false
  }
  return false
}

const hasAvailabilityListed = (item?: ShowcaseItem | null) =>
  !!item &&
  (
    (Array.isArray(item.availabilitySlots) && item.availabilitySlots.length > 0) ||
    (Array.isArray((item as any)?.availability) && (item as any).availability.length > 0)
  )

const LicenseVerifiedBadge = ({ item }: { item?: ShowcaseItem | null }) => {
  if (!hasLicenseVerifiedBadge(item)) return null
  return (
    <span
      className="license-verified-badge"
      aria-label="License Verified"
      role="img"
      tabIndex={0}
      data-tooltip={LICENSE_VERIFIED_TOOLTIP}
    >
      <ShieldCheck size={13} aria-hidden />
    </span>
  )
}

const inferPrimaryCategoryFromResults = (items: ShowcaseItem[]) => {
  const top = Array.isArray(items) ? items.slice(0, 12) : []
  if (!top.length) {
    return {
      primary: '',
      confidence: 0,
      secondary: null as string | null,
      secondaryConfidence: null as number | null,
    }
  }
  const scored = new Map<string, number>()
  top.forEach((item, idx) => {
    const stack = resolveShowcaseStack(item)
    if (!stack) return
    const weight = 1 / (idx + 1)
    scored.set(stack, (scored.get(stack) || 0) + weight)
  })
  const ranked = Array.from(scored.entries()).sort((a, b) => b[1] - a[1])
  if (!ranked.length) {
    return {
      primary: '',
      confidence: 0,
      secondary: null as string | null,
      secondaryConfidence: null as number | null,
    }
  }
  const total = ranked.reduce((sum, [, score]) => sum + score, 0)
  const [primary, primaryScore] = ranked[0]
  const second = ranked[1] || null
  const includeSecondary = second && second[1] >= primaryScore * 0.55
  return {
    primary,
    confidence: Math.max(0.5, Math.min(0.95, total ? primaryScore / total : 0.5)),
    secondary: includeSecondary ? second[0] : null,
    secondaryConfidence: includeSecondary
      ? Math.max(0.25, Math.min(0.9, total ? second[1] / total : 0.25))
      : null,
  }
}

const fetchSuggestApi = async (q: string): Promise<string[]> => {
  if (!SEARCH_PUBLIC_API) return []
  const url = new URL(`${SEARCH_PUBLIC_API}/public/suggest`)
  if (q.trim()) url.searchParams.set('q', q.trim())
  const json = await fetchJsonWithRetry(url.toString(), 'search suggestions')
  const list = Array.isArray(json?.suggestions) ? json.suggestions : []
  return list
}

const buildProviderBookingUrl = (
  provider: ShowcaseItem,
  reason: string,
  loginUrl: string,
  slotISO?: string | null,
  forceDirect?: boolean
) => {
  const url = new URL(loginUrl)
  const appBaseUrl = url.origin
  const professionLabel = String(provider.profession || '').toLowerCase()
  const providerTags = [
    provider.profession,
    provider.specialty,
    ...(provider.specialties || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  const alliedKeywords = [
    'allied',
    'psychologist',
    'counsellor',
    'counselor',
    'dietitian',
    'physio',
    'physiotherapist',
    'exercise physiologist',
    'occupational therapist',
    'speech pathologist',
    'speech therapist',
    'podiatrist',
    'chiropractor',
    'social worker',
    'registered nurse',
    'nurse',
    'pharmacist',
  ]
  const isAlliedByKeyword = alliedKeywords.some((keyword) => providerTags.includes(keyword))
  const professionParam =
    provider.stack ||
    ((professionLabel.includes('allied') || isAlliedByKeyword)
      ? 'allied'
      : professionLabel.includes('migration')
        ? 'migration'
        : professionLabel.includes('financial')
          ? 'financial'
          : professionLabel.includes('legal')
            ? 'legal'
            : professionLabel.includes('property')
              ? 'property'
              : professionLabel.includes('tech')
                ? 'tech'
                : professionLabel.includes('education')
                  ? 'education'
                  : 'education')
  const nextPath =
    `/results/${professionParam}?specialty=${encodeURIComponent('Direct booking')}` +
    `&providerId=${encodeURIComponent(provider.userId || provider.slug)}` +
    `&providerSlug=${encodeURIComponent(provider.slug)}` +
    `&providerName=${encodeURIComponent(provider.name)}` +
    (provider.image ? `&providerImage=${encodeURIComponent(provider.image)}` : '') +
    (reason ? `&reason=${encodeURIComponent(reason)}` : '') +
    (slotISO ? `&slot=${encodeURIComponent(slotISO)}` : '')
  return `${appBaseUrl}${nextPath}`
}

const advisoryLine =
  'Services provided are compliance and governance advisory only and do not constitute legal advice or legal representation.'
const mediatorAdvisoryLine =
  'Services provided are mediation and dispute resolution services only and do not constitute legal advice or legal representation.'

const escapeHtml = (str: string = '') =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const normalizeLine = (value: string = '') => value.toLowerCase().replace(/\s+/g, ' ').trim()
const normalizeSpecialty = (value: string = '') => value.toLowerCase().replace(/[^a-z0-9]/g, '')
const hasSpecialty = (specialties: string[] = [], target: string) => {
  const needle = normalizeSpecialty(target)
  return specialties.some((spec) => normalizeSpecialty(spec) === needle)
}
const ensureDisclaimer = (text: string, line: string) => {
  if (!line) return text
  const normalizedLine = normalizeLine(line)
  if (!normalizedLine) return text
  if (normalizeLine(text).includes(normalizedLine)) return text
  if (!text) return line
  return `${text}\n\n${line}`
}
const applyProfileDisclaimers = (text: string, label: string = '') => {
  let out = text
  if (normalizeLine(label) === normalizeLine('Nationally Accredited Mediator')) {
    out = ensureDisclaimer(out, mediatorAdvisoryLine)
  }
  return out
}
const renderProfileHtml = (text: string) => {
  const escaped = escapeHtml(text)
  const highlightLines = [advisoryLine, mediatorAdvisoryLine]
  const withEmphasis = highlightLines.reduce((acc, line) => {
    if (!line) return acc
    const escapedLine = escapeHtml(line)
    return acc.replace(escapedLine, `<em><strong>${line}</strong></em>`)
  }, escaped)
  return withEmphasis.replace(/\n/g, '<br />')
}

const serviceAnchors = industries.map(({ label }) => ({ label, href: `/industries#${slugify(label)}` }))
const howItWorksItems = [
  { label: 'For professionals', href: '/#pros' },
  { label: 'For clients', href: '/#clients' }
] as const
const productNavItems = [
  { label: 'For Clients', href: '/clients' },
  { label: 'For Professionals', href: '/professionals' },
  { label: 'Professional Verification', href: '/verification' },
] as const
const resourcesNavItems = [
  { label: 'Knowledge Base', href: '/knowledge-base' },
  { label: 'Help Centre', href: '/help-centre' },
] as const
const companyNavItems = [
  { label: 'About', href: '/about' },
  { label: 'Join Us', href: '/join-us' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' }
] as const
const aboutUsMenuSections = [
  { title: 'Products', items: productNavItems },
  { title: 'Resources', items: resourcesNavItems },
  { title: 'Pricing', items: [{ label: 'Pricing', href: '/pricing' }] as const },
  { title: 'Company', items: companyNavItems },
] as const

type KnowledgeBaseAudience = 'Clients' | 'Professionals' | 'Both'

type KnowledgeBaseSection = {
  heading: string
  content: string
}

type KnowledgeBaseArticle = {
  slug: string
  title: string
  summary: string
  category: string
  audience: KnowledgeBaseAudience
  regionSlug: string
  regionLabel: string
  professionSlug: string
  professionLabel: string
  painPoint: string
  updatedAt: string
  keywords: string[]
  searchHref: string
  joinUsHref: string
  sections: KnowledgeBaseSection[]
}

type KnowledgeBaseRegion = {
  slug: string
  label: string
  marketContext: string
  complianceContext: string
  operationsContext: string
}

type KnowledgeBaseProfession = {
  slug: string
  label: string
  searchProfession: string
  joinUsProfession: string
  clientNoun: string
  pluralNoun: string
  serviceFocus: string
  verificationFocus: string
  scopeChecklist: string
  intakeChecklist: string
  continuityActions: string
}

type KnowledgeBaseTopicTemplate = {
  slug: string
  category: string
  audience: Exclude<KnowledgeBaseAudience, 'Both'>
  painPoint: string
  buildTitle: (profession: KnowledgeBaseProfession, region: KnowledgeBaseRegion) => string
  buildSummary: (profession: KnowledgeBaseProfession, region: KnowledgeBaseRegion) => string
  buildSections: (profession: KnowledgeBaseProfession, region: KnowledgeBaseRegion) => KnowledgeBaseSection[]
}

const KNOWLEDGE_BASE_UPDATED_AT = '2026-03-05'

const KNOWLEDGE_BASE_REGIONS: KnowledgeBaseRegion[] = [
  {
    slug: 'australia',
    label: 'Australia',
    marketContext:
      'clients often compare professionals across multiple states and expect transparent digital profiles before booking.',
    complianceContext:
      'registration standards can vary by profession and jurisdiction, so scope and eligibility should be clearly stated early.',
    operationsContext:
      'clear response windows, practical timelines, and online delivery options are now standard expectations in most sectors.',
  },
  {
    slug: 'new-zealand',
    label: 'New Zealand',
    marketContext:
      'referrals remain influential, but clients increasingly use online research to validate capability before first contact.',
    complianceContext:
      'professional registration and privacy obligations are important trust signals, especially for higher-stakes advice.',
    operationsContext:
      'clients value plain-language communication, realistic next steps, and direct guidance without unnecessary complexity.',
  },
  {
    slug: 'united-states',
    label: 'United States',
    marketContext:
      'competition is high and clients evaluate many options quickly, which makes positioning and conversion discipline essential.',
    complianceContext:
      'state-level rules are common across professions, so geographic scope and licensing boundaries need to be explicit.',
    operationsContext:
      'fast first responses and a clear booking path materially improve outcomes in crowded service categories.',
  },
  {
    slug: 'canada',
    label: 'Canada',
    marketContext:
      'clients often prioritize credibility and service clarity before price, especially for regulated or complex engagements.',
    complianceContext:
      'many requirements are provincial, so professionals should state where they can practise and where referral is required.',
    operationsContext:
      'predictable communication cadence and clear preparation guidance improve both first-booking and retention performance.',
  },
  {
    slug: 'united-kingdom',
    label: 'United Kingdom',
    marketContext:
      'buyers tend to respond to practical evidence of competence and delivery quality rather than broad marketing claims.',
    complianceContext:
      'well-defined standards and disclosure requirements reward precise language around credentials and service boundaries.',
    operationsContext:
      'structured onboarding and documented next steps improve confidence for both one-off and ongoing engagements.',
  },
  {
    slug: 'ireland',
    label: 'Ireland',
    marketContext:
      'word-of-mouth matters, but a clear online presence is increasingly the first checkpoint before engagement.',
    complianceContext:
      'registration status, accreditation, and data protection expectations should be communicated in plain language.',
    operationsContext:
      'clients value concise, practical advice and visible progress milestones from the first meeting onward.',
  },
  {
    slug: 'singapore',
    label: 'Singapore',
    marketContext:
      'clients are quality-sensitive and compare providers on responsiveness, precision, and professionalism.',
    complianceContext:
      'regulatory alignment and documentation standards are high, making credential clarity non-negotiable.',
    operationsContext:
      'many engagements are hybrid or cross-border, so turnaround commitments and communication scope should be explicit.',
  },
  {
    slug: 'hong-kong',
    label: 'Hong Kong',
    marketContext:
      'time-sensitive decision cycles are common, so clients value concise expert guidance and quick, confident direction.',
    complianceContext:
      'practice rights and disclosure expectations differ by profession, so early scope transparency reduces risk.',
    operationsContext:
      'clear deliverables, reliable follow-up, and direct communication style are core conversion drivers.',
  },
]

const KNOWLEDGE_BASE_PROFESSIONS: KnowledgeBaseProfession[] = [
  {
    slug: 'legal',
    label: 'Legal, Governance & Compliance',
    searchProfession: 'legal',
    joinUsProfession: 'legal',
    clientNoun: 'Legal and Compliance Professional',
    pluralNoun: 'legal and compliance professionals',
    serviceFocus:
      'contract drafting and review, governance advice, compliance frameworks, dispute preparation, and risk controls',
    verificationFocus:
      'relevant practising status where required, domain expertise, and clear disclosure of service boundaries',
    scopeChecklist:
      'jurisdiction coverage, representation limits, document scope, turnaround expectations, and escalation pathways',
    intakeChecklist:
      'existing agreements, timeline of events, notices received, counterpart communications, and decision deadlines',
    continuityActions:
      'written matter summaries, risk registers, periodic review checkpoints, and clear implementation responsibilities',
  },
  {
    slug: 'doctor',
    label: 'Medical Doctors',
    searchProfession: 'doctor',
    joinUsProfession: 'doctor',
    clientNoun: 'Medical Doctor',
    pluralNoun: 'medical doctors',
    serviceFocus:
      'telehealth or in-person consultations, preventative planning, chronic condition follow-up, and second opinions',
    verificationFocus:
      'current medical registration, specialty credentials, telehealth suitability, and prescribing scope clarity',
    scopeChecklist:
      'clinical scope, referral boundaries, follow-up model, urgent care limits, and expected review intervals',
    intakeChecklist:
      'medical history summary, current medications, recent test results, prior treatment notes, and key concerns',
    continuityActions:
      'care plans, review reminders, progress tracking, and coordinated referrals when broader support is required',
  },
  {
    slug: 'allied',
    label: 'Allied Health',
    searchProfession: 'allied',
    joinUsProfession: 'allied',
    clientNoun: 'Allied Health Professional',
    pluralNoun: 'allied health professionals',
    serviceFocus:
      'assessment, therapy planning, behaviour or lifestyle support, rehabilitation, and measurable functional outcomes',
    verificationFocus:
      'current registration or accreditation, modality suitability, and scope clarity for independent practice',
    scopeChecklist:
      'assessment approach, program structure, review cadence, expected milestones, and referral boundaries',
    intakeChecklist:
      'history of symptoms or goals, prior interventions, relevant reports, constraints, and desired outcomes',
    continuityActions:
      'goal reviews, progress notes, practical home strategies, and coordinated handover where multidisciplinary support helps',
  },
  {
    slug: 'education',
    label: 'Education & Tutoring',
    searchProfession: 'education',
    joinUsProfession: 'education',
    clientNoun: 'Teacher or Tutor',
    pluralNoun: 'teachers and tutors',
    serviceFocus:
      'subject mastery, exam preparation, study skills, learning confidence, and long-term academic planning',
    verificationFocus:
      'teaching qualifications where relevant, track record in target subjects, and evidence of student outcomes',
    scopeChecklist:
      'year level fit, syllabus alignment, lesson cadence, homework support, and assessment preparation boundaries',
    intakeChecklist:
      'current academic level, goals, recent assessment results, learning preferences, and scheduling constraints',
    continuityActions:
      'progress reporting, parent or student feedback loops, exam readiness tracking, and structured revision plans',
  },
  {
    slug: 'financial',
    label: 'Financial Services',
    searchProfession: 'financial',
    joinUsProfession: 'financial',
    clientNoun: 'Financial Professional',
    pluralNoun: 'financial professionals',
    serviceFocus:
      'accounting and tax support, financial advice, lending strategy, cash-flow planning, and compliance guidance',
    verificationFocus:
      'relevant licensing or registration, authorised scope, and transparent disclosure of advisory boundaries',
    scopeChecklist:
      'advice scope, assumptions used, implementation steps, review timing, and exclusions requiring specialist referral',
    intakeChecklist:
      'financial goals, entity or personal profile, key statements, existing arrangements, and upcoming deadlines',
    continuityActions:
      'periodic reviews, forward planning cycles, reporting cadence, and proactive updates when regulations change',
  },
  {
    slug: 'migration',
    label: 'Migration & Visa',
    searchProfession: 'migration',
    joinUsProfession: 'migration',
    clientNoun: 'Migration Professional',
    pluralNoun: 'migration professionals',
    serviceFocus:
      'visa pathway strategy, eligibility assessment, document preparation, lodgement workflow, and case progression support',
    verificationFocus:
      'current registration where required, case-type experience, and transparent limits on legal scope',
    scopeChecklist:
      'visa class coverage, document responsibility, timeline assumptions, communication frequency, and appeal boundaries',
    intakeChecklist:
      'immigration history, identity records, employment or study evidence, family details, and target timelines',
    continuityActions:
      'milestone tracking, document checklists, status updates, and clear guidance if circumstances change mid-process',
  },
  {
    slug: 'property',
    label: 'Property, Design & Building',
    searchProfession: 'property',
    joinUsProfession: 'property',
    clientNoun: 'Property and Design Professional',
    pluralNoun: 'property and design professionals',
    serviceFocus:
      'architectural and interior planning, feasibility analysis, advisory services, and project coordination support',
    verificationFocus:
      'relevant registration where applicable, portfolio quality, project-type fit, and defined service boundaries',
    scopeChecklist:
      'stage of engagement, deliverable definition, authority approvals, timeline dependencies, and decision milestones',
    intakeChecklist:
      'site or property details, budget range, design brief, regulatory constraints, and target completion dates',
    continuityActions:
      'stage-based updates, documented decisions, budget checkpoints, and issue logs that prevent avoidable rework',
  },
  {
    slug: 'tech',
    label: 'Tech, Creative & Media',
    searchProfession: 'tech',
    joinUsProfession: 'tech',
    clientNoun: 'Tech, Creative, or Media Professional',
    pluralNoun: 'tech, creative, and media professionals',
    serviceFocus:
      'product and software delivery, design systems, content strategy, production workflows, and campaign execution',
    verificationFocus:
      'portfolio strength, relevant delivery history, technical or creative scope clarity, and communication discipline',
    scopeChecklist:
      'project objectives, deliverable ownership, revision limits, timeline assumptions, and tooling dependencies',
    intakeChecklist:
      'current assets, technical stack or brand context, success metrics, approvals workflow, and launch deadlines',
    continuityActions:
      'post-delivery support plans, performance review cycles, improvement backlogs, and clear governance for future iterations',
  },
]

const KNOWLEDGE_BASE_REGION_LIST_TEXT = KNOWLEDGE_BASE_REGIONS.map((region) => region.label).join(', ')

const KNOWLEDGE_BASE_TOPIC_TEMPLATES: KnowledgeBaseTopicTemplate[] = [
  {
    slug: 'how-to-find-the-right-professional',
    category: 'Client Discovery',
    audience: 'Clients',
    painPoint: 'Clients struggle to compare options quickly and choose a qualified professional with confidence.',
    buildTitle: (profession, region) =>
      `How to Find the Right ${profession.clientNoun} in ${region.label}`,
    buildSummary: (profession, region) =>
      `A simple guide for people in ${region.label} to compare ${profession.pluralNoun} and choose with confidence.`,
    buildSections: (profession, region) => [
      {
        heading: 'Start by getting clear on what help you need',
        content:
          `Before you contact anyone, take five minutes to write down what you want solved, when you need help, and whether you want online or in-person support. This keeps your search focused and saves you from endless browsing. In ${region.label}, ${region.marketContext} When you can explain your situation clearly, it is much easier to spot ${profession.pluralNoun} who are actually the right fit for you.`,
      },
      {
        heading: 'Check they are qualified and allowed to help in your area',
        content:
          `Do this early, not at the end. Look for clear signs they are properly qualified for the type of help you need, and that they can legally work within your location and situation. In ${region.label}, ${region.complianceContext} A good question to ask is: “Is my case fully within your scope, and if not, what part should go elsewhere?” That one question can save you time, money, and stress.`,
      },
      {
        heading: 'Talk to two or three professionals',
        content:
          `Pick a short list and send each person the same short message. Ask how they would approach your situation, what they need from you first, and how long things usually take. This gives you fair comparisons instead of guesswork. You are not looking for the longest reply, you are looking for someone who is clear, practical, and confident in helping with ${profession.serviceFocus}.`,
      },
      {
        heading: 'Choose the person you trust to guide you',
        content:
          `Price matters, but it should not be the only thing you compare. Choose the person who explains things clearly, has relevant experience, sets realistic expectations, and makes your next steps easy to understand. In ${region.label}, ${region.operationsContext} The best choice is usually the professional who makes you feel informed and supported, not rushed or confused.`,
      },
    ],
  },
  {
    slug: 'fees-pricing-and-scope-clarity',
    category: 'Client Pricing',
    audience: 'Clients',
    painPoint: 'Many clients receive unclear quotes because scope, assumptions, and exclusions are not aligned upfront.',
    buildTitle: (profession, region) =>
      `${profession.clientNoun} Fees in ${region.label}: A Simple Way to Compare Quotes`,
    buildSummary: (profession, region) =>
      `A plain-language checklist for people in ${region.label} to compare ${profession.pluralNoun} without fee surprises.`,
    buildSections: (profession, region) => [
      {
        heading: 'Send the same brief to everyone',
        content:
          `If each professional gets different information, you will get different quotes that are hard to compare. Send one short brief to everyone: what you need, your timeline, and anything that cannot change. In ${region.label}, this helps ${profession.pluralNoun} quote based on the same facts. It also helps you see real differences in value, not just differences caused by missing details.`,
      },
      {
        heading: 'Ask exactly what the fee includes',
        content:
          `A good quote should clearly list what is included, what is not included, and what could cost extra later. Ask for this in writing. You can also ask for a simple explanation of any assumptions they made while quoting. For ${profession.pluralNoun}, it helps to confirm details like ${profession.scopeChecklist} This reduces confusion and protects both sides.`,
      },
      {
        heading: 'Understand how extra costs can happen',
        content:
          `Fixed fees, hourly rates, and staged fees can all be fair. What matters is knowing when the price might change. Ask what usually causes extra work, how changes are approved, and how you will be told before extra fees are added. In markets where ${region.marketContext} clear answers here are often a better sign than the cheapest price.`,
      },
      {
        heading: 'Confirm the basics before you book',
        content:
          `Before you commit, make sure you both agree on payment timing, cancellation rules, how often you will communicate, and who does what next. A short recap message after booking can prevent misunderstandings later. In ${region.label}, ${region.operationsContext} Clear basics make the whole experience smoother.`,
      },
    ],
  },
  {
    slug: 'client-preparation-checklist-before-first-call',
    category: 'Client Preparation',
    audience: 'Clients',
    painPoint: 'First calls are often inefficient because key details and documents are not prepared ahead of time.',
    buildTitle: (profession, region) =>
      `Before Your First ${profession.clientNoun} Appointment in ${region.label}`,
    buildSummary: (profession, region) =>
      `A simple prep checklist so your first conversation with ${profession.pluralNoun} in ${region.label} is useful from the start.`,
    buildSections: (profession, region) => [
      {
        heading: 'Send a short summary before the call',
        content:
          `You will get better advice if the professional knows your situation before the meeting starts. Send a short summary with your goal, key background, timeline, and what decision you need to make. Keep it short and clear. In ${region.label}, where ${region.marketContext} this helps you skip generic questions and get to practical next steps faster.`,
      },
      {
        heading: 'Bring the key documents, not everything',
        content:
          `You do not need to bring every file you own. Bring the documents that matter most to the decision: important dates, past decisions, and records related to your case. If something is missing, just say what is missing and when you can provide it. This is much easier for professionals to work with and usually speeds things up.`,
      },
      {
        heading: 'Prepare a few questions you want answered',
        content:
          `Many first meetings feel unclear because clients forget to ask the most important questions. Write down a few things you need to know, such as your options, likely timeline, possible risks, and what happens after the first step. In ${region.label}, ${region.complianceContext} these questions help you confirm you are in the right place before spending more time or money.`,
      },
      {
        heading: 'Finish with clear next steps',
        content:
          `Before the call ends, ask for a simple action plan: what is confirmed, what still needs checking, and what you should do next. If another appointment is needed, ask what that session will cover. This helps you stay in control and avoids confusion between steps. Most clients feel much more confident when they leave with a clear plan in writing.`,
      },
    ],
  },
  {
    slug: 'build-a-profile-that-converts-qualified-leads',
    category: 'Professional Growth',
    audience: 'Professionals',
    painPoint: 'Professionals often receive low-fit inquiries because their public profile does not clearly describe scope and ideal clients.',
    buildTitle: (profession, region) =>
      `${profession.label} in ${region.label}: Build a Profile That Attracts Better Clients`,
    buildSummary: (profession, region) =>
      `Simple profile improvements for ${profession.pluralNoun} in ${region.label} to attract better-fit enquiries.`,
    buildSections: (profession, region) => [
      {
        heading: 'Say clearly who you help',
        content:
          `People should understand your value in a few seconds. State who you help, what problems you solve best, and what you do not cover. This helps the right clients contact you and helps the wrong-fit clients self-filter out. In ${region.label}, where ${region.marketContext} clear positioning builds trust quickly.`,
      },
      {
        heading: 'Explain how working with you actually works',
        content:
          `Tell clients what happens after they contact you: what you need from them, how long the first step takes, and what they can expect next. Keep it plain and practical. This reduces uncertainty and improves conversion. It also helps to clearly explain boundaries like ${profession.scopeChecklist} so expectations are set early.`,
      },
      {
        heading: 'Show proof people can trust',
        content:
          `Skip broad claims and show real evidence: credentials, relevant experience, and short examples of outcomes. For ${profession.pluralNoun}, useful trust signals include ${profession.verificationFocus} In ${region.label}, ${region.complianceContext} simple, honest proof is far more persuasive than marketing language.`,
      },
      {
        heading: 'Make it easy for good clients to contact you',
        content:
          `Use one clear call-to-action and keep the next step simple. Ask for a short intake summary so you can quickly confirm fit and reply with useful guidance. Prompts based on ${profession.intakeChecklist} help you qualify faster and reduce wasted calls. Small changes here often lift booking rates quickly.`,
      },
    ],
  },
  {
    slug: 'convert-first-contact-into-bookings',
    category: 'Professional Operations',
    audience: 'Professionals',
    painPoint: 'Many enquiries drop off after first contact because response quality and next-step clarity are inconsistent.',
    buildTitle: (profession, region) =>
      `${profession.label} in ${region.label}: Turn First Enquiries Into Bookings`,
    buildSummary: (profession, region) =>
      `A practical follow-up system for ${profession.pluralNoun} in ${region.label}, from first message to confirmed booking.`,
    buildSections: (profession, region) => [
      {
        heading: 'Reply quickly and keep it clear',
        content:
          `A fast reply helps, but clarity is what wins bookings. In your first message, confirm you understand the client’s goal, ask for only the key missing details, and suggest one clear next step. For ${profession.pluralNoun} in ${region.label}, concise and practical replies usually perform best.`,
      },
      {
        heading: 'Ask a few simple qualifying questions',
        content:
          `You only need a few questions to check fit, urgency, and readiness. Keep it short so serious clients can move forward quickly. A simple intake based on ${profession.intakeChecklist} protects your time and improves booking quality without making people jump through hoops.`,
      },
      {
        heading: 'Give one clear way to book',
        content:
          `Too many options can stall good leads. Offer one recommended booking path with clear time choices and simple prep instructions. If alternatives are needed, you can introduce them later. In most cases, fewer choices and clearer steps lead to more confirmed bookings.`,
      },
      {
        heading: 'Follow up once with useful context',
        content:
          `If someone does not book right away, send one helpful follow-up. Recap what they asked, clarify one open question, and remind them of the next step. Keep the tone helpful, not pushy. Over time, track which replies and follow-ups lead to bookings so you can repeat what works.`,
      },
    ],
  },
  {
    slug: 'reviews-retention-and-repeat-bookings',
    category: 'Professional Retention',
    audience: 'Professionals',
    painPoint: 'Professionals underperform on repeat bookings because post-service follow-up and review capture are inconsistent.',
    buildTitle: (profession, region) =>
      `${profession.label} in ${region.label}: Reviews, Retention, and Repeat Bookings`,
    buildSummary: (profession, region) =>
      `How ${profession.pluralNoun} in ${region.label} can keep clients coming back and build stronger public trust.`,
    buildSections: (profession, region) => [
      {
        heading: 'End each job with a clear summary',
        content:
          `Before an engagement ends, send a short summary of what was done, what is still open, and what happens next. This reduces confusion and shows clients you are organised. For ${profession.pluralNoun} in ${region.label}, clear close-outs often lead to better reviews, repeat work, and referrals.`,
      },
      {
        heading: 'Ask for reviews at the right moment',
        content:
          `Ask for feedback soon after a clear win, when the value is fresh. Keep the request short and easy to action. Strong reviews usually mention clear communication, professionalism, and outcomes, so make those parts of your service easy to notice.`,
      },
      {
        heading: 'Offer a simple follow-up plan',
        content:
          `If ongoing support makes sense, suggest it clearly instead of waiting for clients to ask. This could be regular check-ins, progress reviews, or a light maintenance plan. In services involving ${profession.serviceFocus}, this is often where long-term value is created.`,
      },
      {
        heading: 'Use feedback to improve your service',
        content:
          `Reviews are not just marketing. They also show where your process needs work. Look for repeated comments about communication, onboarding, expectations, or timing, then improve one area at a time. Small fixes based on real feedback can noticeably improve retention and referral quality.`,
      },
    ],
  },
]

const createKnowledgeBaseArticles = (): KnowledgeBaseArticle[] => {
  const generated: KnowledgeBaseArticle[] = []
  KNOWLEDGE_BASE_PROFESSIONS.forEach((profession, professionIndex) => {
    KNOWLEDGE_BASE_TOPIC_TEMPLATES.forEach((topic, topicIndex) => {
      const region = KNOWLEDGE_BASE_REGIONS[(professionIndex * 3 + topicIndex) % KNOWLEDGE_BASE_REGIONS.length]
      const slug = `${profession.slug}-${region.slug}-${topic.slug}`
      const title = topic.buildTitle(profession, region)
      const summary = topic.buildSummary(profession, region)
      generated.push({
        slug,
        title,
        summary,
        category: topic.category,
        audience: topic.audience,
        regionSlug: region.slug,
        regionLabel: region.label,
        professionSlug: profession.slug,
        professionLabel: profession.label,
        painPoint: topic.painPoint,
        updatedAt: KNOWLEDGE_BASE_UPDATED_AT,
        keywords: [
          profession.label,
          profession.clientNoun,
          region.label,
          topic.category,
          'EUCA Match knowledge base',
          'verified professionals',
          'independent professionals',
        ],
        searchHref: `/search?profession=${profession.searchProfession}`,
        joinUsHref: `/join-us/${region.slug}/${profession.joinUsProfession}`,
        sections: topic.buildSections(profession, region),
      })
    })
  })

  const globalArticles: KnowledgeBaseArticle[] = [
    {
      slug: 'why-clients-struggle-to-find-trusted-professionals-online',
      title: 'Why Clients Struggle to Find Trusted Professionals Online',
      summary:
        'A long-form analysis of why discovery breaks down in traditional directories and what trust-first marketplaces must do to help clients choose faster and safer.',
      category: 'Market Insight',
      audience: 'Both',
      regionSlug: 'global',
      regionLabel: 'Global',
      professionSlug: 'cross-industry',
      professionLabel: 'Cross-Industry',
      painPoint:
        'Search results are often noisy, making it hard for clients to assess quality and act with confidence.',
      updatedAt: KNOWLEDGE_BASE_UPDATED_AT,
      keywords: ['trusted professionals', 'verification', 'client discovery', 'professional marketplace'],
      searchHref: '/search',
      joinUsHref: '/join-us',
      sections: [
        {
          heading: 'Discovery Failure Is a Decision-Quality Problem',
          content:
            'Across most professional services, clients are not suffering from a shortage of options. They are suffering from low decision quality under time pressure. Search results often prioritize visibility over suitability, which forces clients to compare providers without a consistent framework for trust, scope, or likely outcomes. This produces delays, false starts, and poor-fit bookings. A healthier model starts by reducing ambiguity early: clear role definition, transparent credentials, and explicit service boundaries. When these fundamentals are visible from the first click, clients make better choices with less friction.',
        },
        {
          heading: 'Why Trust Signals Often Fail on Generic Platforms',
          content:
            'Many platforms rely on profile volume and user ratings but fail to verify the details that matter most in regulated or high-stakes work. A star rating alone cannot confirm whether a professional is operating within the right scope, in the right jurisdiction, with the right level of accountability. Clients then compensate by over-researching, which slows conversion and increases anxiety. Verification-first systems work better because they reduce unknowns at the start of the journey, allowing the conversation to focus on fit and outcomes instead of basic credibility checks.',
        },
        {
          heading: 'What High-Intent Clients Need to Decide Quickly',
          content:
            'High-intent clients usually need four things to move forward: confidence in credentials, clarity on scope, realistic timing, and a simple next step. If any one of these is missing, momentum drops. Effective marketplaces therefore need profiles that communicate practical capability, not just marketing claims. They also need structured first-contact pathways that help clients explain their situation without long forms or repeated back-and-forth. The result is a better matching loop: fewer low-fit enquiries, stronger first conversations, and faster progression to booking.',
        },
        {
          heading: 'Why This Matters for Professionals as Well',
          content:
            'Poor discovery systems hurt professionals too. They create a pipeline full of vague enquiries, misaligned expectations, and preventable drop-off after first contact. When trust and scope are clarified earlier, professionals spend less time qualifying weak leads and more time delivering value to suitable clients. Over time, this improves retention, review quality, and operational consistency. In other words, better client discovery is not just a marketing problem; it is a service-quality and business-efficiency problem on both sides of the marketplace.',
        },
        {
          heading: 'A Better Marketplace Standard',
          content:
            `For multi-region marketplaces such as EUCA Match, the standard should be straightforward: verification-first discovery, transparent profile structure, and practical conversion pathways that work across Australia, New Zealand, United States, Canada, United Kingdom, Ireland, Singapore, and Hong Kong. This combination gives clients the confidence to act and gives professionals a healthier demand funnel. The more the platform reduces avoidable uncertainty at each step, the more sustainable growth becomes for everyone.`,
        },
      ],
    },
    {
      slug: 'how-independent-professionals-can-grow-bookings-across-multiple-regions',
      title: 'How Independent Professionals Can Grow Bookings Across Multiple Regions',
      summary:
        'A practical operating model for independent professionals expanding client acquisition and retention across Australia, New Zealand, United States, Canada, United Kingdom, Ireland, Singapore, and Hong Kong.',
      category: 'Growth Strategy',
      audience: 'Professionals',
      regionSlug: 'global',
      regionLabel: 'Global',
      professionSlug: 'cross-industry',
      professionLabel: 'Cross-Industry',
      painPoint:
        'Independent professionals often have capability to scale but lack a repeatable growth and conversion system across regions.',
      updatedAt: KNOWLEDGE_BASE_UPDATED_AT,
      keywords: ['professional growth', 'independent practice', 'multi-region expansion', 'qualified bookings'],
      searchHref: '/search',
      joinUsHref: '/join-us',
      sections: [
        {
          heading: 'Start With a Repeatable Core Offer',
          content:
            'Most cross-region expansion fails because professionals localize everything at once and lose operational consistency. The better approach is to standardize your core service model first: who you serve, what outcomes you deliver, what your intake process requires, and what boundaries define a good-fit engagement. When this core is stable, you can scale demand without recreating your business in every market. A repeatable operating foundation also makes profile optimization, enquiry qualification, and handover workflows easier to maintain as volume grows.',
        },
        {
          heading: 'Localize Compliance, Language, and Expectations',
          content:
            'Localization should focus on the elements that genuinely change by region: credential language, legal or regulatory boundaries, communication norms, and expected response cadence. Keep the underlying workflow consistent, then adapt the outward-facing details clients use to evaluate fit. This avoids duplication while preserving relevance. Professionals who make these targeted adjustments tend to convert faster because prospects immediately understand whether they are eligible, in-scope, and likely to receive the style of support they expect.',
        },
        {
          heading: 'Build a Conversion System, Not Just a Lead Funnel',
          content:
            'Growth across regions is mostly a conversion discipline problem. You need a consistent response framework from first message to booked session, including qualification prompts, scope confirmation, booking clarity, and structured follow-up. Without that system, more traffic simply produces more unclosed conversations. Professionals who convert well usually have response templates, timing standards, and clear next-step logic built into daily operations. The objective is to make conversion quality independent of mood, workload, or channel.',
        },
        {
          heading: 'Treat Reviews and Retention as Growth Multipliers',
          content:
            'Acquisition is expensive when retention is weak. Cross-region practitioners should therefore design each engagement to generate both immediate value and future continuity. Close every matter with a practical summary, propose next checkpoints, and request feedback when outcomes are visible. Reviews improve trust for new clients, while continuity pathways improve lifetime value for existing clients. This dual strategy compounds over time and reduces reliance on constant top-of-funnel volume.',
        },
        {
          heading: 'Use Weekly Metrics to Improve the Right Layer',
          content:
            'Track a small set of leading indicators each week: profile view-to-enquiry rate, enquiry-to-booking rate, first-response time, no-show rate, and repeat-booking rate. These metrics show where momentum is being lost. If enquiries are high but bookings are low, improve qualification and booking clarity. If bookings are strong but retention is weak, improve close-out and follow-up systems. Disciplined measurement lets independent professionals scale sustainably without losing quality as they expand across multiple regions.',
        },
      ],
    },
  ]

  return [...globalArticles, ...generated].slice(0, 50)
}

const KNOWLEDGE_BASE_ARTICLES: KnowledgeBaseArticle[] = createKnowledgeBaseArticles()
type KnowledgeBaseTagOption = {
  id: string
  label: string
  count: number
}

const KNOWLEDGE_BASE_GENERIC_TAGS = new Set([
  'euca match knowledge base',
  'verified professionals',
  'independent professionals',
  'professional marketplace',
])

const getKnowledgeBaseDiscoverabilityTags = (article: KnowledgeBaseArticle): string[] => {
  const source = [
    article.category,
    article.professionLabel,
    article.regionLabel,
    article.audience === 'Both' ? 'Clients' : article.audience,
    ...(article.audience === 'Both' ? ['Professionals'] : []),
    ...article.keywords,
  ]
  const seen = new Set<string>()
  const tags: string[] = []
  source.forEach((rawTag) => {
    const label = String(rawTag || '').trim()
    if (!label || label.length > 42) return
    const lowered = label.toLowerCase()
    if (KNOWLEDGE_BASE_GENERIC_TAGS.has(lowered)) return
    if (seen.has(lowered)) return
    seen.add(lowered)
    tags.push(label)
  })
  return tags.slice(0, 10)
}

const KNOWLEDGE_BASE_TAG_OPTIONS: KnowledgeBaseTagOption[] = (() => {
  const counts = new Map<string, KnowledgeBaseTagOption>()
  KNOWLEDGE_BASE_ARTICLES.forEach((article) => {
    getKnowledgeBaseDiscoverabilityTags(article).forEach((label) => {
      const id = slugify(label)
      if (!id) return
      const existing = counts.get(id)
      if (existing) {
        existing.count += 1
        return
      }
      counts.set(id, { id, label, count: 1 })
    })
  })
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 24)
})()

const KNOWLEDGE_BASE_TAG_LABEL_BY_ID = new Map(KNOWLEDGE_BASE_TAG_OPTIONS.map((tag) => [tag.id, tag.label]))
const KNOWLEDGE_BASE_ARTICLE_MAP = new Map(KNOWLEDGE_BASE_ARTICLES.map((article) => [article.slug, article]))

type JoinUsRegion = {
  slug: string
  label: string
  kind: 'Country' | 'Region'
  summary: string
}

type JoinUsProfession = {
  slug: string
  label: string
  summary: string
}

type MigrationRegionCopy = {
  title: string
  intro: string
  onboarding: string
  whoThisSuits: string
  discoveryLine: string
}

type FinancialRegionCopy = {
  title: string
  intro: string
  onboarding: string
  whoThisSuitsLead: string
  whoThisSuitsItems: string[]
  discoveryLine: string
}

type AlliedRegionCopy = {
  title: string
  intro: string
  onboarding: string
  whoCanApplyLead: string
  credentialItems: string[]
  discoveryLine: string
}

type DoctorRegionCopy = {
  title: string
  intro: string
  onboarding: string
  whoCanApplyLead: string
  credentialItems: string[]
  discoveryLine: string
}

type EducationRegionCopy = {
  title: string
  intro: string
  onboarding: string
  requirementsLead: string
  discoveryLine: string
}

type PropertyRegionCopy = {
  title: string
  intro: string
  onboarding: string
  suitedLead: string
  discoveryLine: string
}

type TechRegionCopy = {
  title: string
  intro: string
  onboarding: string
  suitedLead: string
  discoveryLine: string
}

type LegalRegionCopy = {
  title: string
  intro: string
  onboarding: string
  whoCanApplyLead: string
  credentialItems: string[]
  discoveryLine: string
}

const JOIN_US_REGIONS: JoinUsRegion[] = [
  {
    slug: 'australia',
    label: 'Australia',
    kind: 'Country',
    summary: 'Launch opportunities and onboarding pathways for professionals based in Australia.',
  },
  {
    slug: 'new-zealand',
    label: 'New Zealand',
    kind: 'Country',
    summary: 'Localized opportunities for professionals serving clients across New Zealand.',
  },
  {
    slug: 'united-states',
    label: 'United States',
    kind: 'Country',
    summary: 'Market-ready pathways for US-based professionals across all eight industries.',
  },
  {
    slug: 'canada',
    label: 'Canada',
    kind: 'Country',
    summary: 'Country-specific opportunities and role structures for professionals in Canada.',
  },
  {
    slug: 'united-kingdom',
    label: 'United Kingdom',
    kind: 'Country',
    summary: 'UK-focused professional opportunities and onboarding details by industry.',
  },
  {
    slug: 'ireland',
    label: 'Ireland',
    kind: 'Country',
    summary: 'Role pathways and recruitment opportunities tailored for professionals in Ireland.',
  },
  {
    slug: 'singapore',
    label: 'Singapore',
    kind: 'Country',
    summary: 'Opportunities and network growth plans for Singapore-based professionals.',
  },
  {
    slug: 'hong-kong',
    label: 'Hong Kong',
    kind: 'Region',
    summary: 'Region-specific opportunity streams and onboarding for Hong Kong professionals.',
  },
]

const JOIN_US_PROFESSIONS: JoinUsProfession[] = industries.map((item) => ({
  slug: item.value,
  label: item.label,
  summary: item.description,
}))

const JOIN_US_REGION_MAP = new Map(JOIN_US_REGIONS.map((region) => [region.slug, region]))
const JOIN_US_PROFESSION_MAP = new Map(JOIN_US_PROFESSIONS.map((profession) => [profession.slug, profession]))
const JOIN_US_REGION_COUNTRY_CODE: Record<string, string> = {
  australia: 'AU',
  'new-zealand': 'NZ',
  'united-states': 'US',
  canada: 'CA',
  'united-kingdom': 'GB',
  ireland: 'IE',
  singapore: 'SG',
  'hong-kong': 'HK',
}
const JOIN_US_PROFESSION_JOB_TITLE: Record<string, string> = {
  allied: 'Allied Health Professional',
  doctor: 'Medical Doctor',
  education: 'Teacher or Tutor',
  financial: 'Accounting and Financial Professional',
  legal: 'Legal, Governance, and Compliance Professional',
  migration: 'Migration Professional',
  property: 'Architecture, Design, and Property Professional',
  tech: 'Tech, Creative, and Media Professional',
}
const JOIN_US_PROFESSION_KEYWORDS: Record<string, string> = {
  allied: 'allied health, telehealth, physiotherapy, psychology, counselling',
  doctor: 'medical doctor, telehealth, general practice, specialist',
  education: 'teacher, tutor, education, exam preparation, online lessons',
  financial: 'accounting, tax, financial adviser, mortgage broker',
  legal: 'lawyer, solicitor, governance, compliance, risk',
  migration: 'migration agent, immigration adviser, visa',
  property: 'architect, interior designer, property consultant, buyers agent',
  tech: 'software developer, designer, media, digital marketing, product',
}
const JOIN_US_JOB_DATE_POSTED = '2026-03-05'
const JOIN_US_JOB_VALID_THROUGH = '2027-03-05T23:59:59Z'

const MIGRATION_SHARED_INTRO =
  'EUCA Match is a global professional marketplace connecting clients with verified, independent migration professionals.'

const MIGRATION_REGION_COPY: Record<string, MigrationRegionCopy> = {
  australia: {
    title: 'Migration Agents (MARA) - Join EUCA Match (Australia)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based professionals who practise independently under MARA registration.',
    whoThisSuits: 'Registered Migration Agents (MARA) in Australia.',
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Licensed Immigration Advisers - Join EUCA Match (New Zealand)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based professionals who practise independently under Immigration Advisers Authority (IAA) licensing and related legal authorisations.',
    whoThisSuits:
      'Licensed Immigration Advisers (LIA) and immigration lawyers in New Zealand who provide services independently.',
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Immigration Professionals - Join EUCA Match (United States)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding US-based professionals who practice independently in line with US immigration-advice requirements.',
    whoThisSuits:
      'US immigration attorneys in good standing and DOJ-accredited representatives (where applicable) who practice independently.',
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Immigration Professionals - Join EUCA Match (Canada)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based professionals who practise independently under applicable Canadian immigration-advice regulation.',
    whoThisSuits:
      'Regulated Canadian Immigration Consultants (RCIC), immigration lawyers, and other lawfully authorised Canadian practitioners.',
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Immigration Professionals - Join EUCA Match (United Kingdom)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding UK-based professionals who practise independently under applicable UK immigration-advice regulation.',
    whoThisSuits:
      'Immigration advisers regulated by the UK Immigration Advice Authority (IAA), immigration solicitors, and other authorised UK practitioners.',
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Immigration Professionals - Join EUCA Match (Ireland)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based professionals who practise independently under applicable Irish professional regulation.',
    whoThisSuits:
      'Immigration solicitors, barristers, and other professionals lawfully authorised to provide immigration advice in Ireland.',
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Immigration and Work Pass Professionals - Join EUCA Match (Singapore)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based professionals who practise independently in line with applicable Singapore requirements.',
    whoThisSuits:
      'Professionals lawfully authorised to provide immigration or work pass advisory services in Singapore, including licence holders where required.',
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Immigration Professionals - Join EUCA Match (Hong Kong)',
    intro: MIGRATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based professionals who practise independently in line with applicable Hong Kong requirements.',
    whoThisSuits:
      'Professionals lawfully authorised to provide immigration advisory services in Hong Kong, including qualified legal practitioners where applicable.',
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const MIGRATION_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes with AI assistance for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const MIGRATION_WORK_AREAS = [
  'Skilled visas',
  'Family and partner visas',
  'Employer sponsorship',
  'Permanent residency pathways',
  'Citizenship',
  'Visa refusals, cancellations and appeals',
]

const FINANCIAL_SHARED_INTRO =
  'EUCA Match is a global professional marketplace connecting clients with verified, qualified financial professionals across accounting, tax, lending, and financial advice.'

const FINANCIAL_REGION_COPY: Record<string, FinancialRegionCopy> = {
  australia: {
    title: 'Accountants and Financial Professionals - Join EUCA Match (Australia)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across Australia who are authorised to practise independently under Australian regulatory and professional frameworks, including CA, CPA, the Tax Practitioners Board (TPB), ASIC, and ACL/AFSL requirements.',
    whoThisSuitsLead: 'Professionals with relevant Australian registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Accountants (CA)',
      'Certified Practising Accountants (CPA)',
      'Registered Tax Agents (TPB)',
      'Financial Advisers and Planners (AFSL holder or authorised representative)',
      'Mortgage and Finance Brokers (ACL holder or authorised representative)',
    ],
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Accountants and Financial Professionals - Join EUCA Match (New Zealand)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across New Zealand who are authorised to practise independently under New Zealand regulatory and professional frameworks, including CA ANZ or CPA pathways, Inland Revenue tax-agent obligations, and Financial Advice Provider (FAP) requirements.',
    whoThisSuitsLead: 'Professionals with relevant New Zealand registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Accountants (CA ANZ) and CPA-qualified accountants',
      'Tax professionals authorised to act for clients with Inland Revenue',
      'Financial advisers operating under a licensed Financial Advice Provider (FAP)',
      'Mortgage and finance advisers operating under applicable FAP and disclosure requirements',
    ],
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Accountants and Financial Professionals - Join EUCA Match (United States)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across the United States who are authorized to practice independently under applicable federal and state frameworks, including state CPA board requirements, IRS tax-practitioner rules, securities and advisory registration requirements, and NMLS or state mortgage-licensing obligations.',
    whoThisSuitsLead: 'Professionals with relevant United States registration or licensing, including:',
    whoThisSuitsItems: [
      'Certified Public Accountants (CPA)',
      'Enrolled Agents (EA) and other authorised tax professionals',
      'Financial advisers and planners with required federal or state registrations',
      'Mortgage and finance brokers or loan originators with required NMLS and state licensing',
    ],
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Accountants and Financial Professionals - Join EUCA Match (Canada)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across Canada who are authorised to practise independently under Canadian professional and regulatory frameworks, including CPA requirements, CRA tax-representation requirements, provincial securities rules, and provincial mortgage-licensing obligations.',
    whoThisSuitsLead: 'Professionals with relevant Canadian registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Professional Accountants (CPA)',
      'Tax professionals authorised to represent clients with the Canada Revenue Agency',
      'Financial advisers and planners with required securities or advisory registrations',
      'Mortgage and finance brokers licensed under provincial regulators',
    ],
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Accountants and Financial Professionals - Join EUCA Match (United Kingdom)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across the United Kingdom who are authorised to practise independently under UK professional and regulatory frameworks, including chartered accountancy bodies, HMRC agent requirements, and FCA authorisation requirements for advice and mortgage activity.',
    whoThisSuitsLead: 'Professionals with relevant United Kingdom registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Accountants (ACA, ACCA, CIMA, or equivalent UK-recognised qualifications)',
      'Tax professionals registered for HMRC agent services',
      'Financial advisers and planners authorised by the FCA or operating as an appointed representative',
      'Mortgage advisers and brokers authorised by the FCA or operating as an appointed representative',
    ],
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Accountants and Financial Professionals - Join EUCA Match (Ireland)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across Ireland who are authorised to practise independently under Irish professional and regulatory frameworks, including accountancy body memberships, tax-practitioner requirements, and Central Bank of Ireland authorisation requirements for financial and mortgage services.',
    whoThisSuitsLead: 'Professionals with relevant Irish registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Accountants and accountants with recognised Irish qualifications',
      'Tax professionals with relevant Irish tax registrations or authorisations',
      'Financial advisers and investment intermediaries authorised by the Central Bank of Ireland',
      'Mortgage intermediaries authorised by the Central Bank of Ireland',
    ],
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Accountants and Financial Professionals - Join EUCA Match (Singapore)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across Singapore who are authorised to practise independently under Singapore professional and regulatory frameworks, including accountancy standards, IRAS tax-practice requirements, and MAS regulatory requirements for financial advice and lending-related activities.',
    whoThisSuitsLead: 'Professionals with relevant Singapore registration or licensing, including:',
    whoThisSuitsItems: [
      'Chartered Accountants and other qualified accountants in Singapore',
      'Tax professionals authorised to act for clients under IRAS requirements',
      'Financial advisers and planners operating under applicable MAS licensing or representative frameworks',
      'Mortgage and finance advisers operating under applicable Singapore regulatory requirements',
    ],
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Accountants and Financial Professionals - Join EUCA Match (Hong Kong)',
    intro: FINANCIAL_SHARED_INTRO,
    onboarding:
      'We are onboarding professionals across Hong Kong who are authorised to practise independently under Hong Kong professional and regulatory frameworks, including accountancy standards, Inland Revenue representation requirements, and licensing requirements for financial advice and lending-related services.',
    whoThisSuitsLead: 'Professionals with relevant Hong Kong registration or licensing, including:',
    whoThisSuitsItems: [
      'Certified Public Accountants and other qualified accounting professionals in Hong Kong',
      'Tax professionals authorised to represent clients for tax matters in Hong Kong',
      'Financial advisers and planners licensed or authorised under applicable Hong Kong regulatory frameworks',
      'Mortgage and finance brokers operating under applicable Hong Kong licensing requirements',
    ],
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const FINANCIAL_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes with AI assistance for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const FINANCIAL_ACCOUNTING_TAX_AREAS = [
  'Individual and business tax returns',
  'BAS, GST, and compliance services',
  'Business structuring and advisory',
  'SMSF accounting and compliance',
  'Payroll and employment tax matters',
  'Tax planning and tax authority liaison',
]

const FINANCIAL_ADVICE_AREAS = [
  'Personal financial planning',
  'Cash-flow and budgeting advice',
  'Superannuation and retirement planning',
  'Investment strategy and risk management',
  'Insurance and wealth protection advice',
]

const FINANCIAL_LENDING_AREAS = [
  'Home loans and refinancing',
  'Investment and commercial lending',
  'First-home buyer support',
  'Loan structuring and comparisons',
  'Pre-approval and settlement coordination',
]

const ALLIED_SHARED_INTRO =
  'EUCA Match is a global professional marketplace connecting clients with verified allied health professionals for telehealth, face-to-face, and hybrid care models.'

const ALLIED_REGION_COPY: Record<string, AlliedRegionCopy> = {
  australia: {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (Australia)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Australian registration or accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (AHPRA)',
      'Social Worker (AASW)',
      'Counsellor (PACFA or ACA)',
      'Speech Pathologist (Speech Pathology Australia)',
      'Audiologist (Audiology Australia)',
      'Accredited Practising Dietitian (Dietitians Australia)',
      'Diabetes Educator (ADEA)',
      'Physiotherapist (AHPRA)',
      'Exercise Physiologist (ESSA)',
      'Occupational Therapist (AHPRA)',
      'Chiropractor (AHPRA)',
      'Osteopath (AHPRA)',
      'Podiatrist (AHPRA)',
      'Optometrist (AHPRA)',
      'Orthoptist (Orthoptics Australia)',
      'Pharmacist (AHPRA)',
      'Chinese Medicine Practitioner or Acupuncturist (AHPRA)',
    ],
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (New Zealand)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current New Zealand registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (New Zealand Psychologists Board)',
      'Social Worker (Social Workers Registration Board)',
      'Counsellor (New Zealand Association of Counsellors or equivalent recognised professional body)',
      'Speech-Language Therapist (New Zealand Speech-language Therapists Association and any required registration)',
      'Audiologist (recognised New Zealand audiology credentialing pathway)',
      'Dietitian (Dietitians Board of New Zealand and Dietitians New Zealand where applicable)',
      'Diabetes Educator (recognised New Zealand diabetes-education credentialing)',
      'Physiotherapist (Physiotherapy Board of New Zealand)',
      'Clinical Exercise Physiologist (recognised New Zealand exercise-science credentialing pathway)',
      'Occupational Therapist (Occupational Therapy Board of New Zealand)',
      'Chiropractor (New Zealand Chiropractic Board)',
      'Osteopath (Osteopathic Council of New Zealand)',
      'Podiatrist (Podiatrists Board of New Zealand)',
      'Optometrist (Optometrists and Dispensing Opticians Board)',
      'Orthoptist (recognised New Zealand orthoptic credentialing pathway)',
      'Pharmacist (Pharmacy Council of New Zealand)',
      'Acupuncturist or Chinese Medicine Practitioner (recognised New Zealand registration or professional accreditation)',
    ],
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (United States)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based allied health practitioners who practice independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current United States state or federal registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (state psychology board licensure)',
      'Clinical Social Worker (state social work board licensure)',
      'Counsellor or Therapist (state counselling or mental health board licensure)',
      'Speech-Language Pathologist (state licensure; ASHA credentials where applicable)',
      'Audiologist (state licensure and recognised professional credentials)',
      'Registered Dietitian Nutritionist (RDN; plus state licensure where required)',
      'Diabetes Care and Education Specialist (CDCES or equivalent recognised credential)',
      'Physical Therapist (state physical therapy board licensure)',
      'Clinical Exercise Physiologist (ACSM-CEP or equivalent credential where required)',
      'Occupational Therapist (state licensure; NBCOT where required)',
      'Chiropractor (state chiropractic board licensure)',
      'Osteopathic practitioner (where independently regulated and permitted by state law)',
      'Podiatrist (state podiatry board licensure)',
      'Optometrist (state optometry board licensure)',
      'Orthoptist (recognised orthoptic certification and employer credentialing)',
      'Pharmacist (state board of pharmacy licensure)',
      'Acupuncturist or Chinese Medicine Practitioner (state acupuncture board licensure where required)',
    ],
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (Canada)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Canadian provincial or territorial registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (provincial or territorial psychology regulator)',
      'Social Worker (provincial or territorial social work regulator)',
      'Counsellor or Psychotherapist (provincial regulator or recognised professional body, depending jurisdiction)',
      'Speech-Language Pathologist (provincial regulator)',
      'Audiologist (provincial regulator)',
      'Registered Dietitian (provincial regulator)',
      'Diabetes Educator (CDE or other recognised Canadian credential)',
      'Physiotherapist (provincial regulator)',
      'Clinical Exercise Physiologist (CSEP-CEP or equivalent credential where required)',
      'Occupational Therapist (provincial regulator)',
      'Chiropractor (provincial regulator)',
      'Osteopathic practitioner (where regulated or otherwise permitted in your province or territory)',
      'Podiatrist or Chiropodist (provincial regulator where required)',
      'Optometrist (provincial regulator)',
      'Orthoptist (recognised orthoptic certification and employer credentialing)',
      'Pharmacist (provincial regulator)',
      'Acupuncturist or Traditional Chinese Medicine Practitioner (provincial regulator where required)',
    ],
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (United Kingdom)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current United Kingdom registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Practitioner Psychologist (HCPC)',
      'Social Worker (registered with the relevant UK social work regulator)',
      'Counsellor or Psychotherapist (BACP, UKCP, or equivalent recognised body; statutory registration where required)',
      'Speech and Language Therapist (HCPC)',
      'Audiologist or Hearing Aid Dispenser (HCPC where applicable)',
      'Dietitian (HCPC)',
      'Diabetes Educator (recognised diabetes credentialing with relevant professional registration)',
      'Physiotherapist (HCPC)',
      'Clinical Exercise Physiologist (BASES, BACPR, or equivalent recognised UK pathway)',
      'Occupational Therapist (HCPC)',
      'Chiropractor (General Chiropractic Council)',
      'Osteopath (General Osteopathic Council)',
      'Podiatrist (HCPC)',
      'Optometrist (General Optical Council)',
      'Orthoptist (HCPC)',
      'Pharmacist (General Pharmaceutical Council or Pharmaceutical Society of Northern Ireland)',
      'Acupuncturist or Chinese Medicine Practitioner (recognised UK professional body and any required authorisation)',
    ],
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (Ireland)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Irish registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (relevant Irish registration or recognised chartered status)',
      'Social Worker (CORU)',
      'Counsellor or Psychotherapist (IACP, PSI, or equivalent recognised professional body)',
      'Speech and Language Therapist (CORU)',
      'Audiologist (recognised Irish registration or professional accreditation)',
      'Dietitian (CORU)',
      'Diabetes Educator (recognised diabetes credentialing with relevant professional registration)',
      'Physiotherapist (CORU)',
      'Clinical Exercise Physiologist (recognised Irish or UK exercise-physiology credentialing pathway)',
      'Occupational Therapist (CORU)',
      'Chiropractor (recognised Irish professional registration)',
      'Osteopath (recognised Irish professional registration)',
      'Podiatrist (CORU)',
      'Optometrist (Opticians Board registration where required)',
      'Orthoptist (recognised professional registration in Ireland)',
      'Pharmacist (Pharmaceutical Society of Ireland)',
      'Acupuncturist or Chinese Medicine Practitioner (recognised Irish professional registration or accreditation)',
    ],
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (Singapore)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Singapore registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (Singapore Register of Psychologists or equivalent recognised credentialing)',
      'Social Worker (Singapore Association of Social Workers registration where applicable)',
      'Counsellor (Singapore Association for Counselling registration)',
      'Speech Therapist (Allied Health Professions Council where required)',
      'Audiologist (Allied Health Professions Council where required)',
      'Dietitian (Allied Health Professions Council where required)',
      'Diabetes Educator (recognised Singapore diabetes-education credentialing)',
      'Physiotherapist (Allied Health Professions Council)',
      'Exercise Physiologist (recognised Singapore exercise-science credentialing pathway)',
      'Occupational Therapist (Allied Health Professions Council)',
      'Chiropractor (recognised Singapore professional registration and compliance obligations)',
      'Osteopath (recognised Singapore professional registration and compliance obligations)',
      'Podiatrist (Allied Health Professions Council where required)',
      'Optometrist (Optometrists and Opticians Board)',
      'Orthoptist (recognised orthoptic credentialing and employer requirements)',
      'Pharmacist (Singapore Pharmacy Council)',
      'Chinese Medicine Practitioner or Acupuncturist (Traditional Chinese Medicine Practitioners Board)',
    ],
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Allied Health Professionals (Telehealth and Face-to-Face) - Join EUCA Match (Hong Kong)',
    intro: ALLIED_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based allied health practitioners who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Hong Kong registration, licensing, or professional accreditation in one of the following disciplines:',
    credentialItems: [
      'Psychologist (recognised Hong Kong professional registration or chartered status)',
      'Social Worker (Social Workers Registration Board)',
      'Counsellor (recognised Hong Kong counselling professional body)',
      'Speech Therapist (recognised Hong Kong speech-therapy registration or credentialing)',
      'Audiologist (recognised Hong Kong audiology credentialing pathway)',
      'Dietitian (recognised Hong Kong dietetics credentialing pathway)',
      'Diabetes Educator (recognised Hong Kong diabetes-education credentialing)',
      'Physiotherapist (Physiotherapists Board of Hong Kong)',
      'Exercise Physiologist (recognised Hong Kong exercise-science credentialing pathway)',
      'Occupational Therapist (Occupational Therapists Board of Hong Kong)',
      'Chiropractor (Chiropractors Council of Hong Kong)',
      'Osteopath (recognised Hong Kong osteopathy credentialing pathway)',
      'Podiatrist (Podiatrists Board of Hong Kong)',
      'Optometrist (Optometrists Board of Hong Kong)',
      'Orthoptist (recognised Hong Kong orthoptic credentialing pathway)',
      'Pharmacist (Pharmacy and Poisons Board of Hong Kong)',
      'Chinese Medicine Practitioner or Acupuncturist (Chinese Medicine Council of Hong Kong)',
    ],
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const ALLIED_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const ALLIED_ABOUT_PARAGRAPHS = [
  'EUCA Match was founded by Dr Lily Vu and Dr Dong Fang Zhao, co-founders of EUCA MD, a separate doctor-led Australian telehealth practice trusted by thousands of patients Australia-wide, with 390+ five-star Google reviews.',
  'EUCA Match applies the same trust-first, verification-driven approach across multiple professions, while respecting how each profession practises.',
]

const DOCTOR_SHARED_INTRO =
  'EUCA Match is a global professional matching and booking platform connecting clients with verified medical doctors.'

const DOCTOR_REGION_COPY: Record<string, DoctorRegionCopy> = {
  australia: {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (Australia)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Australian medical doctor registration and any required specialist recognition to practise independently, including:',
    credentialItems: [
      'Medical Practitioner registration with AHPRA',
      'General Practitioners with relevant fellowship pathways where applicable (RACGP or ACRRM)',
      'Specialist Medical Practitioners with recognised specialist registration and college standing',
    ],
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (New Zealand)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current New Zealand medical doctor registration and any required vocational recognition to practise independently, including:',
    credentialItems: [
      'Medical registration with the Medical Council of New Zealand',
      'General practitioners with relevant fellowship pathways where applicable (FRNZCGP or equivalent)',
      'Specialists with recognised vocational scope and relevant college standing',
    ],
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (United States)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based medical doctors who practice independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current United States medical doctor licensing and credentials to practice independently, including:',
    credentialItems: [
      'Active and unrestricted state medical licence in each state of practice',
      'Board-certified physicians (or board-eligible within permitted pathways) in their clinical scope',
      'Any required federal or state prescribing and telemedicine compliance credentials where applicable',
    ],
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (Canada)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Canadian medical doctor licensing and credentials to practise independently, including:',
    credentialItems: [
      'Active provincial or territorial medical licence with the relevant College of Physicians and Surgeons',
      'General practitioners with College of Family Physicians of Canada certification where applicable',
      'Specialists with Royal College of Physicians and Surgeons of Canada certification where applicable',
    ],
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (United Kingdom)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current United Kingdom medical doctor licensing and credentials to practise independently, including:',
    credentialItems: [
      'Full GMC registration with a licence to practise',
      'GP doctors with relevant GP training completion and standing (for example MRCGP with CCT pathways where applicable)',
      'Specialists with GMC specialist registration and recognised college membership or fellowship where applicable',
    ],
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (Ireland)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Irish medical doctor licensing and credentials to practise independently, including:',
    credentialItems: [
      'Active registration with the Medical Council of Ireland',
      'General practitioners with recognised Irish College of General Practitioners pathways where applicable',
      'Specialists with relevant specialist division status and recognised postgraduate college standing',
    ],
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (Singapore)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Singapore medical doctor licensing and credentials to practise independently, including:',
    credentialItems: [
      'Registration with the Singapore Medical Council and a valid practising certificate',
      'General practitioners with relevant credentials and compliance for independent practice',
      'Specialists with Specialist Accreditation Board recognition and relevant standing where applicable',
    ],
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Medical Doctors (Telehealth and Face-to-Face) - Join EUCA Match (Hong Kong)',
    intro: DOCTOR_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based medical doctors who practise independently and provide services via telehealth, face-to-face, or through hybrid models.',
    whoCanApplyLead:
      'You must hold current Hong Kong medical doctor licensing and credentials to practise independently, including:',
    credentialItems: [
      'Full registration with the Medical Council of Hong Kong and a valid practising certificate',
      'General practitioners with recognised independent-practice standing',
      'Specialists with Hong Kong Academy of Medicine specialist recognition and relevant college fellowship where applicable',
    ],
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const DOCTOR_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const EDUCATION_SHARED_INTRO =
  'EUCA Match is a global professional matching and booking platform connecting students and families with verified, independent teachers and tutors across all subjects.'

const EDUCATION_REGION_COPY: Record<string, EducationRegionCopy> = {
  australia: {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (Australia)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in Australia, including:',
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (New Zealand)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in New Zealand, including:',
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (United States)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based teachers, educators, and tutors who practice independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in the United States, including:',
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (Canada)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in Canada, including:',
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (United Kingdom)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in the United Kingdom, including:',
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (Ireland)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in Ireland, including:',
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (Singapore)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in Singapore, including:',
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Teachers and Tutors (All Subjects) - Join EUCA Match (Hong Kong)',
    intro: EDUCATION_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based teachers, educators, and tutors who practise independently and provide services online, face-to-face, or via hybrid models.',
    requirementsLead: 'This opportunity suits independent teachers and tutors in Hong Kong, including:',
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const EDUCATION_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const EDUCATION_REQUIREMENT_ITEMS = [
  'Qualified school teachers',
  'Tutors across all subjects',
  'Primary, secondary, and senior educators',
  'Exam preparation and specialist subject tutors',
  'Independent educators (online or in-person)',
]

const EDUCATION_SUBJECT_ITEMS = [
  'Maths, English, and Science',
  'Humanities and social sciences',
  'Languages (including ESL)',
  'Arts, music, and creative subjects',
  'Exam preparation',
  'Business, economics, and accounting',
  'Special exams (for example GAMSAT, LSAT, and similar pathways)',
]

const PROPERTY_SHARED_INTRO =
  'EUCA Match is a global professional matching and booking platform connecting clients with verified, independent professionals across architecture, design, and property services.'

const PROPERTY_REGION_COPY: Record<string, PropertyRegionCopy> = {
  australia: {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (Australia)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Australia, including:',
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (New Zealand)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in New Zealand, including:',
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (United States)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based architects, interior designers, and property professionals who practice independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in the United States, including:',
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (Canada)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Canada, including:',
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (United Kingdom)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in the United Kingdom, including:',
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (Ireland)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Ireland, including:',
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (Singapore)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Singapore, including:',
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Architects, Interior Designers and Property Professionals - Join EUCA Match (Hong Kong)',
    intro: PROPERTY_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based architects, interior designers, and property professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Hong Kong, including:',
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const PROPERTY_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const PROPERTY_SUITED_ITEMS = [
  'Registered Architects',
  'Interior Designers',
  'Building and Architectural Designers',
  "Buyers' Agents and Property Consultants",
  'Real Estate and Development Professionals',
]

const PROPERTY_WORK_AREAS = [
  'Residential and commercial design',
  'Planning and DA documentation',
  'Interior design and fit-outs',
  'Renovations and new builds',
  'Buyer advocacy and property advisory',
  'Development feasibility and project consulting',
]

const TECH_SHARED_INTRO =
  'EUCA Match is a global professional matching and booking platform connecting clients with verified, independent professionals across tech, creative, and media.'

const TECH_REGION_COPY: Record<string, TechRegionCopy> = {
  australia: {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (Australia)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Australia, including:',
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (New Zealand)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in New Zealand, including:',
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (United States)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based professionals in tech, creative, and media who practice independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in the United States, including:',
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (Canada)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Canada, including:',
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (United Kingdom)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in the United Kingdom, including:',
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (Ireland)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Ireland, including:',
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (Singapore)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Singapore, including:',
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Tech, Creative, and Media Professionals - Join EUCA Match (Hong Kong)',
    intro: TECH_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based professionals in tech, creative, and media who practise independently and provide services online, face-to-face, or via hybrid models.',
    suitedLead: 'This opportunity is suited for independent professionals in Hong Kong, including:',
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const TECH_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const TECH_SUITED_ITEMS = [
  'Software developers and engineers',
  'UX and UI designers',
  'Product and project managers',
  'Data and AI specialists',
  'Cybersecurity and cloud specialists',
  'Digital marketers and growth specialists',
  'Content creators, editors, and copywriters',
  'Brand designers, video producers, and media professionals',
]

const TECH_WORK_AREAS = [
  'Website and app development',
  'Product design, UX/UI, and prototyping',
  'Automation, AI workflow, and technical consulting',
  'Data analytics, dashboards, and reporting',
  'SEO, paid media, and digital growth campaigns',
  'Branding, content strategy, and copywriting',
  'Video production, editing, and podcast media',
  'Social media strategy and campaign execution',
]

const LEGAL_SHARED_INTRO =
  'EUCA Match is a global professional matching and booking platform connecting clients with verified, independent professionals across legal, governance, and compliance services.'

const LEGAL_REGION_COPY: Record<string, LegalRegionCopy> = {
  australia: {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (Australia)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding Australia-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant Australian registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Australian legal practitioners (solicitors and barristers) with current practising certificates',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'Australia-wide client discovery',
  },
  'new-zealand': {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (New Zealand)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding New Zealand-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant New Zealand registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Lawyers with current New Zealand practising certificates',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'New Zealand-wide client discovery',
  },
  'united-states': {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (United States)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding United States-based legal, governance, and compliance professionals who practice independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant United States registration, bar admission, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Attorneys admitted and in good standing in at least one state bar',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'United States-wide client discovery',
  },
  canada: {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (Canada)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding Canada-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant Canadian registration, law society standing, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Lawyers (and, where applicable, licensed paralegals) in good standing with provincial or territorial regulators',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'Canada-wide client discovery',
  },
  'united-kingdom': {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (United Kingdom)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding United Kingdom-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant United Kingdom registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Solicitors, barristers, and other authorised legal professionals with current practising status',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'United Kingdom-wide client discovery',
  },
  ireland: {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (Ireland)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding Ireland-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant Irish registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Solicitors and barristers with current practising status in Ireland',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'Ireland-wide client discovery',
  },
  singapore: {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (Singapore)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding Singapore-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant Singapore registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Legal professionals authorised to practise within Singapore regulatory requirements',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'Singapore-wide client discovery',
  },
  'hong-kong': {
    title: 'Legal, Governance, and Compliance Professionals - Join EUCA Match (Hong Kong)',
    intro: LEGAL_SHARED_INTRO,
    onboarding:
      'We are onboarding Hong Kong-based legal, governance, and compliance professionals who practise independently and provide services online, face-to-face, or via hybrid models.',
    whoCanApplyLead:
      'You should hold relevant Hong Kong registration, practising rights, or recognised credentials for the services you offer, including:',
    credentialItems: [
      'Solicitors, barristers, and other authorised legal professionals with current practising status in Hong Kong',
      'Governance, risk, and compliance professionals with relevant qualifications and experience',
      'Policy, regulatory, and corporate advisory professionals working within authorised scope',
    ],
    discoveryLine: 'Hong Kong-wide client discovery',
  },
}

const LEGAL_OFFER_ITEMS = [
  'Public professional profile',
  'Direct client contact and shareable booking link',
  'Availability calendar with Google Calendar and Calendly sync',
  'Optional online payments with automatic invoices',
  'Secure client notes with AI assistance for each booking',
  'Public client reviews on EUCA Match',
  'Aggregation of your publicly available reviews (where available)',
  'No platform involvement in how you run your practice',
]

const LEGAL_WORK_AREAS = [
  'Contracts, terms, and commercial documentation',
  'Business and corporate advisory',
  'Employment and workplace matters',
  'Regulatory, governance, and compliance frameworks',
  'Risk management and policy design',
  'Dispute strategy and pre-litigation advisory',
  'Privacy, data, and cyber compliance',
  'Cross-border structuring and documentation support',
]

const JOIN_US_COMPLIANCE_NOTICE =
  'EUCA Match provides technology, matching, and booking tools only. Professionals remain independently responsible for the services they provide and for meeting all legal, licensing, insurance, privacy, advertising, and professional conduct requirements in every jurisdiction where they operate.'

const JOIN_US_CROSS_BORDER_NOTICE =
  'Only accept engagements that you are legally authorised to perform in the relevant client and service jurisdictions.'

export default function App() {
  const { language, setLanguage } = useUiLanguage()
  const [menuOpen, setMenuOpen] = useState(false)
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false)
  const [aboutSectionOpen, setAboutSectionOpen] = useState<string | null>(null)
  const [aboutMobileOpen, setAboutMobileOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [howOpen, setHowOpen] = useState(false)
  const [productOpen, setProductOpen] = useState(false)
  const [productMobileOpen, setProductMobileOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [resourcesMobileOpen, setResourcesMobileOpen] = useState(false)
  const [companyOpen, setCompanyOpen] = useState(false)
  const [companyMobileOpen, setCompanyMobileOpen] = useState(false)
  const [globalSearchQuery, setGlobalSearchQuery] = useState('')
  const [globalSearchProfession, setGlobalSearchProfession] = useState('')
  const [globalSuggestions, setGlobalSuggestions] = useState<string[]>([])
  const [globalSuggestionsLoading, setGlobalSuggestionsLoading] = useState(false)
  const [globalSuggestionsError, setGlobalSuggestionsError] = useState<string | null>(null)
  const [globalSuggestionsOpen, setGlobalSuggestionsOpen] = useState(false)
  const [globalInputFocused, setGlobalInputFocused] = useState(false)
  const globalRotatingPlaceholder = useRotatingPlaceholder(!globalSearchQuery, SEARCH_HINTS)
  const navigate = useNavigate()
  const [showcaseGroups, setShowcaseGroups] = useState<ShowcaseGroup[]>([])
  const [showcaseLoading, setShowcaseLoading] = useState(false)
  const [showcaseError, setShowcaseError] = useState<string | null>(null)
  const [reviewsPanel, setReviewsPanel] = useState<{
    provider: ShowcaseItem | null
    reviews: any[]
    summary: { ratingAverage?: number; ratingCount?: number } | null
    loading: boolean
    loadingMore: boolean
    nextCursor: string | null
    error: string | null
  }>({ provider: null, reviews: [], summary: null, loading: false, loadingMore: false, nextCursor: null, error: null })
  const [reviewsVisibleCount, setReviewsVisibleCount] = useState(REVIEWS_PAGE_SIZE)
  const reviewsScrollRef = useRef<HTMLDivElement | null>(null)
  const reviewsSentinelRef = useRef<HTMLDivElement | null>(null)
  const aboutHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const servicesHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const howHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const productHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resourcesHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const companyHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const globalSuggestionsHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const location = useLocation()
  const isNewWebDesign = location.pathname === '/' || location.pathname === '/newwebdesign'
  const hideGlobalSearch = location.pathname.startsWith('/ai-search') || isNewWebDesign
  const primaryNavLinks = navLinks.filter((link) => link.label !== 'Sign In')
  const providerFirstName = reviewsPanel.provider?.name?.trim().split(/\s+/)[0] || 'Our'

  useEffect(() => {
    if (typeof window === 'undefined') return
    const nav = navigator as any
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection
    const applyLiteMode = () => {
      const next = shouldUseLiteMobileMode()
      document.documentElement.classList.toggle('perf-lite', next)
      document.body.classList.toggle('perf-lite', next)
    }
    applyLiteMode()
    window.addEventListener('resize', applyLiteMode)
    if (connection?.addEventListener) connection.addEventListener('change', applyLiteMode)
    return () => {
      window.removeEventListener('resize', applyLiteMode)
      if (connection?.removeEventListener) connection.removeEventListener('change', applyLiteMode)
      document.documentElement.classList.remove('perf-lite')
      document.body.classList.remove('perf-lite')
    }
  }, [])

  useEffect(() => {
    const robotsValue =
      location.pathname === '/newwebdesign'
        ? 'noindex,nofollow,noarchive,nosnippet'
        : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'
    const ensureMeta = (name: 'robots' | 'googlebot') => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('name', name)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', robotsValue)
    }
    ensureMeta('robots')
    ensureMeta('googlebot')
  }, [location.pathname])

  // Lock body scroll when reviews drawer is open
  useEffect(() => {
    const open = Boolean(reviewsPanel.provider)
    if (open) {
      document.body.classList.add('reviews-no-scroll')
    } else {
      document.body.classList.remove('reviews-no-scroll')
    }
    return () => document.body.classList.remove('reviews-no-scroll')
  }, [reviewsPanel.provider])

  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return
    if (typeof window === 'undefined') return
    const existing = document.querySelector(`script[data-ga-id=\"${GA_MEASUREMENT_ID}\"]`)
    if (!existing) {
      const s = document.createElement('script')
      s.async = true
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
      s.setAttribute('data-ga-id', GA_MEASUREMENT_ID)
      document.head.appendChild(s)
    }
    const w = window as any
    w.dataLayer = w.dataLayer || []
    w.gtag =
      w.gtag ||
      function gtag() {
        w.dataLayer.push(arguments)
      }
    w.gtag('js', new Date())
    w.gtag('config', GA_MEASUREMENT_ID)
  }, [])

  useEffect(() => {
    const w = window as any
    if (!GA_MEASUREMENT_ID || !w.gtag) return
    w.gtag('config', GA_MEASUREMENT_ID, {
      page_path: `${location.pathname}${location.search}${location.hash}`,
    })
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  useEffect(() => {
    if (location.hash) {
      // Only attempt to scroll when the hash looks like a simple ID selector.
      const safeHash = /^#[A-Za-z][\w-]*$/.test(location.hash) ? location.hash : null
      if (safeHash) {
        const tryScroll = () => {
          const target = document.querySelector(safeHash)
          if (target) {
            target.scrollIntoView({ behavior: preferredScrollBehavior() })
            return true
          }
          return false
        }
        if (!tryScroll()) {
          // Retry shortly in case the section hasn't rendered yet.
          setTimeout(() => tryScroll(), 80)
        }
      }
    } else {
      window.scrollTo({ top: 0 })
    }
    setAboutMenuOpen(false)
    setAboutSectionOpen(null)
    setAboutMobileOpen(false)
    setServicesOpen(false)
    setHowOpen(false)
    setProductOpen(false)
    setResourcesOpen(false)
    setCompanyOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!location.hash) return
    const safeHash = /^#[A-Za-z][\w-]*$/.test(location.hash) ? location.hash : null
    if (!safeHash) return
    const target = document.querySelector(safeHash)
    if (target) {
      target.scrollIntoView({ behavior: preferredScrollBehavior() })
    }
  }, [location.hash, showcaseGroups])

  const resolveStackKey = (pro: ShowcaseItem | null) => {
    const resolved = resolveShowcaseStack(pro)
    return resolved && STACK_API_BASES[resolved] ? resolved : null
  }

  const resolveStackCandidates = (pro: ShowcaseItem | null): string[] => {
    if (!pro) return []
    const out: string[] = []
    const seen = new Set<string>()
    const add = (stackKey?: string | null) => {
      const key = String(stackKey || '').trim().toLowerCase()
      if (!key || seen.has(key) || !STACK_API_BASES[key]) return
      seen.add(key)
      out.push(key)
    }
    add(resolveStackKey(pro))
    add(resolveShowcaseStack(pro))
    Object.keys(STACK_TITLE_MAP).forEach((stackKey) => add(stackKey))
    return out
  }

  const normalizeProviderReply = (reply: any) => {
    if (!reply) return null
    if (typeof reply === 'string') {
      const comment = String(reply).trim()
      if (!comment) return null
      return {
        comment,
        createdAt: null,
        updatedAt: null,
        replierName: null,
      }
    }
    if (typeof reply !== 'object') return null
    const comment = String(reply.comment || reply.replyComment || reply.message || '').trim()
    if (!comment) return null
    const createdAt = String(reply.createdAt || reply.updatedAt || '').trim() || null
    const updatedAt = String(reply.updatedAt || reply.createdAt || '').trim() || null
    const replierName = String(reply.replierName || reply.replier || '').trim() || null
    return {
      comment,
      createdAt,
      updatedAt,
      replierName,
    }
  }

  const normalizeReviewItem = (review: any) => {
    if (!review || typeof review !== 'object') return review
    return {
      ...review,
      providerReply: normalizeProviderReply(review.providerReply),
    }
  }

  const fetchAllReviews = useCallback(
    async (pro: ShowcaseItem) => {
      const preferredStack = resolveStackKey(pro)
      const stackCandidates = resolveStackCandidates(pro)
      if (!stackCandidates.length || !pro.userId) {
        setReviewsPanel((prev) => ({ ...prev, loading: false, loadingMore: false, error: 'Reviews unavailable for this provider.' }))
        return
      }
      try {
        let best: { reviews: any[]; summary: any; score: number } | null = null
        let lastError: any = null

        for (const stackKey of stackCandidates) {
          const apiBase = STACK_API_BASES[stackKey]
          if (!apiBase) continue
          try {
            let cursor: string | null = null
            let summary: any = null
            let all: any[] = []
            let pages = 0
            do {
              const qs = new URLSearchParams({ ts: Date.now().toString(), limit: String(REVIEWS_FETCH_LIMIT) })
              if (cursor) qs.set('cursor', cursor)
              const r = await fetch(`${apiBase}/reviews/providers/${pro.userId}?${qs.toString()}`, { cache: 'no-store' })
              const j = await r.json().catch(() => ({}))
              if (!r.ok) throw new Error(j?.error || 'Failed to load reviews')
              const incoming = Array.isArray(j.reviews) ? j.reviews.map(normalizeReviewItem) : []
              all = all.concat(incoming)
              summary = j.summary || summary
              cursor = j.nextCursor || null
              pages += 1
            } while (cursor && all.length < MAX_REVIEWS && pages < 20)

            const sorted = sortReviewsByDate(all)
            const hasReplies = sorted.some((rev) => Boolean(rev?.providerReply?.comment))
            const ratingCount = Number(summary?.ratingCount || 0)
            const score =
              (hasReplies ? 1000000 : 0) +
              (stackKey === preferredStack ? 10000 : 0) +
              ratingCount * 10 +
              sorted.length
            if (!best || score > best.score) {
              best = { reviews: sorted, summary, score }
            }
            if (hasReplies && sorted.length) break
          } catch (err: any) {
            lastError = err
          }
        }

        if (!best) throw lastError || new Error('Failed to load reviews')

        setReviewsPanel((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          reviews: best.reviews,
          summary: best.summary,
          nextCursor: null,
          error: null,
        }))
        setReviewsVisibleCount(Math.min(REVIEWS_PAGE_SIZE, best.reviews.length))
      } catch (e: any) {
        setReviewsPanel((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          error: e?.message || 'Failed to load reviews',
        }))
      }
    },
    []
  )

  const openReviewsPanel = async (pro: ShowcaseItem) => {
    if (!pro?.userId) return
    setReviewsPanel({ provider: pro, reviews: [], summary: null, loading: true, loadingMore: false, nextCursor: null, error: null })
    setReviewsVisibleCount(0)
    await fetchAllReviews(pro)
  }

  const closeReviewsPanel = () => {
    setReviewsPanel({ provider: null, reviews: [], summary: null, loading: false, loadingMore: false, nextCursor: null, error: null })
  }

  const loadMoreReviews = async () => {
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    setReviewsVisibleCount((count) => Math.min(count + REVIEWS_PAGE_SIZE, reviewsPanel.reviews.length))
  }

  useEffect(() => {
    if (!reviewsPanel.provider) return
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    const root = reviewsScrollRef.current
    const target = reviewsSentinelRef.current
    if (!root || !target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreReviews()
      },
      { root, rootMargin: '0px 0px 220px 0px', threshold: 0 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [reviewsPanel.provider, reviewsPanel.reviews.length, reviewsVisibleCount, loadMoreReviews])

  const parseReviewDate = (value?: string | null) => {
    if (!value) return null
    const trimmed = String(value).trim()
    if (!trimmed || trimmed === 'REQUEST_BOOKING') return null
    if (/^\d+$/.test(trimmed)) {
      const raw = Number(trimmed)
      const ms = trimmed.length <= 10 ? raw * 1000 : raw
      const parsed = new Date(ms)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    const parsed = new Date(trimmed)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const sortReviewsByDate = (reviews: any[]) =>
    [...reviews].sort((a, b) => {
      const aDate = parseReviewDate(a?.slotISO) || parseReviewDate(a?.createdAt)
      const bDate = parseReviewDate(b?.slotISO) || parseReviewDate(b?.createdAt)
      const aTime = aDate ? aDate.getTime() : 0
      const bTime = bDate ? bDate.getTime() : 0
      return bTime - aTime
    })

  const formatReviewDate = (slotISO?: string | null, createdAt?: string | null) => {
    const parsed = parseReviewDate(slotISO) || parseReviewDate(createdAt)
    if (!parsed) return ''
    return parsed.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const loadShowcases = useCallback(
    async (stacks?: string[]) => {
      if (
        !DOCTOR_PUBLIC_API &&
        !ALLIED_PUBLIC_API &&
        !EDUCATION_PUBLIC_API &&
        !MIGRATION_PUBLIC_API &&
        !FINANCIAL_PUBLIC_API &&
        !PROPERTY_PUBLIC_API &&
        !LEGAL_PUBLIC_API &&
        !TECH_PUBLIC_API
      )
        return

      setShowcaseLoading(true)
      setShowcaseError(null)
      const include = new Set((stacks || []).map((s) => s.toLowerCase()))
      const tasks: Array<Promise<ShowcaseGroup>> = []
      const add = (key: string, fallbackTitle: string, fn: () => Promise<ShowcaseFetchResult>) => {
        if (include.size === 0 || include.has(key)) {
          const resolvedTitle = STACK_TITLE_MAP[key] || fallbackTitle
          tasks.push(
            fn().then(({ items, qualifiedBookingCount }) => ({
              title: resolvedTitle,
              items,
              stack: key,
              qualifiedBookingCount,
            }))
          )
        }
      }
      add('doctor', 'Medical Doctors', fetchDoctorShowcaseData)
      add('allied', 'Allied Health', fetchAlliedShowcaseData)
      add('financial', 'Financial', fetchFinancialShowcaseData)
      add('education', 'Education & Tutoring', fetchEducationShowcaseData)
      add('migration', 'Migration & Visa', fetchMigrationShowcaseData)
      add('property', 'Property, Design & Building', fetchPropertyShowcaseData)
      add('legal', 'Legal, Governance & Compliance', fetchLegalShowcaseData)
      add('tech', 'Tech, Creative & Media', fetchTechShowcaseData)

      const errors: string[] = []
      try {
        const results = await Promise.allSettled(tasks)
        const groups: ShowcaseGroup[] = []
        for (const r of results) {
          if (r.status === 'fulfilled') {
            if (r.value.items.length) groups.push(r.value)
          } else {
            errors.push(r.reason?.message || 'Failed to load showcase')
          }
        }
        const reviewedProviders = (items: ShowcaseItem[]) =>
          items.reduce((count, item) => count + (Number(item.ratingCount) > 0 ? 1 : 0), 0)
        const totalReviews = (items: ShowcaseItem[]) =>
          items.reduce((sum, item) => sum + Math.max(0, Number(item.ratingCount) || 0), 0)
        groups.sort((a, b) => {
          const bookingDiff = (Number(b.qualifiedBookingCount) || 0) - (Number(a.qualifiedBookingCount) || 0)
          if (bookingDiff !== 0) return bookingDiff
          const reviewedDiff = reviewedProviders(b.items) - reviewedProviders(a.items)
          if (reviewedDiff !== 0) return reviewedDiff
          return totalReviews(b.items) - totalReviews(a.items)
        })
        const orderedGroups = groups.map((group) => ({
          ...group,
          items: orderShowcase(group.items),
        }))
        setShowcaseGroups(orderedGroups)
        if (errors.length === results.length) {
          setShowcaseError(errors.join(' · '))
        } else if (errors.length) {
          setShowcaseError('Some showcases failed to load. Showing available professionals.')
        }
      } catch (e: any) {
        setShowcaseError(e?.message || 'Failed to load showcase')
      } finally {
        setShowcaseLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    // Skip fetching showcase feeds on the dedicated search pages; search now uses the SearchIndex API.
    if (location.pathname.startsWith('/search') || location.pathname.startsWith('/ai-search')) return
    let cancelled = false
    loadShowcases().catch((e: any) => {
      if (!cancelled) setShowcaseError(e?.message || 'Failed to load showcase')
    })
    return () => {
      cancelled = true
    }
  }, [loadShowcases, location.pathname])

  useEffect(() => {
    return () => {
      if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
      if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
      if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
      if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current)
      if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
      if (globalSuggestionsHideTimer.current) clearTimeout(globalSuggestionsHideTimer.current)
    }
  }, [])

  useEffect(() => {
    if (!SEARCH_PUBLIC_API) {
      setGlobalSuggestions([])
      return
    }
    if (!globalSearchQuery.trim() || globalSearchQuery.trim().length < 1) {
      setGlobalSuggestions([])
      setGlobalSuggestionsOpen(false)
      return
    }
    let cancelled = false
    const timeout = setTimeout(async () => {
      setGlobalSuggestionsLoading(true)
      setGlobalSuggestionsError(null)
      try {
        const sugg = await fetchSuggestApi(globalSearchQuery.trim())
        if (!cancelled) {
          setGlobalSuggestions(sugg)
          setGlobalSuggestionsOpen(globalInputFocused && sugg.length > 0)
        }
      } catch (err: any) {
        if (!cancelled) {
          setGlobalSuggestionsError(err?.message || 'Unable to load suggestions')
          setGlobalSuggestions([])
        }
      } finally {
        if (!cancelled) setGlobalSuggestionsLoading(false)
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [globalSearchQuery, globalInputFocused])

  useEffect(() => {
    const removeWidget = () => {
      const root = document.querySelector('.grw')
      if (root) root.remove()
      const style = document.getElementById('grw-style')
      if (style) style.remove()
      const script = document.getElementById('grw-script')
      if (script) script.remove()
      const w = window as any
      if (w.GRWO) delete w.GRWO
      if (w.GRWO_INLINE) delete w.GRWO_INLINE
    }
    const isHome = location.pathname === '/'
    if (!isHome) {
      removeWidget()
      return
    }
    if (document.getElementById('grw-script')) return
    const s = document.createElement('script')
    s.id = 'grw-script'
    s.src = '/grw-widget.js'
    s.async = true
    document.body.appendChild(s)
    return () => {
      removeWidget()
    }
  }, [location.pathname])

  // Rehydrate inline Google reviews button whenever navigation changes
  useEffect(() => {
    const hydrate = () => {
      const w = window as any
      if (w.GRWO_INLINE) w.GRWO_INLINE()
    }
    // Try immediately
    hydrate()
    // Also when script finishes loading
    const script = document.getElementById('grw-script')
    if (script) {
      script.addEventListener('load', hydrate)
      return () => script.removeEventListener('load', hydrate)
    }
  }, [location.pathname, location.hash])

  const closeMenu = () => {
    setMenuOpen(false)
    setAboutMenuOpen(false)
    setAboutSectionOpen(null)
    setAboutMobileOpen(false)
    setProductMobileOpen(false)
    setProductOpen(false)
    setResourcesMobileOpen(false)
    setResourcesOpen(false)
    setServicesOpen(false)
    setHowOpen(false)
    setCompanyOpen(false)
    setCompanyMobileOpen(false)
  }
  const closeAllMenus = () => {
    if (aboutHoverTimeout.current) clearTimeout(aboutHoverTimeout.current)
    setMenuOpen(false)
    setAboutMenuOpen(false)
    setAboutSectionOpen(null)
    setAboutMobileOpen(false)
    setServicesOpen(false)
    setHowOpen(false)
    setProductOpen(false)
    setProductMobileOpen(false)
    setResourcesOpen(false)
    setResourcesMobileOpen(false)
    setCompanyOpen(false)
    setCompanyMobileOpen(false)
  }

  const openAboutMenu = () => {
    if (aboutHoverTimeout.current) clearTimeout(aboutHoverTimeout.current)
    setAboutMenuOpen(true)
  }

  const closeAboutMenu = () => {
    if (aboutHoverTimeout.current) clearTimeout(aboutHoverTimeout.current)
    aboutHoverTimeout.current = setTimeout(() => {
      setAboutMenuOpen(false)
      setAboutSectionOpen(null)
    }, 180)
  }

  const openServices = () => {
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    setHowOpen(false)
    setResourcesOpen(false)
    setCompanyOpen(false)
    setServicesOpen(true)
  }

  const closeServices = () => {
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    servicesHoverTimeout.current = setTimeout(() => setServicesOpen(false), 60)
  }

  const toggleServices = () => {
    if (servicesOpen) {
      if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
      setServicesOpen(false)
    } else {
      openServices()
    }
  }

  const openHow = () => {
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    setServicesOpen(false)
    setResourcesOpen(false)
    setCompanyOpen(false)
    setHowOpen(true)
  }

  const closeHow = () => {
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    howHoverTimeout.current = setTimeout(() => setHowOpen(false), 60)
  }

  const openProduct = () => {
    if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current)
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    setServicesOpen(false)
    setHowOpen(false)
    setResourcesOpen(false)
    setCompanyOpen(false)
    setProductOpen(true)
  }

  const closeProduct = () => {
    if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current)
    productHoverTimeout.current = setTimeout(() => setProductOpen(false), 60)
  }

  const openResources = () => {
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current)
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    setServicesOpen(false)
    setHowOpen(false)
    setProductOpen(false)
    setCompanyOpen(false)
    setResourcesOpen(true)
  }

  const closeResources = () => {
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    resourcesHoverTimeout.current = setTimeout(() => setResourcesOpen(false), 60)
  }

  const openCompany = () => {
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    if (servicesHoverTimeout.current) clearTimeout(servicesHoverTimeout.current)
    if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
    if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current)
    if (resourcesHoverTimeout.current) clearTimeout(resourcesHoverTimeout.current)
    setServicesOpen(false)
    setHowOpen(false)
    setProductOpen(false)
    setResourcesOpen(false)
    setCompanyOpen(true)
  }

  const closeCompany = () => {
    if (companyHoverTimeout.current) clearTimeout(companyHoverTimeout.current)
    companyHoverTimeout.current = setTimeout(() => setCompanyOpen(false), 60)
  }

  const closeGlobalSuggestionsSoon = () => {
    if (globalSuggestionsHideTimer.current) clearTimeout(globalSuggestionsHideTimer.current)
    globalSuggestionsHideTimer.current = setTimeout(() => setGlobalSuggestionsOpen(false), 150)
  }

  const onGlobalSuggestionClick = (val: string) => {
    setGlobalSearchQuery(val)
    const params = new URLSearchParams()
    params.set('q', val)
    if (globalSearchProfession) params.set('profession', globalSearchProfession)
    navigate(`/search?${params.toString()}`, { replace: true })
    setGlobalSuggestionsOpen(false)
  }

  const toggleHow = () => {
    if (howOpen) {
      if (howHoverTimeout.current) clearTimeout(howHoverTimeout.current)
      setHowOpen(false)
    } else {
      openHow()
    }
  }

  const renderFooterLink = (link: (typeof primaryNavLinks)[number]) => {
    const renderSimpleLink = () => {
      if (link.href.startsWith('http')) {
        return (
          <a key={`footer-${link.label}`} href={link.href} target="_blank" rel="noreferrer">
            {link.label}
          </a>
        )
      }
      if (link.href.includes('#')) {
        return (
          <a key={`footer-${link.label}`} href={link.href}>
            {link.label}
          </a>
        )
      }
      return (
        <Link key={`footer-${link.label}`} to={link.href}>
          {link.label}
        </Link>
      )
    }

    if (link.label === 'Company') {
      return (
        <div className="footer-nav-item" key={`footer-${link.label}`}>
          <div className="footer-heading">{link.label}</div>
          <div className="footer-submenu">
            {companyNavItems.map((item) => (
              <Link key={`footer-company-${item.label}`} to={item.href}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'Resources') {
      return (
        <div className="footer-nav-item" key={`footer-${link.label}`}>
          <div className="footer-heading">{link.label}</div>
          <div className="footer-submenu">
            {resourcesNavItems.map((item) => (
              <Link key={`footer-resources-${item.label}`} to={item.href}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'Product') {
      return (
        <div className="footer-nav-item" key={`footer-${link.label}`}>
          <div className="footer-heading">{link.label}</div>
          <div className="footer-submenu">
            {productNavItems.map((item) => (
              <Link key={`footer-product-${item.label}`} to={item.href}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }


    return (
      <div className="footer-nav-item" key={`footer-${link.label}`}>
        {renderSimpleLink()}
      </div>
    )
  }

  const renderNavLink = (
    link: (typeof primaryNavLinks)[number],
    key: string,
    onClick?: () => void,
    variant: 'desktop' | 'mobile' = 'desktop'
  ) => {
    if (link.label === 'Resources') {
      if (variant === 'mobile') {
        return (
          <div className={`nav-item mobile-services mobile-accordion ${resourcesMobileOpen ? 'open' : ''}`} key={key}>
            <button
              type="button"
              className="mobile-accordion-toggle"
              onClick={() => setResourcesMobileOpen((prev) => !prev)}
              aria-expanded={resourcesMobileOpen}
            >
              <span>Resources</span>
              <span className="chevron" aria-hidden="true" />
            </button>
            {resourcesMobileOpen && (
              <div className="mobile-services-list">
                {resourcesNavItems.map((item) => (
                  <Link
                    key={`mobile-resources-${item.label}`}
                    to={item.href}
                    onClick={() => {
                      onClick?.()
                      closeAllMenus()
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )
      }
      return (
        <div
          className={`nav-item services-dropdown ${resourcesOpen ? 'open' : ''}`}
          key={key}
          onMouseEnter={openResources}
          onMouseLeave={closeResources}
        >
          <button
            className="nav-trigger"
            type="button"
            aria-haspopup="true"
            aria-expanded={resourcesOpen}
            onClick={() => setResourcesOpen((prev) => !prev)}
          >
            Resources
            <span className="chevron" aria-hidden="true" />
          </button>
          <div className="dropdown-panel">
            {resourcesNavItems.map((item) => (
              <Link
                key={`dropdown-resources-${item.label}`}
                to={item.href}
                onClick={() => {
                  closeAllMenus()
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'Company') {
      if (variant === 'mobile') {
        return (
          <div className={`nav-item mobile-services mobile-accordion ${companyMobileOpen ? 'open' : ''}`} key={key}>
            <button
              type="button"
              className="mobile-accordion-toggle"
              onClick={() => setCompanyMobileOpen((prev) => !prev)}
              aria-expanded={companyMobileOpen}
            >
              <span>Company</span>
              <span className="chevron" aria-hidden="true" />
            </button>
            {companyMobileOpen && (
              <div className="mobile-services-list">
                {companyNavItems.map((item) => (
                  <Link
                    key={`mobile-company-${item.label}`}
                    to={item.href}
                    onClick={() => {
                      onClick?.()
                      closeAllMenus()
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )
      }
      return (
        <div
          className={`nav-item services-dropdown ${companyOpen ? 'open' : ''}`}
          key={key}
          onMouseEnter={openCompany}
          onMouseLeave={closeCompany}
        >
          <button
            className="nav-trigger"
            type="button"
            aria-haspopup="true"
            aria-expanded={companyOpen}
            onClick={() => setCompanyOpen((prev) => !prev)}
          >
            Company
            <span className="chevron" aria-hidden="true" />
          </button>
          <div className="dropdown-panel">
            {companyNavItems.map((item) => (
              <Link
                key={`dropdown-company-${item.label}`}
                to={item.href}
                onClick={() => {
                  closeAllMenus()
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'Product') {
      if (variant === 'mobile') {
        return (
          <div className={`nav-item mobile-services mobile-accordion ${productMobileOpen ? 'open' : ''}`} key={key}>
            <button
              type="button"
              className="mobile-accordion-toggle"
              onClick={() => setProductMobileOpen((prev) => !prev)}
              aria-expanded={productMobileOpen}
            >
              <span>Product</span>
              <span className="chevron" aria-hidden="true" />
            </button>
            {productMobileOpen && (
              <div className="mobile-services-list">
                {productNavItems.map((item) => (
                  <Link
                    key={`mobile-product-${item.label}`}
                    to={item.href}
                    onClick={() => {
                      onClick?.()
                      closeAllMenus()
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )
      }
      return (
        <div
          className={`nav-item services-dropdown ${productOpen ? 'open' : ''}`}
          key={key}
          onMouseEnter={openProduct}
          onMouseLeave={closeProduct}
        >
          <button
            className="nav-trigger"
            type="button"
            aria-haspopup="true"
            aria-expanded={productOpen}
            onClick={() => setProductOpen((prev) => !prev)}
          >
            Product
            <span className="chevron" aria-hidden="true" />
          </button>
          <div className="dropdown-panel">
            {productNavItems.map((item) => (
              <Link
                key={`dropdown-product-${item.label}`}
                to={item.href}
                onClick={() => {
                  closeAllMenus()
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'Industries') {
      if (variant === 'mobile') {
        return (
          <div className="nav-item mobile-services" key={key}>
            <a href={link.href} onClick={onClick}>
              {link.label}
            </a>
            <div className="mobile-services-list">
              {serviceAnchors.map((item) => (
                <a key={`mobile-services-${item.label}`} href={`${SITE_URL}${item.href}`} onClick={onClick}>
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )
      }
        return (
          <div
            className={`nav-item services-dropdown ${servicesOpen ? 'open' : ''}`}
            key={key}
            onMouseEnter={openServices}
            onMouseLeave={closeServices}
          >
            <a
              className="nav-trigger"
              href={link.href}
              aria-haspopup="true"
              aria-expanded={servicesOpen}
              onFocus={openServices}
            >
              {link.label}
              <span className="chevron" aria-hidden="true" />
            </a>
          <div className="dropdown-panel">
            {serviceAnchors.map((item) => (
              <a
                key={`dropdown-${item.label}`}
                href={`${SITE_URL}${item.href}`}
                onClick={() => {
                  setServicesOpen(false)
                  onClick?.()
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )
    }
    if (link.label === 'How It Works') {
      if (variant === 'mobile') {
        return (
          <div className="nav-item mobile-services" key={key}>
            <a href={link.href} onClick={onClick}>
              {link.label}
            </a>
            <div className="mobile-services-list">
              {howItWorksItems.map((item) => (
                <a key={`mobile-how-${item.label}`} href={`${SITE_URL}${item.href}`} onClick={onClick}>
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )
      }
        return (
          <div
            className={`nav-item services-dropdown ${howOpen ? 'open' : ''}`}
            key={key}
            onMouseEnter={openHow}
            onMouseLeave={closeHow}
          >
            <a
              className="nav-trigger"
              href={link.href}
              aria-haspopup="true"
              aria-expanded={howOpen}
              onFocus={openHow}
            >
              {link.label}
              <span className="chevron" aria-hidden="true" />
            </a>
            <div className="dropdown-panel">
              {howItWorksItems.map((item) => (
                <a
                  key={`dropdown-how-${item.label}`}
                  href={`${SITE_URL}${item.href}`}
                  onClick={() => {
                    setHowOpen(false)
                    onClick?.()
                  }}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
      )
    }
    if (link.href.startsWith('http')) {
      return (
        <a key={key} href={link.href} onClick={onClick}>
          {link.label}
        </a>
      )
    }

    return (
      <Link key={key} to={link.href} onClick={onClick}>
        {link.label}
      </Link>
    )
  }

  const newWebDesignElement = (
    <HomePage
      clientSignupUrl={clientSignupUrl}
      providerSignupUrl={providerSignupUrl}
      showSearchBar={false}
      variant="newwebdesign"
      newWebDesignHeader={
        <header className="site-header site-header--newwebdesign">
          <Link className="brand" to="/" aria-label="EUCA Match homepage" onClick={closeMenu}>
            <span className="brand-logo-wrap">
              <img className="brand-logo" src={logoImage} alt="EUCA Match logo" />
            </span>
            <div className="brand-copy">
              <span className="brand-title">EUCA Match</span>
            </div>
          </Link>
          <div className="newwebdesign-menu-row">
            <nav className="nav-links nav-links--newwebdesign" aria-label="Primary">
              <div
                className={`nav-item services-dropdown about-dropdown ${aboutMenuOpen ? 'open' : ''}`}
                onMouseEnter={openAboutMenu}
                onMouseLeave={closeAboutMenu}
              >
                <button
                  className="nav-trigger newwebdesign-pill"
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={aboutMenuOpen}
                  onFocus={openAboutMenu}
                  onClick={() =>
                    setAboutMenuOpen((prev) => {
                      const next = !prev
                      if (next) setAboutSectionOpen(null)
                      return next
                    })
                  }
                >
                  About Us
                  <span className="chevron" aria-hidden="true" />
                </button>
                <div className="dropdown-panel dropdown-panel--newwebdesign">
                  {aboutUsMenuSections.map((section) => (
                    <div
                      key={section.title}
                      className={`newwebdesign-dropdown-section ${aboutSectionOpen === section.title ? 'open' : ''}`}
                    >
                      {section.title === 'Pricing' ? (
                        <Link className="newwebdesign-dropdown-trigger" to="/pricing" onClick={closeAllMenus}>
                          <span>{section.title}</span>
                          <span className="newwebdesign-dropdown-trigger-placeholder" aria-hidden="true" />
                        </Link>
                      ) : (
                        <>
                          <button
                            className="newwebdesign-dropdown-trigger"
                            type="button"
                            aria-expanded={aboutSectionOpen === section.title}
                            onClick={() =>
                              setAboutSectionOpen((prev) => (prev === section.title ? null : section.title))
                            }
                          >
                            <span>{section.title}</span>
                            <span className="chevron" aria-hidden="true" />
                          </button>
                          {aboutSectionOpen === section.title ? (
                            <div className="newwebdesign-dropdown-links">
                              {section.items.map((item) => (
                                <Link
                                  key={`${section.title}-${item.label}`}
                                  to={item.href}
                                  onClick={closeAllMenus}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </nav>
            <div className="header-actions header-actions--newwebdesign">
              <a className="newwebdesign-pill" href={loginUrl}>
                Log In
              </a>
              <label
                className="newwebdesign-pill newwebdesign-language newwebdesign-language-select"
                aria-label="Select language"
                data-no-i18n
              >
                <Globe2 size={18} aria-hidden="true" />
                <select
                  aria-label="Select language"
                  value={language}
                  onChange={(event) => setLanguage(event.target.value as UiLanguageCode)}
                >
                  {UI_LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </header>
      }
      heroTitle="Verified Professionals Worldwide"
      heroSubtitle={
        <>
          <p>
            Find verified professionals in Australia, New Zealand, China, the US, Canada, the UK, Ireland, and
            Singapore.
          </p>
        </>
      }
      featuredSection={
        <FeaturedShowcase
          groups={showcaseGroups}
          loading={showcaseLoading}
          error={showcaseError}
          onOpenReviews={openReviewsPanel}
        />
      }
    />
  )
  const marketingHomeElement = newWebDesignElement

  return (
    <div className={`page home-root${isNewWebDesign ? ' newwebdesign-page' : ''}`}>
      {!isNewWebDesign ? (
        <header className="site-header">
          <Link className="brand" to="/" aria-label="EUCA Match homepage" onClick={closeMenu}>
            <img className="brand-logo" src={logoImage} alt="EUCA Match logo" />
            <div className="brand-copy">
              <span className="brand-title">EUCA Match</span>
            </div>
          </Link>
          <button
            className={`${menuOpen ? 'hamburger is-open' : 'hamburger'}${isNewWebDesign ? ' hamburger--newwebdesign' : ''}`}
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <span />
          </button>
          <nav className="nav-links" aria-label="Primary">
            {primaryNavLinks.map((link) => renderNavLink(link, link.label, undefined, 'desktop'))}
          </nav>
          <div className="header-actions test2-header-actions">
            <a className="header-login-link" href={loginUrl}>
              Log in
            </a>
            <a className="btn cta cta-badged" href={marketingSearchUrl}>
              <span className="cta-badge">FREE</span>
              Find Verified Professionals
            </a>
            <a className="btn cta pro-cta" href={providerSignupUrl}>
              For Professionals
            </a>
          </div>
        </header>
      ) : null}

      {!hideGlobalSearch && (
        <section className="search-page container search-variant search-variant-3">
          <form
            className="search-hero"
            onSubmit={(e) => {
              e.preventDefault()
              const params = new URLSearchParams()
              if (globalSearchQuery.trim()) params.set('q', globalSearchQuery.trim())
              if (globalSearchProfession) params.set('profession', globalSearchProfession)
              navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`)
            }}
          >
            <div className="search-hero-row">
              <div className="search-hero-field">
                <SearchIcon aria-hidden className="search-hero-icon" />
                <input
                  type="search"
                  className="search-hero-input"
                  placeholder={`Search: ${globalRotatingPlaceholder || 'tutor, nurse, legal'}`}
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  onFocus={() => {
                    setGlobalInputFocused(true)
                    if (globalSuggestions.length) setGlobalSuggestionsOpen(true)
                  }}
                  onBlur={() => {
                    setGlobalInputFocused(false)
                    closeGlobalSuggestionsSoon()
                  }}
                />
              </div>
              <div className="search-hero-field select">
                <BriefcaseBusiness aria-hidden className="search-hero-icon" />
                <select
                  className="search-hero-select"
                  value={globalSearchProfession}
                  onChange={(e) => setGlobalSearchProfession(e.target.value)}
                >
                  <option value="">All professions</option>
                  {industries.map((ind) => (
                    <option key={ind.value} value={ind.value}>
                      {ind.label}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn cta" type="submit">
                Search
              </button>
              {SHOW_AI_SEARCH_BUTTON && (
                <button className="btn ai-search" type="button" onClick={() => navigate('/ai-search')}>
                  <Sparkles aria-hidden className="ai-search-icon" />
                  AI Search
                </button>
              )}
            </div>
          </form>
          {globalSuggestionsOpen && (
            <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
              <div className="search-suggestions-header">
                <p className="eyebrow">Search suggestions</p>
                {globalSuggestionsLoading && <p className="muted tiny">Loading…</p>}
                {globalSuggestionsError && !globalSuggestionsLoading && <div className="banner error">{globalSuggestionsError}</div>}
              </div>
              {!globalSuggestionsLoading && !globalSuggestionsError && globalSuggestions.length > 0 && (
                <div className="suggestion-grid">
                  {globalSuggestions.slice(0, 60).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="suggestion-chip"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onGlobalSuggestionClick(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <main>
        <Routes>
        <Route path="/" element={marketingHomeElement} />
        <Route path="/newwebdesign" element={newWebDesignElement} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/professionals" element={<ProfessionalsPage />} />
        <Route path="/verification" element={<VerificationPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/join-us" element={<JoinUsPage />} />
        <Route path="/join-us/:regionSlug" element={<JoinUsRegionPage />} />
        <Route path="/join-us/:regionSlug/:professionSlug" element={<JoinUsOpportunityPage />} />
        <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="/knowledge-base/:articleSlug" element={<KnowledgeBaseArticlePage />} />
        <Route path="/search" element={<SearchPage onOpenReviews={openReviewsPanel} />} />
        <Route path="/ai-search" element={<AiSearchPage onOpenReviews={openReviewsPanel} />} />
        <Route path="/contact" element={<ContactPage clientSignupUrl={clientSignupUrl} />} />
        <Route path="/help-centre" element={<DocsPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/privacypolicy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route
          path="/:providerSlug"
          element={<ProviderProfile clientSignupUrl={clientSignupUrl} onOpenReviews={openReviewsPanel} />}
        />
        <Route
          path="*"
          element={
            <HomePage
              clientSignupUrl={clientSignupUrl}
              providerSignupUrl={providerSignupUrl}
              showSearchBar
              heroTitle="Find and Book Verified Professionals"
              heroSubtitle="EUCA Match connects clients with credential-verified professionals across regulated industries. Submit a request, get matched with a verified professional, and book with confidence."
              featuredSection={
                <FeaturedShowcase
                  groups={showcaseGroups}
                  loading={showcaseLoading}
                  error={showcaseError}
                  onOpenReviews={openReviewsPanel}
                />
              }
            />
          }
        />
        </Routes>
      </main>

      {reviewsPanel.provider ? (
        <div className="reviews-overlay" role="dialog" aria-modal="true" onClick={closeReviewsPanel}>
          <div
            className={`reviews-drawer ${reviewsPanel.loading ? 'is-loading' : ''}`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Provider reviews"
          >
            <div className="reviews-head">
              <button className="reviews-close" type="button" onClick={closeReviewsPanel} aria-label="Close reviews panel">
                ✕
              </button>
            </div>
              <div className="reviews-scroll" ref={reviewsScrollRef}>
              <div className="reviews-title">{`What ${providerFirstName}'s customers say`}</div>
              <div className="reviews-summary">
                <div className="reviews-brand">
                  <img
                    src={reviewsPanel.provider?.image || logoImage}
                    alt={reviewsPanel.provider?.name || 'Reviews'}
                    className="reviews-provider-photo"
                  />
                  <div className="reviews-provider-name">{reviewsPanel.provider?.name || 'Reviews'}</div>
                </div>
                <div className="reviews-avg">
                  <div className="reviews-avg-score">
                    {Number(
                      reviewsPanel.summary?.ratingAverage ?? Number(reviewsPanel.provider.rating || 0)
                    ).toFixed(1)}
                  </div>
                  <div className="reviews-avg-stars" aria-hidden="true">
                    {renderStars(reviewsPanel.summary?.ratingAverage ?? reviewsPanel.provider.rating)}
                  </div>
                  <div className="reviews-avg-count">
                    {reviewCountLabel(reviewsPanel.summary?.ratingCount ?? reviewsPanel.provider.ratingCount ?? 0)}
                  </div>
                </div>
                <button
                  className="reviews-cta-primary"
                  type="button"
                  onClick={() => {
                    const url = reviewsPanel.provider ? buildProviderBookingUrl(reviewsPanel.provider, '', clientSignupUrl) : clientSignupUrl
                    window.location.href = url
                  }}
                >
                  <span className="cta-badge">FREE</span>
                  Send Booking Request
                </button>
              </div>
              <div className="reviews-body">
                {reviewsPanel.loading && <div className="muted">Loading reviews…</div>}
                {reviewsPanel.error && !reviewsPanel.loading && <div className="banner error">{reviewsPanel.error}</div>}
                {!reviewsPanel.loading && !reviewsPanel.error && !reviewsPanel.reviews.length ? (
                  <div className="muted">No reviews yet.</div>
                ) : null}
                {!reviewsPanel.loading &&
                  !reviewsPanel.error &&
                  reviewsPanel.reviews.slice(0, reviewsVisibleCount).map((rev) => (
                    <div key={rev.reviewId || rev.createdAt} className="review-item">
                      <div className="review-head">
                        <div className="reviewer-badge" aria-hidden="true">
                          {(rev.reviewerFirstName || 'Client').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="reviewer-meta">
                          <strong>{rev.reviewerFirstName || 'Client'}</strong>
                          <span className="hint">
                            {formatReviewDate(rev.slotISO, rev.createdAt)}
                            {rev.source ? (
                              <>
                                {' - '}
                                <em>{rev.source}</em>
                              </>
                            ) : null}
                          </span>
                        </div>
                        <div className="review-rating">
                          {renderStars(rev.rating)}
                        </div>
                      </div>
                      {rev.comment ? <p className="review-comment">{rev.comment}</p> : null}
                      {rev.providerReply?.comment ? (
                        <div className="review-provider-reply">
                          <div className="hint" style={{ marginBottom: 4 }}>
                            {rev.providerReply?.replierName || 'Professional'} replied
                          </div>
                          <p className="review-comment" style={{ marginTop: 0 }}>
                            {rev.providerReply.comment}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                {reviewsVisibleCount < reviewsPanel.reviews.length && !reviewsPanel.loading && !reviewsPanel.error ? (
                  <button
                    className="reviews-cta-secondary"
                    type="button"
                    onClick={loadMoreReviews}
                    disabled={reviewsVisibleCount >= reviewsPanel.reviews.length}
                  >
                    Load more reviews
                  </button>
                ) : null}
                <div ref={reviewsSentinelRef} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <section className="bottom-bar">
        <Link className="brand" aria-label="EUCA Match home" to="/">
          <img className="brand-logo" src={logoImage} alt="" aria-hidden="true" />
          <div className="brand-copy">
            <span className="brand-title">EUCA Match</span>
          </div>
        </Link>
        <nav className="nav-links footer-nav" aria-label="Footer navigation">
          {primaryNavLinks.map((link) => renderFooterLink(link))}
          <div className="footer-nav-item">
            <div className="footer-heading">Contact</div>
            <div className="footer-submenu">
              <div className="footer-contact-details">
                <a className="footer-contact-row footer-contact-link" href="mailto:contact@eucamd.com" aria-label="Email contact@eucamd.com">
                  <Mail size={16} aria-hidden="true" />
                  <span>contact@eucamd.com</span>
                </a>
                <div className="footer-contact-row">
                  <MapPin size={16} aria-hidden="true" />
                  <span>
                    Level 1/457-459 Elizabeth St.
                    <br />
                    Surry Hills, NSW 2010
                    <br />
                    Australia
                  </span>
                </div>
              </div>
              <div className="social-group">
                <div className="social-group-icons">
                  <a
                    href="https://www.linkedin.com/company/eucamatch/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="EUCA Match LinkedIn"
                  >
                    <img className="linkedin-icon small" src={linkedinBrandImage} alt="" aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.facebook.com/eucamatch"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="EUCA Match Facebook"
                    className="social-icon small"
                  >
                    <FacebookBrandIcon aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </section>

      <footer>
        <div className="footer-meta">
          <p>© 2026 EUCA Match. ABN 40 692 614 341. All rights reserved.</p>
          <p className="muted" style={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
            EUCA Match is operated by EUCA Match Pty Ltd under the EUCA MD® framework.
          </p>
          <p className="muted" style={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
            Services are provided independently by listed professionals.
          </p>
          <div className="footer-links">
            <Link to="/privacypolicy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>

      {menuOpen && !isNewWebDesign && (
        <div className="mobile-menu" role="dialog" aria-modal="true" aria-label="Main navigation">
          <div className="mobile-menu-content test2-header-actions">
            <a className="mobile-login-link" href={loginUrl} onClick={closeMenu}>
              Log in
            </a>
            <a className="btn cta cta-badged large" href={marketingSearchUrl} onClick={closeMenu}>
              <span className="cta-badge">FREE</span>
              Find Verified Professionals
            </a>
            <a className="btn cta pro-cta large" href={providerSignupUrl} onClick={closeMenu}>For Professionals</a>
            <nav>
              {primaryNavLinks.map((link) => renderNavLink(link, `mobile-${link.label}`, closeMenu, 'mobile'))}
            </nav>
          </div>
        </div>
      )}
    </div>
  )
}

function ProviderProfile({ clientSignupUrl, onOpenReviews }: { clientSignupUrl: string; onOpenReviews?: (pro: ShowcaseItem) => void }) {
  const { providerSlug: slugParam } = useParams<{ providerSlug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const normalizedSlug = cleanSlug(slugParam)
  const MAX_SHOWCASE_CARDS = 8
  const stateProvider = (location.state as any)?.provider as ShowcaseItem | undefined
  const [provider, setProvider] = useState<ShowcaseItem | null>(stateProvider || null)
  const [matches, setMatches] = useState<ShowcaseItem[]>(stateProvider ? [stateProvider] : [])
  const [selectedStack, setSelectedStack] = useState<string | null>(stateProvider?.stack || null)
  const [loading, setLoading] = useState(!stateProvider)
  const [error, setError] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(
    Array.isArray(stateProvider?.availabilitySlots) && stateProvider!.availabilitySlots.length
      ? stateProvider!.availabilitySlots[0]
      : null
  )
  const [availabilityExpanded, setAvailabilityExpanded] = useState(false)
  const [expandedSlots, setExpandedSlots] = useState<string[] | null>(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState<string | null>(null)
  const [profileAvailability, setProfileAvailability] = useState<string[] | null>(null)
  const reviewsAutoOpened = useRef(false)
  const isDirectBook = () => isDirectEucaMd(provider)
  const directUrl = 'https://eucamd.com'
  const inferredStack = provider?.stack || (provider?.profession ? PROFESSION_TO_STACK[provider.profession] : null)
  const anchor =
    inferredStack && STACK_TITLE_MAP[inferredStack]
      ? `/#${showcaseAnchorId(STACK_TITLE_MAP[inferredStack])}`
      : '/#featured'
  const bioParam = (new URLSearchParams(location.search).get('bio') || '').toLowerCase()
  const setBioParam = useCallback(
    (val?: string | null) => {
      const desired = (val || '').toLowerCase()
      const current = bioParam
      if (desired === current) return
      const sp = new URLSearchParams(location.search)
      if (desired) sp.set('bio', desired)
      else sp.delete('bio')
      navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : '' }, { replace: true })
    },
    [bioParam, location.pathname, location.search, navigate]
  )

  useEffect(() => {
    let cancelled = false
    const dedupeByStack = (items: ShowcaseItem[]) => {
      const seen = new Set<string>()
      return items.filter((item) => {
        const key = `${item.userId || item.slug || item.name}:${item.stack || item.profession || ''}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    const loadFallbackShowcases = async () => {
      const results = await Promise.allSettled([
        fetchDoctorShowcase(),
        fetchAlliedShowcase(),
        fetchEducationShowcase(),
        fetchMigrationShowcase(),
        fetchFinancialShowcase(),
        fetchLegalShowcase(),
        fetchTechShowcase(),
        fetchPropertyShowcase(),
      ])
      const combined: ShowcaseItem[] = []
      results.forEach((r) => {
        if (r.status === 'fulfilled' && Array.isArray(r.value)) {
          combined.push(...(r.value as ShowcaseItem[]))
        }
      })
      return combined
    }
    const chooseInitial = (list: ShowcaseItem[]) => {
      const fromBio =
        bioParam &&
        list.find(
          (m) =>
            (m.stack || '').toLowerCase() === bioParam ||
            (m.profession || '').toLowerCase().includes(bioParam)
        )
      if (fromBio) return fromBio
      if (stateProvider?.stack) {
        const matchStack = list.find((m) => m.stack === stateProvider.stack)
        if (matchStack) return matchStack
      }
      if (stateProvider) {
        const matchSlug = list.find((m) => m.slug === stateProvider.slug || m.userId === stateProvider.userId)
        if (matchSlug) return matchSlug
      }
      return list[0]
    }
    const load = async () => {
      if (!slugParam) {
        setLoading(false)
        return
      }
      setError(null)
      if (!provider) setLoading(true)
      try {
        let combined: ShowcaseItem[] = []
        // Prefer the search index to avoid calling every showcase endpoint.
        if (SEARCH_PUBLIC_API) {
          try {
            combined = await fetchSearchApi(slugParam, '')
          } catch (err: any) {
            console.warn('profile search failed', err?.message || err)
            combined = []
          }
        }
        let matched = combined.filter((m) => slugsEqual(m.slug, normalizedSlug) || slugsEqual(m.userId, normalizedSlug))
        if (!matched.length) {
          // Fallback to showcases so direct links still work if search index misses.
          try {
            const fallback = await loadFallbackShowcases()
            matched = fallback.filter((m) => slugsEqual(m.slug, normalizedSlug) || slugsEqual(m.userId, normalizedSlug))
          } catch (err: any) {
            console.warn('fallback showcase load failed', err?.message || err)
          }
        }
        const merged = dedupeByStack([...(stateProvider ? [stateProvider] : []), ...matched])
        if (!merged.length) {
          if (!cancelled) {
            setMatches([])
            setProvider(null)
            setError('Profile not found.')
          }
          return
        }
        const initial = chooseInitial(merged)
        if (!cancelled) {
          setMatches(merged)
          setProvider(initial)
          setSelectedStack(initial?.stack || initial?.profession || null)
          if (merged.length > 1) setBioParam(initial?.stack || initial?.profession || null)
          else setBioParam(null)
          setError(null)
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Unable to load profile')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slugParam, bioParam])

  useEffect(() => {
    if (provider) setSelectedStack(provider.stack || provider.profession || null)
  }, [provider])

  useEffect(() => {
    if (!provider || reviewsAutoOpened.current) return
    const params = new URLSearchParams(location.search)
    if (params.get('reviews') === '1' && Number(provider.rating) > 0) {
      reviewsAutoOpened.current = true
      onOpenReviews?.(provider)
    }
  }, [provider, location.search, onOpenReviews])

  const availabilityStackKey =
    provider?.stack && STACK_API_BASES[provider.stack]
      ? provider.stack
      : provider?.profession
        ? PROFESSION_TO_STACK[provider.profession]
        : null
  const availabilityApiBase = availabilityStackKey ? STACK_API_BASES[availabilityStackKey] : null
  const resolvedAvailability = Array.isArray(profileAvailability)
    ? profileAvailability
    : provider?.availabilitySlots

  useEffect(() => {
    let cancelled = false
    setProfileAvailability(null)
    if (!provider?.userId || !availabilityApiBase) return () => { cancelled = true }
    const existing = Array.isArray(provider?.availabilitySlots) ? provider.availabilitySlots : []
    if (existing.length) return () => { cancelled = true }
    ;(async () => {
      try {
        const qs = new URLSearchParams({
          specialistId: provider.userId,
          days: '30',
          ts: Date.now().toString(),
        })
        const res = await fetch(`${availabilityApiBase}/availability/slots?${qs.toString()}`, { cache: 'no-store' })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json?.error || 'Failed to load availability')
        const slots = Array.isArray(json?.slots) ? json.slots : []
        if (!cancelled) setProfileAvailability(slots)
      } catch (e) {
        if (!cancelled) setProfileAvailability([])
      }
    })()
    return () => { cancelled = true }
  }, [provider?.userId, availabilityApiBase, provider?.availabilitySlots])

  useEffect(() => {
    if (Array.isArray(resolvedAvailability) && resolvedAvailability.length) {
      setSelectedSlot(resolvedAvailability[0])
    } else {
      setSelectedSlot(null)
    }
    setAvailabilityExpanded(false)
    setExpandedSlots(null)
    setAvailabilityLoading(false)
    setAvailabilityError(null)
  }, [provider?.userId, resolvedAvailability])

  const hasAvailability = Array.isArray(resolvedAvailability) && resolvedAvailability.length > 0
  const priceLabel =
    provider?.price && !provider.price.toLowerCase().includes('contact for quote')
      ? `${provider.price} (per 30 minutes)`
      : provider?.price || null
  const providerLocation = addSamuelHartleyPerthLocation(provider, provider?.location)
  const metaParts: string[] = []
  if (providerLocation && !isDirectBook() && !shouldHideLocation(provider)) metaParts.push(providerLocation)
  if (priceLabel) metaParts.push(priceLabel)
  const pricingNotes = provider?.pricingNotes
  const specialties = provider?.specialties || []
  const profileText = applyProfileDisclaimers(
    provider?.profile || 'Verified provider on EUCA Match.',
    provider?.profession || ''
  )
  const metaTitle = provider
    ? `${provider.name} | ${provider.profession} on EUCA Match`
    : 'EUCA Match | Professional Profile'
  const metaDescription = provider
    ? [
        provider.profile || `${provider.name} — ${provider.profession} on EUCA Match.`,
        metaParts.join(' · '),
        specialties.length ? `Specialties: ${specialties.slice(0, 6).join(', ')}` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : 'View verified professionals on EUCA Match.'
  usePageMeta(metaTitle, metaDescription, location.pathname, provider?.image || `${SITE_URL}/home1.png`)
  const personLd =
    provider &&
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: provider.name,
      jobTitle: provider.profession,
      description: provider.profile || undefined,
      image: provider.image || undefined,
      url: `${SITE_URL}${location.pathname}`,
      address: shouldHideLocation(provider) ? undefined : providerLocation || undefined,
      areaServed: shouldHideLocation(provider) ? undefined : providerLocation || undefined,
      knowsAbout: specialties.slice(0, 12),
      offers:
        provider.price && provider.price.toLowerCase().includes('$')
          ? {
              '@type': 'Offer',
              price: provider.price,
              priceCurrency: 'AUD',
              availability: 'https://schema.org/InStock',
            }
          : undefined,
    }

  const nextSlots = Array.isArray(resolvedAvailability)
    ? resolvedAvailability.slice(0, 3)
    : []
  const canExpandAvailability = Boolean(hasAvailability && provider?.userId && availabilityApiBase)
  const nextAvailabilityLabel = nextSlots.length
    ? (() => {
        const first = nextSlots[0]
        try {
          const d = new Date(first)
          const now = new Date()
          const isToday = d.toDateString() === now.toDateString()
          const isTomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString() === d.toDateString()
          const dayLabel = isToday
            ? 'Today'
            : isTomorrow
              ? 'Tomorrow'
              : d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
          return dayLabel
        } catch {
          return 'Next available'
        }
      })()
    : null
  const nextSlotOptions = nextSlots
    .map((slot) => {
      try {
        const d = new Date(slot)
        const label = d.toLocaleString('en-AU', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
        })
        return { iso: slot, label }
      } catch {
        return null
      }
    })
    .filter(Boolean) as { iso: string; label: string }[]
  const availabilityDays = React.useMemo(() => {
    if (!availabilityExpanded || !Array.isArray(expandedSlots)) return []
    const parsed = expandedSlots
      .map((iso) => {
        try {
          const d = new Date(iso)
          if (Number.isNaN(d.getTime())) return null
          const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
            d.getDate()
          ).padStart(2, '0')}`
          const dayLabel = d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
          const timeLabel = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })
          return { iso, time: d.getTime(), dayKey, dayLabel, timeLabel }
        } catch {
          return null
        }
      })
      .filter(Boolean) as { iso: string; time: number; dayKey: string; dayLabel: string; timeLabel: string }[]
    parsed.sort((a, b) => a.time - b.time)
    const groups: { key: string; label: string; slots: { iso: string; label: string }[] }[] = []
    const map = new Map<string, { label: string; slots: { iso: string; label: string }[] }>()
    parsed.forEach((slot) => {
      if (!map.has(slot.dayKey)) {
        map.set(slot.dayKey, { label: slot.dayLabel, slots: [] })
        groups.push({ key: slot.dayKey, label: slot.dayLabel, slots: map.get(slot.dayKey)!.slots })
      }
      map.get(slot.dayKey)!.slots.push({ iso: slot.iso, label: slot.timeLabel })
    })
    return groups
  }, [availabilityExpanded, expandedSlots])
  const loadExpandedAvailability = useCallback(async () => {
    if (!provider?.userId || !availabilityApiBase) return
    if (expandedSlots && expandedSlots.length) return
    setAvailabilityLoading(true)
    setAvailabilityError(null)
    try {
      const qs = new URLSearchParams({
        specialistId: provider.userId,
        days: '30',
        ts: Date.now().toString(),
      })
      const res = await fetch(`${availabilityApiBase}/availability/slots?${qs.toString()}`, { cache: 'no-store' })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.error || 'Failed to load availability')
      const slots = Array.isArray(json?.slots) ? json.slots : []
      setExpandedSlots(slots)
    } catch (e: any) {
      setAvailabilityError(e?.message || 'Failed to load availability')
    } finally {
      setAvailabilityLoading(false)
    }
  }, [provider?.userId, availabilityApiBase, expandedSlots])
  useEffect(() => {
    if (availabilityExpanded) loadExpandedAvailability()
  }, [availabilityExpanded, loadExpandedAvailability])

  if (loading && !provider) {
    return (
      <section className="provider-profile">
        {personLd ? (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd) }} />
        ) : null}
        <p className="muted">Loading profile…</p>
      </section>
    )
  }

  if (error || !provider) {
    return (
      <section className="provider-profile">
        {personLd ? (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd) }} />
        ) : null}
        <p className="muted">{error || 'Profile not found.'}</p>
        <Link className="btn" to="/">
          Back to homepage
        </Link>
      </section>
    )
  }

  return (
    <section className="provider-profile">
      {personLd ? (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd) }} />
      ) : null}
      <Link className="back-link" to={anchor}>
        ← Back to professionals
      </Link>
      {matches.length > 1 && (
        <div className="profile-switcher" role="group" aria-label="Select profile">
          <span className="switcher-label">View profile:</span>
          <div className="switcher-pills">
            {matches.map((m) => {
              const label = STACK_TITLE_MAP[m.stack || ''] || m.profession || 'Profile'
              const key = `${m.userId || m.slug || label}:${m.stack || m.profession || ''}`
              const active =
                (provider?.stack && provider.stack === m.stack) ||
                (!provider?.stack && provider?.profession && provider.profession === m.profession)
              return (
                <button
                  key={key}
                  type="button"
                  className={`pill switcher-pill${active ? ' active' : ''}`}
                  onClick={() => {
                    setProvider(m)
                    setReason('')
                    setReasonOpen(false)
                    setSelectedStack(m.stack || m.profession || null)
                    setBioParam(m.stack || m.profession || null)
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}
      <div className="provider-hero">
        <div className="provider-photo">
          <img src={provider.image || logoImage} alt={`${provider.name} profile`} loading="lazy" />
        </div>
        <div className="provider-copy">
          <p className="eyebrow">{provider.profession}</p>
          <h1 className="provider-name provider-name-line">
            <span>{provider.name}</span>
            <LicenseVerifiedBadge item={provider} />
          </h1>
          <div className="provider-meta">
            <span className="provider-meta-line">{metaParts.join(' · ')}</span>
            {Number(provider.rating) > 0 && (
              <button
                type="button"
                className="provider-rating reviews-link"
                onClick={(e) => {
                  e.preventDefault()
                  onOpenReviews?.(provider)
                }}
              >
                <span className="rating-row">
                  <span className="value">{Number(provider.rating).toFixed(1)}</span>
                  {renderStars(provider.rating)}
                </span>
                <span className="reviews-count">
                  {reviewCountLabel(provider.ratingCount)}
                </span>
              </button>
            )}
          </div>
          {pricingNotes && (
            <div className="provider-pricing" style={{ marginTop: '8px' }}>
              <div className="muted" style={{ fontWeight: 600, marginBottom: '4px' }}>
                Pricing notes
              </div>
              <p className="muted" style={{ margin: '0' }}>
                {pricingNotes}
              </p>
            </div>
          )}
          <div
            className="provider-bio"
            dangerouslySetInnerHTML={{
              __html: renderProfileHtml(profileText),
            }}
          />
          {specialties.length > 0 && (
            <div className="provider-tags">
              {specialties.map((spec) => (
                <span key={spec} className="pill">
                  {spec}
                </span>
              ))}
            </div>
          )}
          <div className="provider-actions">
            {hasAvailability && nextAvailabilityLabel ? (
              <div className="provider-availability">
                <div className="availability-head">
                  <h3>Next available</h3>
                  <span className="availability-hint">{nextAvailabilityLabel}</span>
                  {canExpandAvailability ? (
                    <button
                      type="button"
                      className="availability-toggle"
                      onClick={() => setAvailabilityExpanded((open) => !open)}
                      aria-expanded={availabilityExpanded}
                    >
                      {availabilityExpanded ? 'Hide full availability' : 'See full availability'}
                    </button>
                  ) : null}
                </div>
                {nextSlotOptions.length ? (
                  <div className="availability-chips" aria-label="Soonest available times">
                    {nextSlotOptions.map((opt) => (
                      <button
                        key={opt.iso}
                        type="button"
                        className={`availability-chip${selectedSlot === opt.iso ? ' is-selected' : ''}`}
                        onClick={() => setSelectedSlot(opt.iso)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {availabilityExpanded ? (
                  <div className="availability-calendar" aria-label="Full availability">
                    {availabilityLoading ? <p className="muted">Loading full availability…</p> : null}
                    {availabilityError && !availabilityLoading ? (
                      <div className="banner error">{availabilityError}</div>
                    ) : null}
                    {!availabilityLoading && !availabilityError && availabilityDays.length ? (
                      availabilityDays.map((day) => (
                        <div key={day.key} className="availability-day">
                          <div className="availability-day-title">{day.label}</div>
                          <div className="availability-day-slots">
                            {day.slots.map((slot) => (
                              <button
                                key={slot.iso}
                                type="button"
                                className={`availability-chip${selectedSlot === slot.iso ? ' is-selected' : ''}`}
                                onClick={() => setSelectedSlot(slot.iso)}
                              >
                                {slot.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : null}
                    {!availabilityLoading && !availabilityError && availabilityExpanded && !availabilityDays.length ? (
                      <p className="muted">No additional times listed.</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <button
              className="btn cta cta-green cta-badged profile-cta"
              type="button"
              onClick={() => {
                if (isDirectBook()) {
                  window.location.href = directUrl
                  return
                }
                window.location.href = buildProviderBookingUrl(
                  provider,
                  '',
                  clientSignupUrl,
                  selectedSlot || undefined,
                  true
                )
              }}
            >
              <span className="cta-badge">FREE</span>
              Send Booking Request
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function HomePage({
  clientSignupUrl,
  providerSignupUrl,
  showSearchBar,
  heroTitle,
  heroSubtitle,
  featuredSection,
  variant = 'default',
  newWebDesignHeader,
}: {
  clientSignupUrl: string
  providerSignupUrl: string
  showSearchBar?: boolean
  heroTitle?: string
  heroSubtitle?: React.ReactNode
  featuredSection?: React.ReactNode
  variant?: 'default' | 'newwebdesign'
  newWebDesignHeader?: React.ReactNode
}) {
  const isNewWebDesignVariant = variant === 'newwebdesign'
  const location = useLocation()
  const isNewWebDesignRoute = location.pathname === '/newwebdesign'
  usePageMeta(
    isNewWebDesignVariant
      ? 'EUCA Match | Search Verified Professionals Across Borders'
      : 'EUCA Match | Verification-first professional marketplace',
    isNewWebDesignVariant
      ? 'Search verified professionals across borders with EUCA Match, a verification-first marketplace for credential-verified professionals.'
      : 'Match with verified professionals across legal, healthcare, migration, finance, education, tech, and more — built on a shared trust layer.',
    isNewWebDesignRoute ? '/newwebdesign' : '/'
  )

  const [homeSearchQuery, setHomeSearchQuery] = useState('')
  const [homeSearchProfession, setHomeSearchProfession] = useState('')
  const navigate = useNavigate()
  const rotatingHomePlaceholder = useRotatingPlaceholder(!homeSearchQuery, SEARCH_HINTS)
  const [homeSuggestions, setHomeSuggestions] = useState<string[]>([])
  const [homeSuggestionsLoading, setHomeSuggestionsLoading] = useState(false)
  const [homeSuggestionsError, setHomeSuggestionsError] = useState<string | null>(null)
  const [homeSuggestionsOpen, setHomeSuggestionsOpen] = useState(false)
  const [homeInputFocused, setHomeInputFocused] = useState(false)
  const homeSuggestionsHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isNewWebDesignVariant) return
    if (!SEARCH_PUBLIC_API) {
      setHomeSuggestions([])
      return
    }
    if (!homeSearchQuery.trim() || homeSearchQuery.trim().length < 2) {
      setHomeSuggestions([])
      setHomeSuggestionsOpen(false)
      return
    }
    let cancelled = false
    const timeout = setTimeout(async () => {
      setHomeSuggestionsLoading(true)
      setHomeSuggestionsError(null)
      try {
        const sugg = await fetchSuggestApi(homeSearchQuery.trim())
        if (!cancelled) {
          setHomeSuggestions(sugg)
          setHomeSuggestionsOpen(homeInputFocused && sugg.length > 0)
        }
      } catch (err: any) {
        if (!cancelled) {
          setHomeSuggestionsError(err?.message || 'Unable to load suggestions')
          setHomeSuggestions([])
        }
      } finally {
        if (!cancelled) setHomeSuggestionsLoading(false)
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [homeSearchQuery, homeInputFocused, isNewWebDesignVariant])

  useManagedToolVideos('.pro-tools-section .pro-tool-video')

  const closeHomeSuggestionsSoon = () => {
    if (homeSuggestionsHideTimer.current) clearTimeout(homeSuggestionsHideTimer.current)
    homeSuggestionsHideTimer.current = setTimeout(() => setHomeSuggestionsOpen(false), 150)
  }

  const onHomeSuggestionClick = (val: string) => {
    setHomeSearchQuery(val)
    const params = new URLSearchParams()
    params.set('q', val)
    if (homeSearchProfession) params.set('profession', homeSearchProfession)
    navigate(`/search?${params.toString()}`, { replace: true })
    setHomeSuggestionsOpen(false)
  }

  return (
    <>
      {isNewWebDesignVariant ? (
        <section className="newwebdesign-hero">
          {newWebDesignHeader ? <div className="newwebdesign-hero-header">{newWebDesignHeader}</div> : null}
          <div className="newwebdesign-hero-background" aria-hidden="true">
            <picture>
              <source media="(max-width: 768px)" srcSet={newWebDesignBackgroundImageMobile} />
              <img src={newWebDesignBackgroundImage} alt="" loading="eager" fetchPriority="high" />
            </picture>
          </div>
          <div className="newwebdesign-hero-scrim" aria-hidden="true" />
          <div className="newwebdesign-hero-content">
            <h1 className="newwebdesign-title">{heroTitle || 'EUCA Match'}</h1>
            <div className="newwebdesign-copy">
              {heroSubtitle || <p>Match with verified professionals. Book with confidence.</p>}
            </div>
            <div className="newwebdesign-hero-actions">
              <Link
                className="btn newwebdesign-hero-action newwebdesign-hero-action-primary"
                to="/ai-search"
              >
                Tell us what you need
              </Link>
              <a
                className="btn newwebdesign-hero-action newwebdesign-hero-action-secondary"
                href={providerSignupUrl}
              >
                Register as a professional
              </a>
            </div>
            <div className="newwebdesign-credit">
              <p className="newwebdesign-founders">Created by Dr Lily Vu and Dr Dong Fang Zhao</p>
              <p className="newwebdesign-founded-brand">
                Founders of{' '}
                <a href="https://eucamd.com/" target="_blank" rel="noreferrer">
                  EUCA MD®
                </a>
              </p>
              <button
                className="newwebdesign-google-reviews"
                id="grw-inline-trigger"
                type="button"
                aria-label="Read EUCA MD Google reviews"
              >
                <img src={googleIconImage} alt="Google" />
                <span>Google</span>
                <strong id="grw-inline-rating">5.0</strong>
                <span id="grw-inline-stars" aria-hidden="true" />
                <span id="grw-inline-count">447 reviews</span>
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {showSearchBar && (
            <section className="search-page container search-variant search-variant-3">
              <form
                className="search-hero"
                onSubmit={(e) => {
                  e.preventDefault()
                  const params = new URLSearchParams()
                  if (homeSearchQuery.trim()) params.set('q', homeSearchQuery.trim())
                  if (homeSearchProfession) params.set('profession', homeSearchProfession)
                  navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`)
                }}
              >
                <div className="search-hero-row">
                  <div className="search-hero-field">
                    <SearchIcon aria-hidden className="search-hero-icon" />
                    <input
                      type="search"
                      className="search-hero-input"
                      placeholder={`Search: ${rotatingHomePlaceholder || 'tutor, nurse, legal'}`}
                      value={homeSearchQuery}
                      onChange={(e) => setHomeSearchQuery(e.target.value)}
                      onFocus={() => {
                        setHomeInputFocused(true)
                        if (homeSuggestions.length) setHomeSuggestionsOpen(true)
                      }}
                      onBlur={() => {
                        setHomeInputFocused(false)
                        closeHomeSuggestionsSoon()
                      }}
                    />
                  </div>
                  <div className="search-hero-field select">
                    <BriefcaseBusiness aria-hidden className="search-hero-icon" />
                    <select
                      className="search-hero-select"
                      value={homeSearchProfession}
                      onChange={(e) => setHomeSearchProfession(e.target.value)}
                    >
                      <option value="">All professions</option>
                      {industries.map((ind) => (
                        <option key={ind.value} value={ind.value}>
                          {ind.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button className="btn cta" type="submit">
                    Search
                  </button>
                </div>
              </form>
              {homeSuggestionsOpen && (
                <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
                  <div className="search-suggestions-header">
                    <p className="eyebrow">Search suggestions</p>
                    {homeSuggestionsLoading && <p className="muted tiny">Loading…</p>}
                    {homeSuggestionsError && !homeSuggestionsLoading && <div className="banner error">{homeSuggestionsError}</div>}
                  </div>
                  {!homeSuggestionsLoading && !homeSuggestionsError && homeSuggestions.length > 0 && (
                    <div className="suggestion-grid">
                      {homeSuggestions.slice(0, 60).map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="suggestion-chip"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onHomeSuggestionClick(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          <section className="banner-inline test2-hero">
            <div className="banner-inline-copy">
              <p className="eyebrow hero-eyebrow">Verification-First Marketplace</p>
              <div className="banner-lockup">
                <h1 className="banner-inline-title">{heroTitle || 'EUCA Match'}</h1>
              </div>
              <p className="banner-inline-subtitle hero-body">
                {heroSubtitle || 'Match with verified professionals. Book with confidence.'}
              </p>
              <div className="founder-note founder-inline">
                <span className="founder-inline-text">
                  Created by Dr Lily Vu and Dr Dong Zhao, Co-Founders of{' '}
                  <a href="https://eucamd.com" target="_blank" rel="noreferrer">EUCA MD®</a>
                </span>
                <span className="founder-inline-sep" aria-hidden="true">•</span>
                <button className="hero-grw-inline" id="grw-inline-trigger" type="button">
                  <span className="grw-inline-row">
                    <img className="grw-inline-logo" src={googleIconImage} alt="Google" />
                    <strong id="grw-inline-rating">—</strong>
                    <span id="grw-inline-stars" aria-hidden="true" />
                    <span id="grw-inline-count">— reviews</span>
                  </span>
                </button>
              </div>
              <div className="hero-cta">
                <a className="btn cta cta-badged" href={marketingSearchUrl}>
                  <span className="cta-badge">FREE</span>
                  Find Verified Professionals
                </a>
                <a className="btn cta pro-cta" href={providerSignupUrl}>
                  For Professionals
                </a>
              </div>
            </div>
            <div className="banner-inline-media" aria-hidden="true">
              <img className="banner-main" src={test2HeroImage} alt="" loading="lazy" />
              <img className="banner-overlay" src={cofounderImage} alt="EUCA Match co-founders" loading="lazy" />
            </div>
          </section>
        </>
      )}

      {featuredSection}

      <section className="why-euca home-soft-section">
          <div className="why-hero">
            <div className="why-head">
              <p className="eyebrow hero-eyebrow">Why EUCA Match</p>
              <h2>Confidence for clients. Embedded workflows for professionals.</h2>
              <p className="why-sub">
                EUCA Match is built to make professional services safer and simpler. Clients can confidently find credential-verified professionals,
                while professionals run their daily work in one trusted place — for EUCA Match bookings and existing clients alike.
              </p>
            </div>
            <div className="why-media" aria-hidden="true">
              <img
                src={test2WhyImage}
                alt="EUCA verification-first experience"
                loading="lazy"
              />
            </div>
          </div>
          <div className="why-grid">
            <article className="why-card">
              <div className="why-icon">
                <Globe2 aria-hidden="true" />
              </div>
              <h3>Verification-First by Design</h3>
              <p>
                All professionals are verified before going live, with healthcare-grade checks and transparent public profiles. Trust isn’t
                optional — it’s the foundation of the platform.
              </p>
            </article>
            <article className="why-card">
              <div className="why-icon">
                <ShieldCheck aria-hidden="true" />
              </div>
              <h3>Built for Regulated, Real-World Work</h3>
              <p>
                EUCA Match supports professionals across regulated industries — including legal, healthcare, migration, finance, education,
                property, and technology — with workflows designed to fit how they already practise.
              </p>
            </article>
            <article className="why-card">
              <div className="why-icon">
                <Clock aria-hidden="true" />
              </div>
              <h3>One Place for Daily Practice</h3>
              <p>
                Professionals manage bookings, accept secure payments, generate invoices, sync calendars, and keep client notes in one hub —
                without changing how they work.
              </p>
            </article>
          </div>
      </section>

      <section className="pro-tools-section home-soft-section">
          <div className="pro-tools">
            <div className="pro-tools-features">
              <section className="pro-tool">
                <div className="pro-tool-text">
                  <h3>Secure Online Payments & Automatic Invoicing</h3>
                  <div className="pro-tool-points">
                    <p>
                      <strong>Built-in, Stripe-powered payments.</strong> EUCA Match supports secure online credit card payments through
                      Stripe, fully integrated into the platform.
                    </p>
                    <p>
                      <strong>Automatic invoices, every time.</strong> Invoices are generated instantly for each successful payment — no
                      manual work required.
                    </p>
                    <p>
                      <strong>Transparent pricing.</strong> Standard Stripe processing fees apply (1.7% + $0.30 per transaction).
                    </p>
                    <p>
                      <strong>No platform fees.</strong> EUCA Match does not charge any additional payment or service fees.
                    </p>
                  </div>
                </div>
                <div className="pro-tool-media" aria-label="Payments demo video">
                  <video className="pro-tool-video" controls playsInline muted preload="none" poster={invoiceVideoPoster}>
                    <source src={invoiceVideoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </section>
              <section className="pro-tool is-flipped">
                <div className="pro-tool-text">
                  <h3>Calendar Sync That Just Works</h3>
                  <div className="pro-tool-points">
                    <p>
                      <strong>Google Calendar and Calendly, synced.</strong> EUCA Match connects with your Google Calendar and Calendly to
                      keep availability up to date automatically.
                    </p>
                    <p>
                      <strong>Busy times blocked.</strong> Existing events from both calendars are reflected in EUCA Match, so clients
                      can’t book when you’re unavailable.
                    </p>
                    <p>
                      <strong>Real-time updates.</strong> New bookings made through EUCA Match sync back instantly to your connected
                      calendars.
                    </p>
                    <p>
                      <strong>Shareable availability.</strong> Send booking links or availability by email or message, directly from your
                      calendar.
                    </p>
                  </div>
                </div>
                <div className="pro-tool-media" aria-label="Calendar sync demo video">
                  <video className="pro-tool-video" controls playsInline muted preload="none" poster={calendarVideoPoster}>
                    <source src={calendarVideoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </section>
              <section className="pro-tool">
                <div className="pro-tool-text">
                  <h3>Notes for Every Booking</h3>
                  <div className="pro-tool-points">
                    <p>
                      <strong>Structured booking notes.</strong> Write, save, and publish notes for each booking, keeping records clear
                      and organised.
                    </p>
                    <p>
                      <strong>Files and attachments.</strong> Upload images and documents as needed, all stored with the relevant booking.
                    </p>
                    <p>
                      <strong>AI-assisted drafting by profession.</strong> Type a few phrases and EUCA Match drafts a polished note using
                      language and structure suited to your profession.
                    </p>
                    <p>
                      <strong>AI summary of notes.</strong> Generate a succinct workflow-ready summary from your full notes to support
                      follow-ups, handovers, and faster review.
                    </p>
                  </div>
                </div>
                <div className="pro-tool-media" aria-label="Notes demo video placeholder">
                  <video className="pro-tool-video" controls playsInline muted preload="none" poster={notesVideoPoster}>
                    <source src={notesVideoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </section>
              <section className="pro-tool is-flipped">
                <div className="pro-tool-text">
                  <h3>Zoom + Google Meet Links for Accepted Bookings</h3>
                  <div className="pro-tool-points">
                    <p>
                      <strong>Zoom and Google Meet integrations.</strong> Create and attach video meeting links for accepted bookings
                      directly inside EUCA Match.
                    </p>
                    <p>
                      <strong>Manual send when needed.</strong> Share meeting links yourself when you want direct coordination with
                      the client.
                    </p>
                    <p>
                      <strong>Automatic link delivery.</strong> Configure supported booking flows to send meeting links automatically.
                    </p>
                    <p>
                      <strong>Booking-linked meeting details.</strong> Keep meeting information tied to each booking for clearer
                      scheduling and follow-up.
                    </p>
                  </div>
                </div>
                <div className="pro-tool-media" aria-label="Video meeting links demo video">
                  <video className="pro-tool-video" controls playsInline muted preload="none" poster={meetingVideoPoster}>
                    <source src={meetingVideoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </section>
            </div>
          </div>
        </section>
<section className="flow-light flow-a test2-flow home-soft-section">
          <div className="flow-head">
            <p className="eyebrow hero-eyebrow">How EUCA Match Works</p>
            <h2>One platform for clients and verified professionals.</h2>
          </div>
          <div className="flow-a-grid">
            <article className="flow-card">
              <p className="eyebrow">For Clients</p>
              <h3>Request, review, and book with confidence.</h3>
              <ul>
                <li>
                  <Send aria-hidden="true" />
                  <span>Submit a request to a credential-verified professional</span>
                </li>
                <li>
                  <ClipboardList aria-hidden="true" />
                  <span>Review transparent profiles, fees, and availability</span>
                </li>
                <li>
                  <ShieldCheck aria-hidden="true" />
                  <span>Book securely on a verification-first platform</span>
                </li>
                <li>
                  <CheckCircle2 aria-hidden="true" />
                  <span>Manage bookings and communication in one place</span>
                </li>
              </ul>
              <p>
                <strong>Free to use for clients.</strong>
              </p>
              <a className="btn cta cta-badged" href={marketingSearchUrl}>
                <span className="cta-badge">FREE</span>
                Find Verified Professionals
              </a>
            </article>
            <div className="flow-visual">
              <img src={secondaryImage} alt="Product preview" loading="lazy" />
            </div>
            <article className="flow-card">
              <p className="eyebrow">For Professionals</p>
              <h3>Verification-first profiles, with daily practice tools built in.</h3>
              <ul>
                <li>
                  <UserCheck aria-hidden="true" />
                  <span>Public, shareable profile with verified credentials</span>
                </li>
                <li>
                  <Clock aria-hidden="true" />
                  <span>Full control over availability, bookings, and client acceptance</span>
                </li>
                <li>
                  <CheckCircle2 aria-hidden="true" />
                  <span>Built-in tools for daily work: online payments, automatic invoices, calendar sync, and booking notes</span>
                </li>
              </ul>
              <p>
                <strong>Free to use for professionals.</strong>
              </p>
              <a className="btn cta pro-cta" href={providerSignupUrl}>
                For Professionals
              </a>
            </article>
          </div>
        </section>

      <section className="visual-highlight home-soft-section" id="industries">
        <div className="visual-copy">
          <p className="eyebrow">Who We Serve</p>
          <h2>Eight regulated industries. One verification-first platform.</h2>
<div className="visual-mobile-media" aria-hidden="true">
              <img src={test2IndustriesImage} alt="EUCA Match professionals collaborating" loading="lazy" />
            </div>
          <div className="industry-button-grid">
            {industries.map(({ icon: Icon, label, value }) => (
              <Link className="industry-button" key={`nav-${label}`} to={`/search?profession=${value}`}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="visual-media">
          <img
            src={test2IndustriesImage}
            alt="EUCA Match professionals collaborating"
            loading="lazy"
          />
        </div>
      </section>

    </>
  )
}

function ClientsPage() {
  usePageMeta(
    'EUCA Match for Clients',
    'How clients use EUCA Match to find credential-verified professionals across regulated industries.',
    '/clients',
    test2WhyImage
  )

  return (
    <section className="clients-page">
      <div className="clients-hero-grid clients-theme-1">
        <div className="clients-hero-copy">
          <p className="eyebrow hero-eyebrow">For Clients</p>
          <h1>Book credential-verified professionals with confidence.</h1>
          <p className="clients-lede">
            Choose a verified professional and send a booking request directly to them. Compare
            profiles, pricing, and availability — then book with confidence on a verification-first marketplace.
          </p>
          <div className="clients-hero-media mobile-only" aria-hidden="true">
            <img className="clients-hero-img" src={test2WhyImage} alt="EUCA Match client experience preview" loading="lazy" />
          </div>
          <div className="clients-cards">
            <article className="clients-card">
              <p className="eyebrow muted">How it works</p>
              <h3>Send One Request. Match with a Professional.</h3>
              <ul>
                <li>
                  <SearchIcon aria-hidden="true" />
                  <span>Start a search</span>
                </li>
                <li>
                  <Users aria-hidden="true" />
                  <span>Select a professional</span>
                </li>
                <li>
                  <Send aria-hidden="true" />
                  <span>
                    Send your request —{' '}
                    <span className="promo-price-stack">
                      <span className="new-price">FREE</span>
                    </span>
                  </span>
                </li>
              </ul>
            </article>
            <article className="clients-card">
              <p className="eyebrow muted">After you send</p>
              <h3>Professionals Respond Directly.</h3>
              <ul>
                <li>
                  <ShieldCheck aria-hidden="true" />
                  <span>Verified professionals receive your request</span>
                </li>
                <li>
                  <ClipboardList aria-hidden="true" />
                  <span>Arrange details directly with professionals</span>
                </li>
                <li>
                  <Star aria-hidden="true" />
                  <span>Leave a review after your appointment</span>
                </li>
              </ul>
            </article>
          </div>
          <div className="clients-cta-row">
            <Link className="btn cta cta-badged" to="/search">
              <span className="cta-badge">FREE</span>
              Find Verified Professionals
            </Link>
          </div>
        </div>
        <div className="clients-hero-media desktop-only" aria-hidden="true">
          <img className="clients-hero-img" src={test2WhyImage} alt="EUCA Match client experience preview" loading="lazy" />
        </div>
      </div>
    </section>
  )
}

function ServicesPage() {
  usePageMeta(
    'EUCA Match | Industries & Eligibility',
    'Explore the 8 industries supported by EUCA Match and see who can join as a verified professional.',
    '/industries'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Industries', item: `${SITE_URL}/industries` },
    ],
  }

  return (
    <section className="services-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header"></div>
      {/* Allied Health & Medical Doctors (combined anchors for Allied and Doctors) */}
      <article className="industry-detail" id={slugify('Allied Health')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Medical Doctors & Allied Health</h3>
            <span id="allied-health" className="anchor-target" aria-hidden="true" />
            <span id="doctors" className="anchor-target" aria-hidden="true" />
            <div className="detail-cards stacked">
              <div className="detail-card">
                <h4>Eligibility — Medical Doctors</h4>
                <ul>
                  <li>Unrestricted AHPRA registration (general or specialist)</li>
                  <li>Consult via telehealth or face-to-face</li>
                  <li>Use your own practice/admin/billing.</li>
                </ul>
              </div>
              <div className="detail-card">
                <h4>Eligibility — Allied Health</h4>
                <p className="muted" style={{ marginBottom: 10 }}>
                  Eligible professions (with the registrations noted):
                </p>
                <ul>
                  <li>Psychologist (AHPRA)</li>
                  <li>Social Worker (AASW)</li>
                  <li>Counsellor (PACFA or ACA)</li>
                  <li>Speech Pathologist (Speech Pathology Australia)</li>
                  <li>Audiologist (Audiology Australia)</li>
                  <li>Accredited Practising Dietitian (Dietitians Australia)</li>
                  <li>Diabetes Educator (ADEA)</li>
                  <li>Registered Nurse (AHPRA)</li>
                  <li>Physiotherapist (AHPRA)</li>
                  <li>Exercise Physiologist (ESSA)</li>
                  <li>Occupational Therapist (AHPRA)</li>
                  <li>Chiropractor (AHPRA)</li>
                  <li>Osteopath (AHPRA)</li>
                  <li>Podiatrist (AHPRA)</li>
                  <li>Optometrist (AHPRA)</li>
                  <li>Orthoptist (Orthoptics Australia)</li>
                  <li>Pharmacist (AHPRA)</li>
                  <li>Chinese Medicine Practitioner (Acupuncturist) (AHPRA)</li>
                </ul>
                <p className="muted" style={{ marginTop: 10 }}>
                  You must be able to practise independently and use your own practice systems.
                </p>
              </div>
              <div className="detail-card">
                <h4>How Matching Works</h4>
                <ul>
                  <li>Receive client requests</li>
                  <li>After accepting, receive the client’s details</li>
                  <li>Arrange ongoing sessions directly through your own practice</li>
                </ul>
              </div>
              <div className="detail-card important">
                <h4>Important</h4>
                <ul>
                  <li>EUCA Match is not a medical or allied health practice</li>
                  <li>No Medicare billing, admin, e-scripts, letters, notes, or reception support</li>
                  <li>Matching service only</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Education & Tutoring */}
      <article className="industry-detail education-detail" id={slugify('Education & Tutoring')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Education & Tutoring</h3>
            <p>Australian-based educators providing learning support remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Academic tutors (primary, high school, university)</li>
                <li>Language tutors</li>
                <li>Exam and test preparation coaches</li>
                <li>Qualified teachers and education specialists</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Have relevant qualifications or proven teaching experience</li>
                <li>Provide lessons and support remotely or face-to-face</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      {/* Financial */}
      <article className="industry-detail financial-detail" id={slugify('Financial')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Financial</h3>
            <p>Australian-qualified professionals offering financial and business support remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Accountants and bookkeepers</li>
                <li>Tax consultants</li>
                <li>Financial planners</li>
                <li>Business and budgeting advisors</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Hold relevant Australian registrations (CPA, CA, Tax Agent, ASIC)</li>
                <li>Provide advice or document-based services remotely or face-to-face</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      {/* Legal, Governance & Compliance */}
      <article className="industry-detail legal-detail" id={slugify('Legal, Governance & Compliance')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Legal, Governance & Compliance</h3>
            <p>Qualified Australian professionals delivering legal, translation, or document support remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Lawyers and legal advisors</li>
                <li>Contract and dispute consultants</li>
                <li>NAATI-certified translators</li>
                <li>Document preparation and attestation professionals</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Hold the appropriate Australian qualifications or certifications</li>
                <li>Provide advice or document-based services remotely or face-to-face</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      {/* Migration & Visa */}
      <article className="industry-detail migration-detail" id={slugify('Migration & Visa')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Migration & Visa</h3>
            <p>Australian-qualified professionals delivering migration and visa support remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Registered migration agents</li>
                <li>Immigration consultants</li>
                <li>Visa document and application specialists</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Hold valid Australian registration or certification (e.g., OMARA)</li>
                <li>Provide advice and document support remotely or face-to-face</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      {/* Property, Design & Building */}
      <article className="industry-detail realestate-detail" id={slugify('Property, Design & Building')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Property, Design & Building</h3>
            <p>Australian-based property professionals providing advice and guidance remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Buyer’s agents / seller’s agents</li>
                <li>Property managers</li>
                <li>Property consultants & advisors</li>
                <li>Renovation & investment consultants</li>
                <li>Strata & tenancy guidance professionals</li>
                <li>Architects</li>
                <li>Interior designers</li>
                <li>Building / property inspectors</li>
                <li>Forensic engineers</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Hold relevant Australian qualifications or property experience</li>
                <li>Provide consultations (remote or face-to-face; no inspections via the platform)</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      {/* Tech, Creative & Media */}
      <article className="industry-detail tech-detail" id={slugify('Tech, Creative & Media')}>
          <div className="detail-primary">
          <div className="detail-copy">
            <h3 className="industry-title">Tech, Creative & Media</h3>
            <p>Australian-based tech and creative professionals offering services remotely or face-to-face.</p>
            <div className="detail-card">
              <h4>Eligible professionals</h4>
              <ul>
                <li>Software engineers & developers</li>
                <li>IT support & systems specialists</li>
                <li>UI/UX & product designers</li>
                <li>Graphic designers & branding specialists</li>
                <li>Digital/media creators (video, content, ads)</li>
                <li>Journalists & content writers</li>
              </ul>
            </div>
            <div className="detail-card">
              <h4>Preferred</h4>
              <ul>
                <li>Have relevant qualifications or proven experience</li>
                <li>Provide project-based or consultative services remotely or face-to-face</li>
                <li>Manage your own billing, notes, and admin outside EUCA Match</li>
              </ul>
            </div>
          </div>
        </div>
      </article>

    </section>
  )
}
function AboutPage() {
  usePageMeta(
    'EUCA Match | About',
    'Learn about EUCA Match, the doctor-led team behind the platform, and our mission to connect clients with verified professionals.',
    '/about'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${SITE_URL}/about` },
    ],
  }
  return (
    <section className="about-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="about-primary">
        <div className="founder-hero">
          <img src={cofounderImage} alt="Dr Dong Fang Zhao and Dr Lily Vu, co-founders of EUCA Match" loading="lazy" />
        </div>
        <div className="about-content">
          <p className="eyebrow">About Us</p>
          <div className="about-card">
            <p>
              EUCA Match is a professional marketplace connecting clients with verified, qualified professionals across
              multiple regulated industries.
            </p>
            <p>
              Founded and built by Dr Lily Vu (MD, University of Melbourne) and Dr Dong Fang Zhao (MD, University of
              Sydney), EUCA Match applies a healthcare-grade approach to trust and verification, informed by the founders'
              experience scaling a high-growth telehealth platform in a highly regulated environment.
            </p>
            <p>
              For clients, EUCA Match enables discovery of verified professionals offering online, in-person, or hybrid
              services through transparent, credential-verified profiles. Clients can request a verified professional,
              improving response times and reducing friction.
            </p>
            <p>
              For professionals, the platform provides a public profile, shareable booking link, optional live availability
              calendar, and Australia-wide client discovery, while allowing full control over how they operate their
              practice.
            </p>
            <p>
              Legal and healthcare are EUCA Match's initial liquidity anchors, with the platform designed to expand
              horizontally across additional regulated industries on a shared trust layer.
            </p>
            <p>EUCA Match supports multiple industries, including:</p>
            <ul className="about-list">
              <li>Legal, Governance & Compliance</li>
              <li>Allied Health</li>
              <li>Medical Doctors</li>
              <li>Education & Tutoring</li>
              <li>Financial Services</li>
              <li>Migration &amp; Visa</li>
              <li>Property, Design & Building</li>
              <li>Tech, Creative & Media</li>
            </ul>
            <p>
              EUCA Match is building a verification-first, multi-industry trust layer, helping people connect with the
              right professional faster, with greater confidence.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function JoinUsPage() {
  usePageMeta(
    'EUCA Match | Join Us',
    'Explore Join Us opportunities by country and region across EUCA Match professional industries.',
    '/join-us'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Join Us', item: `${SITE_URL}/join-us` },
    ],
  }

  return (
    <section className="services-page joinus-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header">
        <p className="eyebrow">Job Ad - Join Us</p>
        <h1>Explore Opportunities by Country and Region</h1>
        <p>
          EUCA Match is building opportunities across eight professional industries in multiple markets. Choose a
          country or region to view the structure. You can then drill down to each profession.
        </p>
      </div>
      <div className="joinus-grid" aria-label="Join Us regions">
        {JOIN_US_REGIONS.map((region) => (
          <article className="joinus-card" key={region.slug}>
            <div className="joinus-card-head">
              <h2>{region.label}</h2>
              {region.kind === 'Region' ? <span className="joinus-badge">{region.kind}</span> : null}
            </div>
            <p>{region.summary}</p>
            <div className="joinus-card-actions">
              <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
                View {region.label}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function JoinUsRegionPage() {
  const { regionSlug = '' } = useParams<{ regionSlug: string }>()
  const region = JOIN_US_REGION_MAP.get(regionSlug)
  const title = region ? `EUCA Match | Join Us in ${region.label}` : 'EUCA Match | Join Us'
  const description = region
    ? `Explore ${region.label} Join Us opportunities across all EUCA Match professional categories.`
    : 'Explore Join Us opportunities across EUCA Match markets.'
  usePageMeta(title, description, `/join-us/${regionSlug}`)

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Join Us', item: `${SITE_URL}/join-us` },
    ...(region
      ? [{ '@type': 'ListItem', position: 3, name: region.label, item: `${SITE_URL}/join-us/${region.slug}` }]
      : []),
  ]
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  }

  if (!region) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>Region Not Found</h1>
          <p>We could not find this country or region. Please choose from the Join Us index.</p>
        </div>
        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to="/join-us">
            Back to Join Us
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="services-page joinus-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="joinus-breadcrumb">
        <Link to="/join-us">Join Us</Link>
        <span aria-hidden> / </span>
        <span>{region.label}</span>
      </div>
      <div className="section-header">
        <p className="eyebrow">Join Us in {region.label}</p>
        <h1>{region.label}: Profession Categories</h1>
        <p>
          Select a profession to view localized opportunity details, eligibility expectations, and application steps
          for this {region.kind.toLowerCase()}.
        </p>
      </div>
      <div className="joinus-grid" aria-label={`${region.label} profession opportunities`}>
        {JOIN_US_PROFESSIONS.map((profession) => (
          <article className="joinus-card" key={`${region.slug}-${profession.slug}`}>
            <div className="joinus-card-head">
              <h2>{profession.label}</h2>
            </div>
            <p>{profession.summary}</p>
            <div className="joinus-card-actions">
              <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}/${profession.slug}`}>
                Learn more
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function JoinUsOpportunityPage() {
  const { regionSlug = '', professionSlug = '' } = useParams<{ regionSlug: string; professionSlug: string }>()
  const region = JOIN_US_REGION_MAP.get(regionSlug)
  const profession = JOIN_US_PROFESSION_MAP.get(professionSlug)
  const legalCopy = region && profession?.slug === 'legal' ? LEGAL_REGION_COPY[region.slug] || null : null
  const educationCopy = region && profession?.slug === 'education' ? EDUCATION_REGION_COPY[region.slug] || null : null
  const propertyCopy = region && profession?.slug === 'property' ? PROPERTY_REGION_COPY[region.slug] || null : null
  const techCopy = region && profession?.slug === 'tech' ? TECH_REGION_COPY[region.slug] || null : null
  const doctorCopy = region && profession?.slug === 'doctor' ? DOCTOR_REGION_COPY[region.slug] || null : null
  const alliedCopy = region && profession?.slug === 'allied' ? ALLIED_REGION_COPY[region.slug] || null : null
  const migrationCopy = region && profession?.slug === 'migration' ? MIGRATION_REGION_COPY[region.slug] || null : null
  const financialCopy = region && profession?.slug === 'financial' ? FINANCIAL_REGION_COPY[region.slug] || null : null
  const opportunityTitle =
    region && profession
      ? legalCopy?.title ||
        educationCopy?.title ||
        propertyCopy?.title ||
        techCopy?.title ||
        doctorCopy?.title ||
        alliedCopy?.title ||
        migrationCopy?.title ||
        financialCopy?.title ||
        `${profession.label} - Join EUCA Match (${region.label})`
      : 'EUCA Match | Join Us Opportunities'
  const jobAdRoleTitle = profession ? JOIN_US_PROFESSION_JOB_TITLE[profession.slug] || profession.label : 'Professional'
  const seoTitle = region && profession ? `Job Ad: ${opportunityTitle}` : 'EUCA Match | Join Us Opportunities'
  const seoDescription =
    region && profession
      ? `Job ad for independent ${profession.label} professionals in ${region.label}. Flexible online, in-person, or hybrid services. Free listing with no commission or subscription.`
      : 'Join Us opportunity page.'
  usePageMeta(seoTitle, seoDescription, `/join-us/${regionSlug}/${professionSlug}`)

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!robots) {
      robots = document.createElement('meta')
      robots.setAttribute('name', 'robots')
      document.head.appendChild(robots)
    }
    robots.setAttribute('content', 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1')

    let googlebot = document.querySelector('meta[name="googlebot"]') as HTMLMetaElement | null
    if (!googlebot) {
      googlebot = document.createElement('meta')
      googlebot.setAttribute('name', 'googlebot')
      document.head.appendChild(googlebot)
    }
    googlebot.setAttribute('content', 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1')
  }, [])

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Join Us', item: `${SITE_URL}/join-us` },
    ...(region
      ? [{ '@type': 'ListItem', position: 3, name: region.label, item: `${SITE_URL}/join-us/${region.slug}` }]
      : []),
    ...(region && profession
      ? [
          {
            '@type': 'ListItem',
            position: 4,
            name: profession.label,
            item: `${SITE_URL}/join-us/${region.slug}/${profession.slug}`,
          },
        ]
      : []),
  ]
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  }
  const isUnitedStates = region?.slug === 'united-states'
  const practiceWord = isUnitedStates ? 'practice' : 'practise'
  const jobPostingSchema =
    region && profession
      ? {
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: `${jobAdRoleTitle} - ${region.label}`,
          description: seoDescription,
          datePosted: JOIN_US_JOB_DATE_POSTED,
          validThrough: JOIN_US_JOB_VALID_THROUGH,
          employmentType: 'CONTRACTOR',
          hiringOrganization: {
            '@type': 'Organization',
            name: 'EUCA Match',
            sameAs: SITE_URL,
            logo: `${SITE_URL}/logo.png`,
          },
          identifier: {
            '@type': 'PropertyValue',
            name: 'EUCA Match',
            value: `join-us-${region.slug}-${profession.slug}`,
          },
          directApply: true,
          jobLocationType: 'TELECOMMUTE',
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              addressCountry: JOIN_US_REGION_COUNTRY_CODE[region.slug] || region.label,
              addressRegion: region.label,
            },
          },
          applicantLocationRequirements: {
            '@type': 'Country',
            name: JOIN_US_REGION_COUNTRY_CODE[region.slug] || region.label,
          },
          industry: profession.label,
          occupationalCategory: profession.label,
          workHours: 'Flexible and self-managed',
          incentiveCompensation: 'Free listing. No commission. No subscription.',
          keywords: `${JOIN_US_PROFESSION_KEYWORDS[profession.slug] || profession.label}, ${region.label}`,
          url: `${SITE_URL}/join-us/${region.slug}/${profession.slug}`,
        }
      : null

  useEffect(() => {
    const scriptId = 'join-us-jobposting-schema'
    const existing = document.getElementById(scriptId)
    if (existing) existing.remove()
    if (!jobPostingSchema) return
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.text = JSON.stringify(jobPostingSchema)
    document.head.appendChild(script)
    return () => {
      const node = document.getElementById(scriptId)
      if (node) node.remove()
    }
  }, [jobPostingSchema])
  const renderComplianceNoticeCard = () => (
    <article className="joinus-detail-card">
      <h2>Compliance notice</h2>
      <p>{JOIN_US_COMPLIANCE_NOTICE}</p>
      <p>{JOIN_US_CROSS_BORDER_NOTICE}</p>
    </article>
  )

  if (!region || !profession) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>Opportunity Page Not Found</h1>
          <p>Please return to the Join Us region page and choose a listed profession.</p>
        </div>
        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to="/join-us">
            Back to Join Us
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'migration' && migrationCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{migrationCopy.title}</h1>
          <p>{migrationCopy.intro}</p>
          <p>{migrationCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {MIGRATION_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{migrationCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who this suits</h2>
          <p>{migrationCopy.whoThisSuits}</p>
          <p>Please apply only if you hold any registrations, licences, or authorisations required in your jurisdiction.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Common areas of work</h2>
          <ul>
            {MIGRATION_WORK_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
          <p>Services may be delivered online, in person, or hybrid, depending on your practice.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply (preferred)</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Migration profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=migration" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=migration
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Questions</h2>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'legal' && legalCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{legalCopy.title}</h1>
          <p>{legalCopy.intro}</p>
          <p>{legalCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {LEGAL_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{legalCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who this is suited for</h2>
          <p>{legalCopy.whoCanApplyLead}</p>
          <ul>
            {legalCopy.credentialItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Based in {region.label}.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Areas of work may include</h2>
          <ul>
            {LEGAL_WORK_AREAS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Services can be delivered in person, online, or both.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Legal, Governance, and Compliance profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=legal" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=legal
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'doctor' && doctorCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{doctorCopy.title}</h1>
          <p>{doctorCopy.intro}</p>
          <p>{doctorCopy.onboarding}</p>
          <p>EUCA Match is not an emergency service. If you need urgent or emergency medical care, contact local emergency services immediately.</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {DOCTOR_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{doctorCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who can apply (required)</h2>
          <p>{doctorCopy.whoCanApplyLead}</p>
          <ul>
            {doctorCopy.credentialItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            <strong>You must also:</strong>
          </p>
          <ul>
            <li>Be based in {region.label}</li>
            <li>Be eligible to {practiceWord} independently</li>
            <li>Hold appropriate professional indemnity insurance</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>About EUCA Match</h2>
          {ALLIED_ABOUT_PARAGRAPHS.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Medical Doctor profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=doctor" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=doctor
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'education' && educationCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{educationCopy.title}</h1>
          <p>{educationCopy.intro}</p>
          <p>{educationCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {EDUCATION_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{educationCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Requirements</h2>
          <p>{educationCopy.requirementsLead}</p>
          <ul>
            {EDUCATION_REQUIREMENT_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            Please apply only if you hold any registration, authorisation, or credential required in your jurisdiction
            for the services you provide.
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Subjects may include</h2>
          <ul>
            {EDUCATION_SUBJECT_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Lessons can be delivered online, in person, or both.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Education and Tutoring profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=education" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=education
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'property' && propertyCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{propertyCopy.title}</h1>
          <p>{propertyCopy.intro}</p>
          <p>{propertyCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {PROPERTY_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{propertyCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who this is suited for</h2>
          <p>{propertyCopy.suitedLead}</p>
          <ul>
            {PROPERTY_SUITED_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Based in {region.label}.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Areas of work may include</h2>
          <ul>
            {PROPERTY_WORK_AREAS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Services can be delivered in person, online, or both.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Property, Design and Building profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=property" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=property
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'tech' && techCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{techCopy.title}</h1>
          <p>{techCopy.intro}</p>
          <p>{techCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {TECH_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{techCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who this is suited for</h2>
          <p>{techCopy.suitedLead}</p>
          <ul>
            {TECH_SUITED_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Based in {region.label}.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Areas of work may include</h2>
          <ul>
            {TECH_WORK_AREAS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Services can be delivered in person, online, or both.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Tech, Creative, and Media profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=tech" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=tech
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'allied' && alliedCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{alliedCopy.title}</h1>
          <p>{alliedCopy.intro}</p>
          <p>{alliedCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {ALLIED_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{alliedCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who can apply (required)</h2>
          <p>{alliedCopy.whoCanApplyLead}</p>
          <ul>
            {alliedCopy.credentialItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            <strong>You must also:</strong>
          </p>
          <ul>
            <li>Be based in {region.label}</li>
            <li>Be eligible to {practiceWord} independently</li>
            <li>Hold appropriate professional indemnity insurance</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>About EUCA Match</h2>
          {ALLIED_ABOUT_PARAGRAPHS.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Allied Health profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=allied" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=allied
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more or apply</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  if (profession.slug === 'financial' && financialCopy) {
    return (
      <section className="services-page joinus-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
        <div className="joinus-breadcrumb">
          <Link to="/join-us">Join Us</Link>
          <span aria-hidden> / </span>
          <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
          <span aria-hidden> / </span>
          <span>{profession.label}</span>
        </div>
        <div className="section-header">
          <p className="eyebrow">Job Ad - Join Us</p>
          <h1>{financialCopy.title}</h1>
          <p>{financialCopy.intro}</p>
          <p>{financialCopy.onboarding}</p>
          <p>EUCA Match is designed to support your practice, not interfere with it.</p>
          <p>
            Clients can discover and contact you directly while you remain fully independent. You retain full control
            over the services you offer, your pricing, your availability, and how you work with clients.
          </p>
          <p>
            In addition to client discovery, EUCA Match provides practical, time-saving tools to support day-to-day
            practice management in one place, for both EUCA Match clients and your existing clients.
          </p>
        </div>

        <article className="joinus-detail-card">
          <h2>What EUCA Match offers</h2>
          <ul>
            {FINANCIAL_OFFER_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>{financialCopy.discoveryLine}</li>
          </ul>
        </article>

        <article className="joinus-detail-card">
          <h2>Cost</h2>
          <p>Free. No commission. No subscription. No lock-in.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Who this suits</h2>
          <p>{financialCopy.whoThisSuitsLead}</p>
          <ul>
            {financialCopy.whoThisSuitsItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>Please apply only if you hold any registrations, licences, or authorisations required in your jurisdiction.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>Areas of work may include</h2>
          <p>
            <strong>Accounting and Tax</strong>
          </p>
          <ul>
            {FINANCIAL_ACCOUNTING_TAX_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
          <p>
            <strong>Financial Advice</strong>
          </p>
          <ul>
            {FINANCIAL_ADVICE_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
          <p>
            <strong>Mortgage and Lending</strong>
          </p>
          <ul>
            {FINANCIAL_LENDING_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
          <p>Services may be delivered in person, online, or hybrid, depending on your role and licence.</p>
        </article>

        <article className="joinus-detail-card">
          <h2>How to apply</h2>
          <ol>
            <li>
              Visit <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
            </li>
            <li>Click &ldquo;For Professionals&rdquo;</li>
            <li>Log in and select Register</li>
            <li>Complete your details and submit your application</li>
          </ol>
          <p>
            Once verification is complete, your professional profile will be published, and you will be notified by
            email.
          </p>
        </article>

        {renderComplianceNoticeCard()}

        <article className="joinus-detail-card">
          <h2>View current Accounting and Financial Services profiles</h2>
          <p>
            <a href="https://eucamatch.com/search?profession=financial" target="_blank" rel="noreferrer">
              https://eucamatch.com/search?profession=financial
            </a>
          </p>
        </article>

        <article className="joinus-detail-card">
          <h2>Learn more</h2>
          <p>
            <a href="https://eucamatch.com" target="_blank" rel="noreferrer">https://eucamatch.com</a>
          </p>
          <p>
            <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>
          </p>
        </article>

        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
            Back to {region.label}
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="services-page joinus-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="joinus-breadcrumb">
        <Link to="/join-us">Join Us</Link>
        <span aria-hidden> / </span>
        <Link to={`/join-us/${region.slug}`}>{region.label}</Link>
        <span aria-hidden> / </span>
        <span>{profession.label}</span>
      </div>
      <div className="section-header">
        <p className="eyebrow">Job Ad - Join Us</p>
        <h1>{profession.label} Opportunities in {region.label}</h1>
        <p>
          Structure page is ready. Populate this page next with role summary, eligibility, scope, workflow, and
          application details for {region.label}.
        </p>
      </div>
      <article className="joinus-detail-card">
        <h2>Page Scaffold</h2>
        <ul>
          <li>Role categories and opportunity types</li>
          <li>Eligibility and credential requirements</li>
          <li>Location and service delivery model</li>
          <li>Application process and contact points</li>
        </ul>
      </article>
      <div className="joinus-card-actions">
        <Link className="btn cta pro-cta compact" to={`/join-us/${region.slug}`}>
          Back to {region.label}
        </Link>
      </div>
    </section>
  )
}

function KnowledgeBasePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const activeTagId = searchParams.get('tag') || ''
  const activeTagLabel = KNOWLEDGE_BASE_TAG_LABEL_BY_ID.get(activeTagId) || activeTagId.replace(/-/g, ' ').trim()
  const normalizedQuery = query.toLowerCase().trim()
  const setKnowledgeBaseFilters = useCallback(
    (nextQuery: string, nextTagId: string) => {
      const params = new URLSearchParams(searchParams)
      const cleanQuery = nextQuery.trim()
      if (cleanQuery) params.set('q', cleanQuery)
      else params.delete('q')
      if (nextTagId) params.set('tag', nextTagId)
      else params.delete('tag')
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )
  const filteredArticles = React.useMemo(() => {
    return KNOWLEDGE_BASE_ARTICLES.filter((article) => {
      const articleTags = getKnowledgeBaseDiscoverabilityTags(article)
      const articleTagIds = new Set(articleTags.map((tag) => slugify(tag)))
      if (activeTagId && !articleTagIds.has(activeTagId)) return false
      if (!normalizedQuery) return true
      const haystack = [
        article.title,
        article.summary,
        article.category,
        article.audience,
        article.regionLabel,
        article.professionLabel,
        ...articleTags,
        ...article.keywords,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [activeTagId, normalizedQuery])

  usePageMeta(
    'EUCA Match | Knowledge Base',
    `50 search-friendly articles for clients and professionals across ${KNOWLEDGE_BASE_REGION_LIST_TEXT}.`,
    '/knowledge-base'
  )

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Knowledge Base', item: `${SITE_URL}/knowledge-base` },
    ],
  }

  return (
    <section className="services-page joinus-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header">
        <p className="eyebrow">Knowledge Base</p>
        <h1>Knowledge Base</h1>
        <p>
          Practical guides to help you choose the right professional, understand your options, and take the next step.
        </p>
        <p>Coverage: {KNOWLEDGE_BASE_REGION_LIST_TEXT}.</p>
        <div style={{ width: '100%', maxWidth: 760 }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setKnowledgeBaseFilters(e.target.value, activeTagId)}
            placeholder="Search by topic, region, or profession..."
            aria-label="Search knowledge base articles"
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: 10,
              padding: '0.85rem 1rem',
              fontSize: '0.98rem',
            }}
          />
        </div>
        <div className="kb-tag-toolbar" aria-label="Knowledge base tag filters">
          <div className="kb-tag-toolbar-head">
            <p className="muted">{activeTagId ? `Filtering by tag: ${activeTagLabel}` : 'Browse by tag'}</p>
            {activeTagId && (
              <button type="button" className="kb-tag-clear" onClick={() => setKnowledgeBaseFilters(query, '')}>
                Clear tag
              </button>
            )}
          </div>
          <div className="kb-tag-list">
            {KNOWLEDGE_BASE_TAG_OPTIONS.map((tag) => {
              const isActive = tag.id === activeTagId
              return (
                <button
                  key={`kb-tag-${tag.id}`}
                  type="button"
                  className={`kb-tag-chip ${isActive ? 'active' : ''}`}
                  onClick={() => setKnowledgeBaseFilters(query, isActive ? '' : tag.id)}
                  aria-pressed={isActive}
                >
                  {tag.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>
      <div className="joinus-card-actions" style={{ marginTop: '-1rem' }}>
        <p className="muted">
          Showing {filteredArticles.length} of {KNOWLEDGE_BASE_ARTICLES.length} articles
        </p>
      </div>
      <div className="joinus-grid" aria-label="Knowledge base article list">
        {filteredArticles.map((article) => {
          const articleTags = getKnowledgeBaseDiscoverabilityTags(article).slice(0, 4)
          return (
            <article className="joinus-card" key={article.slug}>
              <p className="eyebrow muted">
                {article.category} · {article.audience}
              </p>
              <h3>{article.title}</h3>
              <p>{article.summary}</p>
              <p className="muted">
                {article.regionLabel} · {article.professionLabel}
              </p>
              <div className="kb-card-tags" aria-label={`Tags for ${article.title}`}>
                {articleTags.map((tag) => (
                  <Link
                    key={`kb-card-tag-${article.slug}-${tag}`}
                    className="kb-card-tag"
                    to={`/knowledge-base?tag=${encodeURIComponent(slugify(tag))}`}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
              <Link className="btn cta pro-cta compact" to={`/knowledge-base/${article.slug}`}>
                Read article
              </Link>
            </article>
          )
        })}
      </div>
      {!filteredArticles.length && (
        <article className="joinus-detail-card">
          <h2>No articles found</h2>
          <p>Try broader terms like &ldquo;migration&rdquo;, &ldquo;pricing&rdquo;, or &ldquo;United States&rdquo;.</p>
        </article>
      )}
    </section>
  )
}

function KnowledgeBaseArticlePage() {
  const { articleSlug } = useParams<{ articleSlug: string }>()
  const article = (articleSlug && KNOWLEDGE_BASE_ARTICLE_MAP.get(articleSlug)) || null

  usePageMeta(
    article ? `${article.title} | EUCA Match Knowledge Base` : 'EUCA Match | Knowledge Base',
    article ? article.summary : 'Browse practical guides for clients and professionals.',
    article ? `/knowledge-base/${article.slug}` : '/knowledge-base'
  )

  if (!article) {
    return (
      <section className="services-page joinus-page">
        <div className="section-header">
          <p className="eyebrow">Knowledge Base</p>
          <h1>Article not found</h1>
          <p>We could not find this article. View all articles from the Knowledge Base hub.</p>
        </div>
        <div className="joinus-card-actions">
          <Link className="btn cta pro-cta compact" to="/knowledge-base">
            Back to Knowledge Base
          </Link>
        </div>
      </section>
    )
  }

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Knowledge Base', item: `${SITE_URL}/knowledge-base` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${SITE_URL}/knowledge-base/${article.slug}` },
    ],
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.updatedAt,
    dateModified: article.updatedAt,
    mainEntityOfPage: `${SITE_URL}/knowledge-base/${article.slug}`,
    author: { '@type': 'Organization', name: 'EUCA Match' },
    publisher: {
      '@type': 'Organization',
      name: 'EUCA Match',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    },
    keywords: article.keywords.join(', '),
  }

  const showProCta = article.audience === 'Professionals' || article.audience === 'Both'
  const articleTags = getKnowledgeBaseDiscoverabilityTags(article)
  const nextStepCopy =
    article.audience === 'Professionals'
      ? `If this was useful, the best next move is to see how clients are searching for support in ${article.regionLabel} and how your peers present their services. You can also set up your EUCA Match profile to start receiving direct enquiries.`
      : article.audience === 'Both'
        ? `If this gave you clarity, shortlist a few verified professionals on EUCA Match and book the one that best fits your goals, timeline, and budget. If you also provide services, you can create your profile and be discovered by clients directly.`
        : `If this helped, take the next step now: shortlist verified professionals on EUCA Match and book the one that feels right for your situation, timeline, and budget.`

  return (
    <section className="info-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <div className="section-header">
        <p className="eyebrow hero-eyebrow">Knowledge Base Article</p>
        <h2>{article.title}</h2>
        <p>{article.summary}</p>
        <p className="muted">
          Region: {article.regionLabel} · Updated: {article.updatedAt}
        </p>
        <div className="kb-article-tags" aria-label="Article tags">
          {articleTags.map((tag) => (
            <Link
              key={`kb-article-tag-${article.slug}-${tag}`}
              className="kb-article-tag"
              to={`/knowledge-base?tag=${encodeURIComponent(slugify(tag))}`}
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
      <div className="info-content">
        {article.sections.map((section, idx) => (
          <div key={`${article.slug}-section-${idx}`}>
            <h3>{section.heading}</h3>
            <p>{section.content}</p>
          </div>
        ))}
        <h3>Ready for the next step?</h3>
        <p>{nextStepCopy}</p>
        <div className="kb-article-cta-row">
          <Link className="btn cta compact kb-article-cta-primary" to={article.searchHref}>
            Book a Verified Professional
          </Link>
          {showProCta && (
            <Link className="btn cta compact kb-article-cta-secondary" to={article.joinUsHref}>
              For Professionals
            </Link>
          )}
          {!showProCta && (
            <Link className="btn cta compact kb-article-cta-secondary" to="/knowledge-base">
              Back to Knowledge Base
            </Link>
          )}
        </div>
        <Link className="kb-article-back-link" to="/knowledge-base">Back to Knowledge Base</Link>
      </div>
    </section>
  )
}

function SearchPage({ onOpenReviews }: { onOpenReviews?: (pro: ShowcaseItem) => void }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allProviders, setAllProviders] = useState<ShowcaseItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [roleFilter, setRoleFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [ratingFilter, setRatingFilter] = useState('any')
  const [sortBy, setSortBy] = useState('recommended')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const query = searchParams.get('q') || ''
  const profession = searchParams.get('profession') || ''
  const professionLabel = React.useMemo(
    () => industries.find((ind) => ind.value === profession)?.label || '',
    [profession]
  )
  const duplicateSearchSlugs = React.useMemo(() => {
    const counts = new Map<string, number>()
    allProviders.forEach((pro) => {
      if (!pro || !pro.slug) return
      const cleanSlug = String(pro.slug).replace(/-/g, '')
      counts.set(cleanSlug, (counts.get(cleanSlug) || 0) + 1)
    })
    return new Set(Array.from(counts.entries()).filter(([, c]) => c > 1).map(([slug]) => slug))
  }, [allProviders])
  const navigate = useNavigate()
  const roleOptions = React.useMemo(() => {
    const stackLabels = new Set(Object.values(STACK_TITLE_MAP).map((label) => label.toLowerCase()))
    const seen = new Map<string, string>()
    allProviders.forEach((pro) => {
      const label = String(pro.profession || '').trim()
      if (!label) return
      if (stackLabels.has(label.toLowerCase())) return
      const key = label.toLowerCase()
      if (!seen.has(key)) seen.set(key, label)
    })
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
  }, [allProviders])
  const filtersActive =
    roleFilter ||
    stateFilter ||
    ratingFilter !== 'any'
  const activeFilterCount = [
    roleFilter,
    stateFilter,
    ratingFilter !== 'any' ? 'rating' : '',
  ].filter(Boolean).length

  useEffect(() => {
    if (roleFilter && !roleOptions.includes(roleFilter)) setRoleFilter('')
  }, [roleFilter, roleOptions])

  const clearFilters = () => {
    setRoleFilter('')
    setStateFilter('')
    setRatingFilter('any')
  }

  usePageMeta(
    'EUCA Match | Search Professionals',
    'Search verified professionals across EUCA Match by expertise or keywords.',
    '/search'
  )

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const q = searchParams.get('q') || ''
        const p = searchParams.get('profession') || ''
        let merged: ShowcaseItem[] = []
        if (!SEARCH_PUBLIC_API) {
          throw new Error('Search API unavailable')
        }
        merged = await fetchSearchApi(q, p)
        if (!cancelled) setAllProviders(merged)
        if (!merged.length && !cancelled) setError('No professionals found.')
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load professionals')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [searchParams])

  const filtered = React.useMemo(() => {
    const q = (query || '').toLowerCase().trim()
    const p = (profession || '').toLowerCase().trim()
    const role = roleFilter.toLowerCase().trim()
    const stateTarget = stateFilter.toUpperCase().trim()
    const minRating = ratingFilter === 'any' ? null : Number(ratingFilter)
    const randomTieBreakers = new Map<string, number>()

    const randomOrderValue = (item: ShowcaseItem) => {
      const key = `${item.userId || ''}|${item.slug || ''}|${item.name || ''}|${item.profession || ''}`
      if (!randomTieBreakers.has(key)) randomTieBreakers.set(key, Math.random())
      return randomTieBreakers.get(key) || 0
    }

    const resolveState = (item: ShowcaseItem) => {
      if (item.state) return String(item.state).toUpperCase()
      const loc = String(item.location || '')
      const match = loc.match(/\b(NSW|VIC|QLD|SA|WA|TAS|ACT|NT)\b/i)
      return match ? match[1].toUpperCase() : ''
    }
    const matchText = (item: ShowcaseItem) => {
      if (!q) return true
      const haystack = [
        item.name || '',
        item.profession || '',
        item.profile || '',
        ...(Array.isArray(item.specialties) ? item.specialties : []),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    }
    const matchProfession = (item: ShowcaseItem) => {
      if (!p) return true
      return (
        item.profession.toLowerCase().includes(p) ||
        (item.stack ? item.stack.toLowerCase().includes(p) : false)
      )
    }
    const matchRole = (item: ShowcaseItem) => {
      if (!role) return true
      return String(item.profession || '').toLowerCase().trim() === role
    }
    const matchState = (item: ShowcaseItem) => {
      if (!stateTarget) return true
      return resolveState(item) === stateTarget
    }
    const matchRating = (item: ShowcaseItem) => {
      if (!minRating) return true
      const ratingVal = Number(item.rating)
      return Number.isFinite(ratingVal) && ratingVal >= minRating
    }
    const byRecommended = (a: ShowcaseItem, b: ShowcaseItem) => {
      const ra = Number(a.ratingCount) || 0
      const rb = Number(b.ratingCount) || 0
      const aReviewed = ra > 0
      const bReviewed = rb > 0
      if (aReviewed !== bReviewed) return Number(bReviewed) - Number(aReviewed)
      if (aReviewed && bReviewed) {
        return randomOrderValue(a) - randomOrderValue(b)
      }
      if (!aReviewed && !bReviewed) {
        const av = hasLicenseVerifiedBadge(a) ? 1 : 0
        const bv = hasLicenseVerifiedBadge(b) ? 1 : 0
        if (bv !== av) return bv - av
        const aa = hasAvailabilityListed(a) ? 1 : 0
        const ba = hasAvailabilityListed(b) ? 1 : 0
        if (ba !== aa) return ba - aa
        return randomOrderValue(a) - randomOrderValue(b)
      }
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    }
    const byHighestRated = (a: ShowcaseItem, b: ShowcaseItem) => {
      const ra = Number(a.rating) || 0
      const rb = Number(b.rating) || 0
      if (rb !== ra) return rb - ra
      return (b.ratingCount || 0) - (a.ratingCount || 0)
    }
    const byMostReviews = (a: ShowcaseItem, b: ShowcaseItem) => {
      const ra = Number(a.ratingCount) || 0
      const rb = Number(b.ratingCount) || 0
      if (rb !== ra) return rb - ra
      if (ra <= 0 && rb <= 0) {
        const av = hasLicenseVerifiedBadge(a) ? 1 : 0
        const bv = hasLicenseVerifiedBadge(b) ? 1 : 0
        if (bv !== av) return bv - av
        const aa = hasAvailabilityListed(a) ? 1 : 0
        const ba = hasAvailabilityListed(b) ? 1 : 0
        if (ba !== aa) return ba - aa
        return randomOrderValue(a) - randomOrderValue(b)
      }
      const byRating = (Number(b.rating) || 0) - (Number(a.rating) || 0)
      if (byRating !== 0) return byRating
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    }
    const byNameAsc = (a: ShowcaseItem, b: ShowcaseItem) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    const byNameDesc = (a: ShowcaseItem, b: ShowcaseItem) =>
      String(b.name || '').localeCompare(String(a.name || ''), undefined, { sensitivity: 'base' })

    const scored = allProviders.filter(
      (it) =>
        matchText(it) &&
        matchProfession(it) &&
        matchRole(it) &&
        matchState(it) &&
        matchRating(it)
    )

    const sorted = [...scored]
    if (sortBy === 'rating') sorted.sort(byHighestRated)
    else if (sortBy === 'reviews') sorted.sort(byMostReviews)
    else if (sortBy === 'name-asc') sorted.sort(byNameAsc)
    else if (sortBy === 'name-desc') sorted.sort(byNameDesc)
    else sorted.sort(byRecommended)
    return sorted
  }, [
    allProviders,
    query,
    profession,
    roleFilter,
    stateFilter,
    ratingFilter,
    sortBy,
  ])

  return (
    <section className="search-page container">
      {professionLabel ? <p className="search-selected-eyebrow">{professionLabel}</p> : null}

      {loading && <p className="muted">Searching professionals…</p>}
      {error && !loading && <div className="banner error">{error}</div>}
      {!loading && (
        <div className={`search-filters ${filtersOpen ? 'open' : ''}`.trim()}>
          <div className="search-filters-header">
            <button
              type="button"
              className="search-filters-toggle"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              aria-controls="search-filters-body"
            >
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
            </button>
            {filtersActive ? (
              <button type="button" className="search-filters-clear-inline" onClick={clearFilters}>
                Clear
              </button>
            ) : null}
          </div>
          <div className="search-filters-body" id="search-filters-body">
            <div className="search-filter-row">
              <label className="search-filter-group">
                <span className="search-filter-label">Role</span>
                <select
                  className="search-filter-input"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All roles</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
              <label className="search-filter-group">
                <span className="search-filter-label">State</span>
                <select
                  className="search-filter-input"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                >
                  <option value="">All states</option>
                  {STATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="search-filter-group">
                <span className="search-filter-label">Rating</span>
                <select
                  className="search-filter-input"
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                >
                  <option value="any">Any rating</option>
                  <option value="4.5">4.5+</option>
                  <option value="4.0">4.0+</option>
                </select>
              </label>
              <label className="search-filter-group sort">
                <span className="search-filter-label">Sort</span>
                <select
                  className="search-filter-input"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="recommended">Recommended</option>
                  <option value="rating">Highest rated</option>
                  <option value="reviews">Most reviews</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                </select>
              </label>
            </div>
            {filtersActive ? (
              <div className="search-filter-chips">
                {roleFilter ? <span className="search-filter-chip">Role: {roleFilter}</span> : null}
                {stateFilter ? <span className="search-filter-chip">State: {stateFilter}</span> : null}
                {ratingFilter !== 'any' ? (
                  <span className="search-filter-chip">Rating: {ratingFilter}+</span>
                ) : null}
                <button type="button" className="search-filter-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {!loading && !error && !filtered.length && (
        <p className="muted">No professionals match your filters.</p>
      )}
      <div className="search-grid">
        {!loading &&
          filtered.map((pro) => (
            <Link
              key={(pro.userId || pro.slug) + pro.profession}
              className="search-card"
              to={`/${(pro.slug || '').replace(/-/g, '')}${
                duplicateSearchSlugs.has((pro.slug || '').replace(/-/g, '')) && pro.stack
                  ? `?bio=${encodeURIComponent(pro.stack)}`
                  : ''
              }`}
              state={{ provider: pro }}
            >
              <div className="search-card-photo">
                <img src={pro.image || logoImage} alt={`${pro.name} — ${pro.profession}`} loading="lazy" />
                <span className="search-pill">{pro.profession}</span>
              </div>
              <div className="search-card-body">
                <div className="search-card-row">
                  <h3 className="provider-name-line">
                    <span>{pro.name}</span>
                    <LicenseVerifiedBadge item={pro} />
                  </h3>
                </div>
                {Number(pro.rating) > 0 && (
                  <button
                    type="button"
                    className="search-rating reviews-link"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onOpenReviews?.(pro)
                    }}
                  >
                    <span className="value">{Number(pro.rating).toFixed(1)}</span>
                    {renderStars(pro.rating)}
                    <span className="reviews-count">
                      {reviewCountLabel(pro.ratingCount)}
                    </span>
                  </button>
                )}
                {(() => {
                  const priceLabel =
                    pro.price && !pro.price.toLowerCase().includes('contact')
                      ? `${pro.price} (per 30 minutes)`
                      : pro.price
                  const meta = [shouldHideLocation(pro) ? null : pro.location, priceLabel]
                    .filter(Boolean)
                    .join(' · ')
                  return <p className="search-meta">{meta}</p>
                })()}
                <p className="search-specialty">
                  {(pro.specialties || []).filter(Boolean).slice(0, 4).join(' · ')}
                </p>
              </div>
            </Link>
          ))}
      </div>
    </section>
  )
}

function AiSearchPage({ onOpenReviews }: { onOpenReviews?: (pro: ShowcaseItem) => void }) {
  usePageMeta(
    'EUCA Match | AI Search',
    'Find verified professionals across borders. Select a country, describe your needs, and get matched with credential-verified professionals.',
    '/ai-search'
  )
  const [query, setQuery] = useState('')
  const [aiResult, setAiResult] = useState<any | null>(null)
  const [results, setResults] = useState<ShowcaseItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logId, setLogId] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const sessionIdRef = useRef(getAiSearchSessionId())
  const [stateHydrated, setStateHydrated] = useState(false)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const autoSearchQueryRef = useRef('')
  const [searchCountry, setSearchCountry] = useState('')
  const autoSearchCountryRef = useRef('')
  const activeSearchRunRef = useRef(0)
  const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim()
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [requestName, setRequestName] = useState('')
  const [requestEmail, setRequestEmail] = useState('')
  const [requestPhone, setRequestPhone] = useState('')
  const [requestDetails, setRequestDetails] = useState('')
  const [requestTurnstileToken, setRequestTurnstileToken] = useState('')
  const [requestSubmitting, setRequestSubmitting] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [requestSuccess, setRequestSuccess] = useState<{
    selectionId: string
    notifiedCount: number
    emailMasked: string
  } | null>(null)

  useEffect(() => {
    const saved = loadAiSearchState()
    if (saved) {
      setQuery(saved.query)
      setSearchCountry(saved.country || '')
      setAiResult(saved.aiResult)
      setResults(saved.results)
      setError(saved.error)
      setLogId(saved.logId)
      setHasSearched(saved.hasSearched)
    }
    setStateHydrated(true)
  }, [])

  useEffect(() => {
    if (!stateHydrated) return
    persistAiSearchState({
      query,
      searchCountry,
      aiResult,
      results,
      error,
      logId,
      hasSearched,
    })
  }, [query, searchCountry, aiResult, results, error, logId, hasSearched, stateHydrated])

  const resolveLabel = (value: string) =>
    industries.find((ind) => ind.value === value)?.label || 'Verified professionals'

  const runSearch = useCallback(
    async (text: string, country = searchCountry) => {
      const runId = activeSearchRunRef.current + 1
      activeSearchRunRef.current = runId
      const trimmed = text.trim()
      if (!trimmed) {
        setError('Tell us what you’re looking for to continue.')
        return
      }
      const normalizedCountry = normalizeCountryCode(country) || ''
      const countryLabel = normalizedCountry
        ? AI_SEARCH_COUNTRIES.find((item) => item.value === normalizedCountry)?.label || ''
        : ''
      const aiPrompt = [
        trimmed,
        countryLabel ? `Country needed: ${countryLabel}.` : '',
      ]
        .filter(Boolean)
        .join(' ')
      setQuery(trimmed)
      setShowRequestForm(false)
      setRequestError('')
      setRequestSuccess(null)
      setRequestTurnstileToken('')
      setHasSearched(true)
      setLoading(true)
      setError(null)
      try {
        const aiPromise = fetchAiSearch(aiPrompt, sessionIdRef.current, normalizedCountry).catch(() => null)
        let providers = await fetchSearchApi(trimmed, '', normalizedCountry, {
          attempts: 1,
          hydrateAvailability: false,
        })
        providers = dedupeAiProviders(providers)
        if (normalizedCountry) {
          providers = providers.filter((item) => matchesSearchCountry(item, normalizedCountry))
        }
        providers = rankAiSearchProviders(providers, trimmed)
        if (activeSearchRunRef.current !== runId) return
        setResults(providers)
        setLoading(false)
        if (providers.length < 6) {
          void (async () => {
            try {
              const broadened = await fetchSearchApi(trimmed, '', '', {
                attempts: 1,
                hydrateAvailability: false,
              })
              if (activeSearchRunRef.current !== runId) return
              let merged = dedupeAiProviders([...providers, ...broadened])
              merged = rankAiSearchProviders(merged, trimmed)
              if (normalizedCountry) {
                const inCountry = merged.filter((item) => matchesSearchCountry(item, normalizedCountry))
                const outOfCountry = merged.filter((item) => !matchesSearchCountry(item, normalizedCountry))
                merged = dedupeAiProviders([...inCountry, ...outOfCountry])
              }
              if (merged.length > providers.length) {
                setResults(merged)
              }
            } catch {
              // ignore fallback errors; primary fast results are already shown
            }
          })()
        }

        const inferred = inferPrimaryCategoryFromResults(providers)
        const ai = await aiPromise
        if (activeSearchRunRef.current !== runId) return
        const fallbackAiResult = {
          primary: inferred.primary || '',
          confidence: Number(inferred.confidence || 0) || 0,
          secondary: inferred.secondary || null,
          secondaryConfidence: Number(inferred.secondaryConfidence || 0) || null,
          keywords: [],
          providers: [],
          intents: [],
          rationale: '',
          logId: '',
        }
        if (!ai) {
          setAiResult(fallbackAiResult as any)
          return
        }
        setLogId(ai?.logId || '')
        const aiPrimary = String(ai?.primary || '').trim()
        const aiSecondary = String(ai?.secondary || '').trim()
        const nextPrimary = aiPrimary || inferred.primary || ''
        const nextSecondary =
          aiSecondary && aiSecondary !== nextPrimary
            ? aiSecondary
            : (inferred.secondary && inferred.secondary !== nextPrimary ? inferred.secondary : null)
        setAiResult({
          ...ai,
          primary: nextPrimary,
          confidence: Number.isFinite(Number(ai?.confidence))
            ? Number(ai.confidence)
            : Number(inferred.confidence || 0) || 0,
          secondary: nextSecondary,
          secondaryConfidence:
            nextSecondary === aiSecondary
              ? (ai?.secondaryConfidence ?? null)
              : (Number(inferred.secondaryConfidence || 0) || null),
        })
      } catch (e: any) {
        if (activeSearchRunRef.current !== runId) return
        setError(e?.message || 'AI search is unavailable right now.')
        setLoading(false)
      }
    },
    [searchCountry]
  )

  useEffect(() => {
    if (!stateHydrated) return
    const stateCountry = String((location.state as any)?.country || '').trim()
    const stateQuery = String((location.state as any)?.initialQuery || '').trim()
    const urlQuery = String(searchParams.get('q') || '').trim()
    const urlCountry = String(searchParams.get('country') || '').trim()
    const incomingCountry = normalizeCountryCode(urlCountry || stateCountry) || ''
    const incomingQuery = urlQuery || stateQuery
    if (!incomingQuery || (autoSearchQueryRef.current === incomingQuery && autoSearchCountryRef.current === incomingCountry)) {
      return
    }
    autoSearchQueryRef.current = incomingQuery
    autoSearchCountryRef.current = incomingCountry
    setQuery(incomingQuery)
    setSearchCountry(incomingCountry)
    void runSearch(incomingQuery, incomingCountry)
  }, [location.state, runSearch, searchParams, stateHydrated])

  const primaryLabel = aiResult?.primary ? resolveLabel(aiResult.primary) : ''
  const secondaryLabel = aiResult?.secondary ? resolveLabel(aiResult.secondary) : ''
  const duplicateSearchSlugs = React.useMemo(() => {
    const counts = new Map<string, number>()
    results.forEach((pro) => {
      if (!pro || !pro.slug) return
      const cleanSlug = String(pro.slug).replace(/-/g, '')
      counts.set(cleanSlug, (counts.get(cleanSlug) || 0) + 1)
    })
    return new Set(Array.from(counts.entries()).filter(([, c]) => c > 1).map(([slug]) => slug))
  }, [results])
  const showCompactSearch = hasSearched && (!!aiResult || results.length > 0 || !!error)
  const topMatches = results.slice(0, 3)
  const nextMatches = results.slice(3, 6)
  const remainingMatchCount = Math.max(0, results.length - 6)
  const normalizeStackKey = (value: unknown) => {
    const key = String(value || '').trim().toLowerCase()
    return key && STACK_API_BASES[key] ? key : ''
  }
  const requestStack = (() => {
    const fromAi = normalizeStackKey(aiResult?.primary)
    if (fromAi) return fromAi
    for (const candidate of topMatches) {
      const key = normalizeStackKey(resolveShowcaseStack(candidate))
      if (key) return key
    }
    return ''
  })()
  const requestStackLabel = requestStack ? STACK_TITLE_MAP[requestStack] || 'professionals' : 'professionals'
  const requestApiBase = requestStack ? STACK_API_BASES[requestStack] || '' : ''
  const requestPicks = (() => {
    const picks: Array<{ specialistId: string; slotISO: string }> = []
    const seen = new Set<string>()
    for (const pro of topMatches) {
      const specialistId = String(pro.userId || '').trim()
      if (!specialistId || seen.has(specialistId)) continue
      const slots = Array.isArray(pro.availabilitySlots) ? pro.availabilitySlots : []
      const slotISO = slots.find((slot) => typeof slot === 'string' && slot.trim() && slot !== CONTACT_SLOT) || CONTACT_SLOT
      picks.push({ specialistId, slotISO })
      seen.add(specialistId)
    }
    return picks
  })()
  const requestFollowupQueuePicks = (() => {
    const picks: Array<{ specialistId: string; slotISO: string }> = []
    const seen = new Set(requestPicks.map((pick) => pick.specialistId))
    for (const pro of results.slice(3, 12)) {
      const specialistId = String(pro.userId || '').trim()
      if (!specialistId || seen.has(specialistId)) continue
      const slots = Array.isArray(pro.availabilitySlots) ? pro.availabilitySlots : []
      const slotISO = slots.find((slot) => typeof slot === 'string' && slot.trim() && slot !== CONTACT_SLOT) || CONTACT_SLOT
      picks.push({ specialistId, slotISO })
      seen.add(specialistId)
      if (picks.length >= 9) break
    }
    return picks
  })()
  const maskEmail = (value: string) => {
    const trimmed = String(value || '').trim().toLowerCase()
    const [local, domain] = trimmed.split('@')
    if (!local || !domain) return trimmed
    if (local.length <= 1) return `*@${domain}`
    return `${local[0]}***@${domain}`
  }
  const submitTopRequest = async () => {
    if (requestSubmitting) return
    setRequestError('')
    setRequestSuccess(null)
    const cleanName = requestName.trim()
    const cleanEmail = requestEmail.trim().toLowerCase()
    const cleanPhone = requestPhone.trim()
    const detailText = requestDetails.trim()
    const reasonText = detailText || query.trim()
    const specialtyText = query.trim() || requestStackLabel

    if (!requestApiBase || !requestStack) {
      setRequestError('Unable to resolve the matching profession for this request.')
      return
    }
    if (requestPicks.length < 1) {
      setRequestError('No suitable professionals are available right now. Please refine your search.')
      return
    }
    if (!cleanName) {
      setRequestError('Please enter your name.')
      return
    }
    if (!SIMPLE_EMAIL_REGEX.test(cleanEmail)) {
      setRequestError('Please enter a valid email address.')
      return
    }
    if (!reasonText) {
      setRequestError('Please add a short request detail so professionals know what you need.')
      return
    }
    if (!turnstileSiteKey) {
      setRequestError('Verification is currently unavailable. Please try again shortly.')
      return
    }
    if (!requestTurnstileToken) {
      setRequestError('Please complete verification before submitting.')
      return
    }

    const nameParts = cleanName.split(/\s+/).filter(Boolean)
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || 'Client'
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney'

    setRequestSubmitting(true)
    try {
      const payload = {
        firstName,
        lastName,
        email: cleanEmail,
        phone: cleanPhone || undefined,
        specialty: specialtyText,
        reason: reasonText,
        picks: requestPicks,
        followupQueuePicks: requestFollowupQueuePicks,
        autoNotifyTop: false,
        clientTimeZone,
        turnstileToken: requestTurnstileToken,
      }
      const res = await postJsonWithRetry(
        `${requestApiBase}/public/requests/guest`,
        payload,
        'submit request',
        2
      )
      const selectionId = String(res?.selectionId || '')
      const notifiedCount = Number(res?.notifiedCount || requestPicks.length || 0)
      setRequestSuccess({
        selectionId,
        notifiedCount,
        emailMasked: maskEmail(cleanEmail),
      })
      setRequestTurnstileToken('')
      setRequestDetails('')
    } catch (submitErr: any) {
      setRequestError(submitErr?.message || 'Failed to send request')
      setRequestTurnstileToken('')
    } finally {
      setRequestSubmitting(false)
    }
  }
  const renderSearchCard = (pro: ShowcaseItem) => (
    <Link
      key={`${pro.userId || pro.slug || pro.name}:${pro.profession}`}
      className="search-card"
      to={`/${(pro.slug || '').replace(/-/g, '')}${
        duplicateSearchSlugs.has((pro.slug || '').replace(/-/g, '')) && pro.stack
          ? `?bio=${encodeURIComponent(pro.stack)}`
          : ''
      }`}
      state={{ provider: pro }}
      onClick={() => {
        if (logId) void logAiSearchClick(logId, pro.userId || pro.slug || '')
      }}
    >
      <div className="search-card-photo">
        <img src={pro.image || logoImage} alt={`${pro.name} — ${pro.profession}`} loading="lazy" />
        <span className="search-pill">{pro.profession}</span>
      </div>
      <div className="search-card-body">
        <div className="search-card-row">
          <h3 className="provider-name-line">
            <span>{pro.name}</span>
            <LicenseVerifiedBadge item={pro} />
          </h3>
        </div>
        {Number(pro.rating) > 0 && (
          <button
            type="button"
            className="search-rating reviews-link"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onOpenReviews?.(pro)
            }}
          >
            <span className="value">{Number(pro.rating).toFixed(1)}</span>
            {renderStars(pro.rating)}
            <span className="reviews-count">{reviewCountLabel(pro.ratingCount)}</span>
          </button>
        )}
        {(() => {
          const priceLabel =
            pro.price && !pro.price.toLowerCase().includes('contact')
              ? `${pro.price} (per 30 minutes)`
              : pro.price
          const meta = [shouldHideLocation(pro) ? null : pro.location, priceLabel]
            .filter(Boolean)
            .join(' · ')
          return <p className="search-meta">{meta}</p>
        })()}
        <p className="search-specialty">
          {(pro.specialties || []).filter(Boolean).slice(0, 4).join(' · ')}
        </p>
      </div>
    </Link>
  )

  return (
    <section className="ai-search-page container">
      {showCompactSearch && (
        <div className="ai-search-hero ai-search-hero-compact">
          <p className="ai-search-eyebrow">Refine your search</p>
          <form
            className="ai-search-compact-form"
            onSubmit={(e) => {
              e.preventDefault()
              runSearch(query, searchCountry)
            }}
          >
            <input
              type="search"
              className="ai-search-input ai-search-input-compact"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Update your request"
            />
            <div className="ai-search-compact-options">
              <select
                className="ai-search-country-select"
                value={searchCountry}
                onChange={(e) => setSearchCountry(e.target.value)}
                aria-label="Select country"
              >
                <option value="">Country</option>
                {AI_SEARCH_COUNTRIES.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
              <button className="btn cta cta-green" type="submit" disabled={loading}>
                {loading ? 'Searching…' : 'Search again'}
              </button>
            </div>
          </form>
          {loading && <span className="muted">Matching verified professionals…</span>}
        </div>
      )}

      {!showCompactSearch && (
        <div className="ai-search-hero">
          <p className="ai-search-eyebrow">AI Search</p>
          <h1 className="ai-search-title">Find Verified Professionals Across Borders</h1>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              runSearch(query, searchCountry)
            }}
          >
            <div className="ai-search-guided-grid">
              <label className="ai-search-guided-field">
                <span>Country needed</span>
                <select
                  className="ai-search-country-select"
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  aria-label="Select country"
                >
                  <option value="">Country</option>
                  {AI_SEARCH_COUNTRIES.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="ai-search-guided-field ai-search-guided-field--full">
              <span>What do you need help with?</span>
              <textarea
                className="ai-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type what you need in simple words."
              />
            </label>
            <div className="ai-search-actions">
              <button className="btn cta cta-green" type="submit" disabled={loading}>
                {loading ? 'Searching…' : 'Search'}
              </button>
              {loading && <span className="muted">Matching verified professionals…</span>}
            </div>
          </form>
        </div>
      )}

      {error && !loading && <div className="banner error">{error}</div>}

      {aiResult && (
        <div className="ai-search-summary">
          <div className="ai-search-summary-card">
            <p className="eyebrow">Primary recommendation</p>
            <h3>{primaryLabel}</h3>
            <p className="muted">Best-matched verified professionals for your request.</p>
            {aiResult.primary && (
              <Link className="btn ghost" to={`/search?profession=${aiResult.primary}`}>
                View all {primaryLabel}
              </Link>
            )}
          </div>
          {aiResult.secondary && (
            <div className="ai-search-summary-card secondary">
              <p className="eyebrow">You may also need</p>
              <h3>{secondaryLabel}</h3>
              <p className="muted">Use this when your request spans more than one service.</p>
              <Link className="btn ghost" to={`/search?profession=${aiResult.secondary}`}>
                View {secondaryLabel}
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="ai-search-results">
        {!loading && topMatches.length > 0 && (
          <section className="ai-search-request-panel">
            <div className="ai-search-request-head">
              <h2>Ready to notify the 3 most suitable professionals?</h2>
              <p>
                We send your request to the top matches only. All conversations stay in EUCA Match chat after login.
              </p>
            </div>
            {!showRequestForm && !requestSuccess && (
              <button
                type="button"
                className="btn cta cta-green"
                onClick={() => {
                  setShowRequestForm(true)
                  setRequestError('')
                  if (!requestDetails.trim()) setRequestDetails(query.trim())
                }}
              >
                Send request to 3 most suitable professionals
              </button>
            )}
            {showRequestForm && !requestSuccess && (
              <div className="ai-search-request-form">
                {requestError ? (
                  <div className="banner error" role="alert">
                    {requestError}
                  </div>
                ) : null}
                <div className="ai-search-request-grid">
                  <label className="ai-search-request-field">
                    <span className="ai-search-request-field-label">
                      Name <span className="ai-search-required">*</span>
                    </span>
                    <input
                      className="ai-search-request-input"
                      value={requestName}
                      onChange={(e) => setRequestName(e.target.value)}
                      autoComplete="name"
                      placeholder="Your full name"
                      disabled={requestSubmitting}
                    />
                  </label>
                  <label className="ai-search-request-field">
                    <span className="ai-search-request-field-label">
                      Email <span className="ai-search-required">*</span>
                    </span>
                    <input
                      type="email"
                      className="ai-search-request-input"
                      value={requestEmail}
                      onChange={(e) => setRequestEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@example.com"
                      disabled={requestSubmitting}
                    />
                  </label>
                  <label className="ai-search-request-field">
                    <span className="ai-search-request-field-label">Phone (optional)</span>
                    <input
                      className="ai-search-request-input"
                      value={requestPhone}
                      onChange={(e) => setRequestPhone(e.target.value)}
                      autoComplete="tel"
                      placeholder="+61..."
                      disabled={requestSubmitting}
                    />
                  </label>
                  <label className="ai-search-request-field ai-search-request-field--wide">
                    <span className="ai-search-request-field-label">Request details</span>
                    <textarea
                      className="ai-search-request-input ai-search-request-input--textarea"
                      value={requestDetails}
                      onChange={(e) => setRequestDetails(e.target.value)}
                      placeholder="Describe what you need in simple words."
                      rows={4}
                      disabled={requestSubmitting}
                    />
                  </label>
                </div>
                <p className="muted ai-search-request-note">
                  {`This will notify ${requestPicks.length} ${requestStackLabel.toLowerCase()} now.`}
                </p>
                {turnstileSiteKey ? (
                  <div className="ai-search-request-turnstile">
                    <Turnstile
                      siteKey={turnstileSiteKey}
                      action="guest_request"
                      onToken={(token) => {
                        setRequestTurnstileToken(token || '')
                      }}
                    />
                  </div>
                ) : null}
                <div className="ai-search-request-actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setShowRequestForm(false)
                      setRequestError('')
                      setRequestTurnstileToken('')
                    }}
                    disabled={requestSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn cta cta-green"
                    onClick={submitTopRequest}
                    disabled={requestSubmitting}
                  >
                    {requestSubmitting ? 'Sending request…' : 'Send request now'}
                  </button>
                </div>
              </div>
            )}
            {requestSuccess ? (
              <div className="ai-search-request-success">
                <div className="ai-search-request-success-title">
                  <CheckCircle2 size={20} aria-hidden />
                  <span>Request sent</span>
                </div>
                <p>
                  {`We notified ${requestSuccess.notifiedCount} professionals and emailed a secure login link to ${requestSuccess.emailMasked}.`}
                </p>
                <p className="muted">
                  Open your email, log in, and continue the conversation in the in-app chat.
                </p>
                {requestSuccess.selectionId ? (
                  <p className="muted ai-search-request-id">Reference: {requestSuccess.selectionId}</p>
                ) : null}
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    setRequestSuccess(null)
                    setShowRequestForm(false)
                    setRequestTurnstileToken('')
                  }}
                >
                  Send another request
                </button>
              </div>
            ) : null}
          </section>
        )}
        {!loading && hasSearched && !results.length && !error && (
          <p className="ai-search-empty">No professionals found yet in this category.</p>
        )}
        {!loading && topMatches.length > 0 && (
          <section className="ai-search-group ai-search-group-top">
            <header className="ai-search-group-head">
              <h2>3 most suitable professionals</h2>
            </header>
            <div className="search-grid">
              {topMatches.map((pro) => renderSearchCard(pro))}
            </div>
          </section>
        )}
        {!loading && nextMatches.length > 0 && (
          <section className="ai-search-group ai-search-group-next">
            <header className="ai-search-group-head">
              <h2>Other suitable professionals</h2>
            </header>
            <div className="search-grid">
              {nextMatches.map((pro) => renderSearchCard(pro))}
            </div>
          </section>
        )}
        {!loading && remainingMatchCount > 0 && (
          <p className="ai-search-more-hint">
            {remainingMatchCount} more professionals are available in this category.
          </p>
        )}
      </div>
    </section>
  )
}

function ContactPage({ clientSignupUrl }: { clientSignupUrl: string }) {
  usePageMeta(
    'EUCA Match | Contact',
    'Contact EUCA Match for support, questions, or partnership enquiries. Email contact@eucamatch.com.',
    '/contact'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
    ],
  }
  const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [formSuccess, setFormSuccess] = useState('')

  const submitContact = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError('')
    setFormSuccess('')

    const cleanName = name.trim()
    const cleanEmail = email.trim().toLowerCase()
    const cleanMessage = message.trim()
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)

    if (!cleanName) {
      setFormError('Please enter your name.')
      return
    }
    if (!emailValid) {
      setFormError('Please enter a valid email address.')
      return
    }
    if (!turnstileSiteKey) {
      setFormError('Verification is not available right now. Please email contact@eucamatch.com.')
      return
    }
    if (!turnstileToken) {
      setFormError('Please complete the verification check.')
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch(`${SEARCH_PUBLIC_API}/public/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: cleanName,
          email: cleanEmail,
          message: cleanMessage,
          turnstileToken,
        }),
      })
      const raw = await res.text()
      let json: any = {}
      if (raw) {
        try {
          json = JSON.parse(raw)
        } catch {
          json = {}
        }
      }
      if (!res.ok) {
        throw new Error(json?.error || 'Unable to send your message right now.')
      }
      setName('')
      setEmail('')
      setMessage('')
      setTurnstileToken('')
      setFormSuccess('Thanks, your message has been sent. We will get back to you soon.')
    } catch (err: any) {
      setFormError(err?.message || 'Unable to send your message right now.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="contact-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="contact-split">
        <p className="contact-eyebrow">Contact</p>
        <div className="about-card contact-card-panel">
          <p>Have a question or need support? Send us a message and our team will get back to you.</p>
          <form className="contact-form" onSubmit={submitContact}>
            <div className="contact-form-grid">
              <label className="contact-field">
                <span>Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </label>
              <label className="contact-field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>
            </div>
            <label className="contact-field">
              <span>Message</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="How can we help?"
              />
            </label>
            {turnstileSiteKey ? (
              <div className="contact-turnstile">
                <Turnstile
                  siteKey={turnstileSiteKey}
                  action="contact_form"
                  onToken={setTurnstileToken}
                  theme="auto"
                  size="flexible"
                />
              </div>
            ) : (
              <p className="contact-form-note">
                Verification is currently unavailable. You can email us directly at{' '}
                <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>.
              </p>
            )}
            {formError ? (
              <p className="contact-form-error" role="alert">{formError}</p>
            ) : null}
            {formSuccess ? (
              <p className="contact-form-success" role="status">{formSuccess}</p>
            ) : null}
            <button className="btn cta" type="submit" disabled={submitting || !turnstileSiteKey}>
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

function ProfessionalsPage() {
  return <PreviewProfessionalsPage />
}

function PreviewProfessionalsPage() {
  usePageMeta(
    'EUCA Match for Professionals',
    'How professionals join EUCA Match, get verified, and connect with clients across regulated industries.',
    '/professionals',
    professionalsHeroImage
  )

  useManagedToolVideos('.professionals-page .pro-tool-video')

  return (
    <section className="professionals-page">
      <div className="pro-hero-shell pro-reveal">
        <div className="clients-hero-grid clients-theme-1">
          <div className="clients-hero-copy">
            <p className="eyebrow hero-eyebrow">For Professionals</p>
            <h1>Verify once. Accept clients. Embedded workflows.</h1>
            <p className="clients-lede">
              Sign up, get verified, and accept client requests when it suits you. Then use embedded workflows for{' '}
              <strong>both EUCA Match clients and your existing clients</strong> — accept payments, generate
              invoices, sync calendars, and manage client notes in one place.
            </p>
            <div className="pro-kpis">
              <article className="pro-kpi">
                <p>Approval model</p>
                <strong>Verification-first</strong>
              </article>
              <article className="pro-kpi">
                <p>Client intake</p>
                <strong>Accept when it suits you</strong>
              </article>
              <article className="pro-kpi">
                <p>Embedded workflows</p>
                <strong>Payments, calendar, notes</strong>
              </article>
            </div>
            <div className="clients-hero-media mobile-only" aria-hidden="true">
              <img className="clients-hero-img" src={professionalsHeroImage} alt="EUCA Match professional experience preview" loading="lazy" />
            </div>
            <div className="clients-cta-row">
              <a className="btn cta pro-cta" href={providerSignupUrl}>
                For Professionals
              </a>
              <a className="btn cta ghost" href="/verification">
                How verification works
              </a>
            </div>
          </div>
          <div className="clients-hero-media desktop-only pro-hero-media-wrap" aria-hidden="true">
            <img className="clients-hero-img" src={professionalsHeroImage} alt="EUCA Match professional experience preview" loading="lazy" />
          </div>
        </div>
      </div>

      <div className="pro-journey-grid pro-reveal">
        <article className="pro-journey-card">
          <p className="pro-journey-step">01</p>
          <h3>Create your profile</h3>
          <p>Choose your profession, add credentials, and publish your service details once.</p>
        </article>
        <article className="pro-journey-card">
          <p className="pro-journey-step">02</p>
          <h3>Get verified</h3>
          <p>Our team reviews your registration, identity checks, and profile information.</p>
        </article>
        <article className="pro-journey-card">
          <p className="pro-journey-step">03</p>
          <h3>Run your workflow</h3>
          <p>Accept requests and manage payments, calendar, and notes from one place.</p>
        </article>
      </div>

      <div className="pro-tools pro-reveal">
        <div className="pro-tools-intro">
          <p className="eyebrow pro-tools-eyebrow">Embedded Workflows</p>
          <h2>Everything you need after approval.</h2>
          <p>
            Built for real day-to-day work across multiple professions. Keep one system for incoming EUCA Match clients and
            your existing clients.
          </p>
        </div>
        <div className="pro-tools-features">
          <section className="pro-tool pro-reveal">
            <div className="pro-tool-text">
              <p className="pro-tool-badge">01 · Payments</p>
              <h3>Secure Online Payments & Automatic Invoicing</h3>
              <div className="pro-tool-points">
                <p>
                  <strong>Built-in, Stripe-powered payments.</strong> EUCA Match supports secure online credit card payments through Stripe,
                  fully integrated into the platform.
                </p>
                <p>
                  <strong>Automatic invoices, every time.</strong> Invoices are generated instantly for each successful payment — no manual
                  work required.
                </p>
                <p>
                  <strong>Transparent pricing.</strong> Standard Stripe processing fees apply (1.7% + $0.30 per transaction).
                </p>
                <p>
                  <strong>No platform fees.</strong> EUCA Match does not charge any additional payment or service fees.
                </p>
              </div>
            </div>
            <div className="pro-tool-media" aria-label="Payments demo video">
              <video className="pro-tool-video" controls playsInline muted preload="none" poster={invoiceVideoPoster}>
                <source src={invoiceVideoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
          <section className="pro-tool is-flipped pro-reveal">
            <div className="pro-tool-text">
              <p className="pro-tool-badge">02 · Scheduling</p>
              <h3>Calendar Sync That Just Works</h3>
              <div className="pro-tool-points">
                <p>
                  <strong>Google Calendar and Calendly, synced.</strong> EUCA Match connects with your Google Calendar and Calendly to keep
                  availability up to date automatically.
                </p>
                <p>
                  <strong>Busy times blocked.</strong> Existing events from both calendars are reflected in EUCA Match, so clients can’t
                  book when you’re unavailable.
                </p>
                <p>
                  <strong>Real-time updates.</strong> New bookings made through EUCA Match sync back instantly to your connected calendars.
                </p>
                <p>
                  <strong>Shareable availability.</strong> Send booking links or availability by email or message, directly from your
                  calendar.
                </p>
              </div>
            </div>
            <div className="pro-tool-media" aria-label="Calendar sync demo video">
              <video className="pro-tool-video" controls playsInline muted preload="none" poster={calendarVideoPoster}>
                <source src={calendarVideoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
          <section className="pro-tool pro-reveal">
            <div className="pro-tool-text">
              <p className="pro-tool-badge">03 · Client Records</p>
              <h3>Notes for Every Booking</h3>
              <div className="pro-tool-points">
                <p>
                  <strong>Structured booking notes.</strong> Write, save, and publish notes for each booking, keeping records clear and
                  organised.
                </p>
                <p>
                  <strong>Files and attachments.</strong> Upload images and documents as needed, all stored with the relevant booking.
                </p>
                <p>
                  <strong>AI-assisted drafting by profession.</strong> Type a few phrases and EUCA Match drafts a polished note using
                  language and structure suited to your profession.
                </p>
                <p>
                  <strong>AI summary of notes.</strong> Generate a succinct workflow-ready summary from your full notes to support
                  follow-ups, handovers, and faster review.
                </p>
              </div>
            </div>
            <div className="pro-tool-media" aria-label="Notes demo video placeholder">
              <video className="pro-tool-video" controls playsInline muted preload="none" poster={notesVideoPoster}>
                <source src={notesVideoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
          <section className="pro-tool is-flipped pro-reveal">
            <div className="pro-tool-text">
              <p className="pro-tool-badge">04 · Video Meetings</p>
              <h3>Zoom + Google Meet Links for Accepted Bookings</h3>
              <div className="pro-tool-points">
                <p>
                  <strong>Zoom and Google Meet integrations.</strong> Create and attach video meeting links for accepted bookings
                  directly inside EUCA Match.
                </p>
                <p>
                  <strong>Manual send when needed.</strong> Share meeting links yourself when you want direct coordination with the
                  client.
                </p>
                <p>
                  <strong>Automatic link delivery.</strong> Configure supported booking flows to send meeting links automatically.
                </p>
                <p>
                  <strong>Booking-linked meeting details.</strong> Keep meeting information tied to each booking for clearer
                  scheduling and follow-up.
                </p>
              </div>
            </div>
            <div className="pro-tool-media" aria-label="Video meeting links demo video">
              <video className="pro-tool-video" controls playsInline muted preload="none" poster={meetingVideoPoster}>
                <source src={meetingVideoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
        </div>
      </div>
    </section>
  )
}

function VerificationPage() {
  usePageMeta(
    'EUCA Match | Verification Standards',
    'How EUCA Match verifies professionals before they are listed and can receive booking requests.',
    '/verification'
  )
  return (
    <section className="verification-page">
      <div className="verification-card">
        <p className="eyebrow verification-eyebrow">Verification</p>
        <h1>How We Verify Professionals</h1>
        <p>EUCA Match is a verification-first marketplace.</p>
        <p>Every professional listed on the platform is reviewed before their profile becomes visible or they can receive booking requests.</p>
        <p>Our verification process focuses on credentials, identity, and professional standing, so clients can book with confidence.</p>
        <h3>1. Active Practising Licence</h3>
        <p>Licensed professionals must hold an active practising licence in their field.</p>
        <p>We verify registration status against authoritative public databases, including:</p>
        <ul>
          <li>Professional registration boards</li>
          <li>Licensing authorities</li>
          <li>Government or statutory registers</li>
        </ul>
        <p>Only professionals with current, valid registration are approved.</p>
        <h3>2. Qualifications & Education</h3>
        <p>We review declared educational qualifications, which may include:</p>
        <ul>
          <li>Degrees</li>
          <li>Professional training</li>
          <li>Specialist certifications (where applicable)</li>
        </ul>
        <p>These are assessed for consistency with:</p>
        <ul>
          <li>The professional’s registered scope of practice</li>
          <li>The services they offer on EUCA Match</li>
        </ul>
        <h3>3. Professional Experience (CV Review)</h3>
        <p>Each applicant submits a curriculum vitae (CV) outlining:</p>
        <ul>
          <li>Professional background</li>
          <li>Areas of practice</li>
          <li>Relevant experience</li>
        </ul>
        <p>We review this to ensure profiles are accurate, coherent, and appropriately represented.</p>
        <h3>4. Identity Verification</h3>
        <p>Where appropriate, we may request government-issued photo identification as part of the verification process.</p>
        <p>Identity checks are used to:</p>
        <ul>
          <li>Confirm that the individual listed is the person offering the service</li>
          <li>Reduce the risk of false, duplicate, or third-party profiles</li>
        </ul>
        <p>The level of identity verification may vary depending on:</p>
        <ul>
          <li>The professional category</li>
          <li>Regulatory requirements</li>
          <li>Information available through public registers</li>
        </ul>
        <p>For many licensed professions, identity can be confirmed through official registration databases.</p>
        <p>In some cases, additional identity documentation may be requested to support verification.</p>
        <h3>Ongoing Accuracy & Review</h3>
        <p>Professionals are responsible for maintaining up-to-date registration and profile information.</p>
        <p>EUCA Match may:</p>
        <ul>
          <li>Re-verify credentials periodically</li>
          <li>Review profiles if concerns are raised</li>
          <li>Suspend or remove profiles if verification can no longer be confirmed</li>
        </ul>
        <h3>What Verification Means — and What It Doesn’t</h3>
        <p>Verification confirms that a professional:</p>
        <ul>
          <li>Is a real person</li>
          <li>Holds valid credentials and registration</li>
          <li>Has been reviewed before listing</li>
        </ul>
        <p>Verification confirms credentials at the time of review and does not constitute regulatory oversight, supervision, or professional accreditation.</p>
        <p>Verification does not:</p>
        <ul>
          <li>Endorse professional outcomes or performance</li>
          <li>Replace a client’s independent judgment</li>
          <li>Guarantee availability or suitability for every request</li>
        </ul>
        <p>Verification standards may vary by jurisdiction and professional category.</p>
        <h3>Why Verification Matters</h3>
        <p>Clients should not have to:</p>
        <ul>
          <li>Check licence numbers</li>
          <li>Search multiple regulatory registers</li>
          <li>Assess credibility from incomplete or inconsistent profiles</li>
        </ul>
        <p>Verification allows clients to focus on choosing the right professional, not validating credentials.</p>
        <div className="verification-disclaimer">
          <h3>Legal Disclaimer</h3>
          <p>
            EUCA Match verifies professional credentials based on information available at the time of review. Verification does not constitute regulatory oversight, professional accreditation,
            endorsement, or supervision, and does not replace a client’s independent assessment of suitability.
          </p>
        </div>
      </div>
    </section>
  )
}

function FAQPage() {
  const supportedRegionsText =
    'Australia, New Zealand, United States (USA), Canada, United Kingdom (UK), Ireland, Singapore, and Hong Kong.'
  usePageMeta(
    'EUCA Match | Frequently Asked Questions',
    `FAQ for clients and professionals using EUCA Match across ${supportedRegionsText}`,
    '/faq'
  )
  const renderAnswerBlock = (block, idx) => {
    if (block.type === 'cta') {
      const classes = block.variant === 'secondary' ? 'btn cta pro-cta' : 'btn cta'
      return (
        <p key={idx}>
          <a className={classes} href={block.href}>
            {block.label}
          </a>
        </p>
      )
    }
    if (block.type === 'list') {
      return (
        <ul key={idx}>
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )
    }
    if (block.type === 'link') {
      return (
        <p key={idx}>
          <a href={block.href}>{block.href}</a>
        </p>
      )
    }
    return <p key={idx}>{block.text}</p>
  }

  const clientFaq = [
    {
      q: 'What is EUCA Match?',
      a: [
        { type: 'p', text: 'EUCA Match is a verification-first professional marketplace that connects clients with verified, qualified professionals across multiple regulated industries.' },
        { type: 'p', text: 'The platform is designed to match clients with verified professionals, ensuring credentials are checked before any professional can receive requests or appear on the platform.' },
        { type: 'p', text: `EUCA Match currently supports clients and professionals in ${supportedRegionsText}` },
      ],
    },
    {
      q: 'Which countries and regions does EUCA Match currently support?',
      a: [{ type: 'p', text: supportedRegionsText }],
    },
    {
      q: 'What types of professionals are on EUCA Match?',
      a: [
        { type: 'p', text: 'EUCA Match supports professionals across multiple regulated industries, including:' },
        {
          type: 'list',
          items: [
            'Legal, Governance & Compliance',
            'Medical Doctors',
            'Allied Health',
            'Education & Tutoring',
            'Financial Services',
            'Migration & Visa',
            'Property, Design & Building',
            'Tech, Creative & Media',
          ],
        },
      ],
    },
    {
      q: 'How do I request a professional?',
      a: [
        { type: 'p', text: 'You can:' },
        {
          type: 'list',
          items: [
            'Browse verified professionals by industry',
            'Select a verified professional',
            'Submit your request with a brief description of what you need',
          ],
        },
        { type: 'p', text: 'Clients can submit a request to a verified professional and discuss details after acceptance before deciding how to proceed.' },
      ],
    },
    {
      q: 'What does it cost to use EUCA Match?',
      a: [
        { type: 'p', text: 'EUCA Match is free for clients to submit and manage booking requests.' },
        { type: 'p', text: 'All professional service fees are discussed and agreed directly with the professional after a match is accepted.' },
      ],
    },
    {
      q: 'Do I pay EUCA Match or the professional?',
      a: [
        { type: 'p', text: 'Clients do not pay EUCA Match a booking fee.' },
        { type: 'p', text: 'All consultation fees, service charges, and professional costs are paid directly to the professional, in line with how they normally operate.' },
      ],
    },
    {
      q: 'Are professionals employed by EUCA Match?',
      a: [
        { type: 'p', text: 'No.' },
        { type: 'p', text: 'All professionals listed on EUCA Match are independent practitioners or businesses. EUCA Match does not employ professionals or provide professional services.' },
      ],
    },
    {
      q: 'How do I clarify whether a professional offers online or in-person services?',
      a: [
        { type: 'p', text: 'Service format varies by professional.' },
        { type: 'p', text: 'Once a match is accepted, clients are encouraged to contact the professional directly to confirm:' },
        {
          type: 'list',
          items: [
            'Whether services are provided online, in person, or both',
            'Location, availability, and scope of services',
          ],
        },
      ],
    },
    {
      q: 'What happens after a match is accepted?',
      a: [
        { type: 'p', text: 'Once a professional accepts your request:' },
        {
          type: 'list',
          items: [
            'You both receive each other’s contact details',
            'You communicate directly to confirm scope, fees, timing, and format',
            'The engagement proceeds according to the professional’s usual practice',
          ],
        },
      ],
    },
    {
      q: 'How do I get started as a client?',
      a: [
        { type: 'p', text: 'You can get started in two ways:' },
        { type: 'p', text: 'Browse without logging in' },
        {
          type: 'list',
          items: [
            'You can browse professional profiles directly on the EUCA Match homepage',
            'You can also use the search bar to search for professionals by industry or expertise',
            'No login is required to browse profiles',
          ],
        },
        { type: 'p', text: 'Use the EUCA Match web app' },
        {
          type: 'list',
          items: [
            'To submit a request and match with a professional, use the EUCA Match web app',
            'Sign in using Google, Apple, or your email address',
            'Works on both desktop and mobile devices',
          ],
        },
        { type: 'cta', href: marketingSearchUrl, label: 'Find Verified Professionals' },
        { type: 'p', text: 'Browsing is open to everyone.' },
        { type: 'p', text: 'Creating a request requires signing in so we can support secure matching and communication.' },
      ],
    },
  ]

  const proFaq = [
    {
      q: 'What is EUCA Match for professionals?',
      a: [
        { type: 'p', text: 'EUCA Match is a matching and verification platform, not an employer, agency, or managed service.' },
        { type: 'p', text: `EUCA Match currently supports clients and professionals in ${supportedRegionsText}` },
        { type: 'p', text: 'It provides:' },
        {
          type: 'list',
          items: [
            'A public, credential-verified professional profile',
            'A shareable booking link you can send to clients',
            'An optional live availability calendar',
            'Client discovery across industries',
            'Full control over how you operate your practice',
          ],
        },
      ],
    },
    {
      q: 'Which countries and regions does EUCA Match currently support?',
      a: [{ type: 'p', text: supportedRegionsText }],
    },
    {
      q: 'How does remuneration work?',
      a: [
        { type: 'p', text: 'EUCA Match:' },
        {
          type: 'list',
          items: [
            'Does not set your fees',
            'Does not handle your payments',
            'Does not take a percentage or commission',
          ],
        },
        { type: 'p', text: 'Once a request is accepted, you and the client agree directly on fees, scope of work, and engagement terms, in line with your usual practice.' },
      ],
    },
    {
      q: 'Does EUCA Match take a cut of my fees?',
      a: [
        { type: 'p', text: 'No.' },
        { type: 'p', text: 'EUCA Match does not take a percentage of professional fees.' },
      ],
    },
    {
      q: 'How do clients engage with me?',
      a: [
        { type: 'p', text: 'Each professional has a public, verified profile that can be:' },
        {
          type: 'list',
          items: [
            'Discovered through EUCA Match',
            'Shared directly with clients via a shareable booking link',
          ],
        },
        { type: 'p', text: 'Professionals may also choose to enable an optional live availability calendar to indicate when they are open to receiving requests.' },
        { type: 'p', text: 'Enabling a calendar is optional and does not affect your ability to receive or accept requests.' },
      ],
    },
    {
      q: 'Does EUCA Match manage my practice, workflow, or bookings?',
      a: [
        { type: 'p', text: 'No.' },
        { type: 'p', text: 'EUCA Match does not replace your existing practice structure, workflows, or systems.' },
        { type: 'p', text: 'Most professionals use EUCA Match for discovery and initial contact, then manage engagements through their usual processes.' },
      ],
    },
    {
      q: 'What verification is required?',
      a: [
        { type: 'p', text: 'Verification standards vary by profession and may include:' },
        {
          type: 'list',
          items: [
            'Identity verification',
            'Registration or licensing checks',
            'Qualification and experience review',
          ],
        },
        { type: 'p', text: 'Only professionals who pass verification are listed on the platform.' },
      ],
    },
    {
      q: 'Am I required to accept requests?',
      a: [
        { type: 'p', text: 'No.' },
        { type: 'p', text: 'You have full discretion to accept or decline any request.' },
      ],
    },
    {
      q: 'Is there a cost to join EUCA Match?',
      a: [
        { type: 'p', text: 'At present, EUCA Match is free for professionals.' },
        { type: 'p', text: 'There is no set end date for the free period.' },
      ],
    },
    {
      q: 'How do I get started as a professional?',
      a: [
        { type: 'p', text: 'To get started:' },
        { type: 'cta', href: 'https://app.eucamatch.com/login?exp=provider', label: 'For Professionals', variant: 'secondary' },
        { type: 'p', text: 'Sign in using:' },
        { type: 'list', items: ['Google', 'Apple', 'Or your email address'] },
        { type: 'p', text: 'Complete your professional profile and verification' },
        { type: 'p', text: 'Use EUCA Match on desktop or mobile' },
      ],
    },
  ]

  const renderQAGroup = (items, groupLabel) => (
    <div className="faq-group">
      <p className="eyebrow muted">{groupLabel}</p>
      <div className="faq-accordion">
        {items.map((item, idx) => (
          <details key={`${groupLabel}-${idx}`} className="faq-item">
            <summary className="faq-question">
              <span>{item.q}</span>
              <span className="faq-chevron" aria-hidden="true" />
            </summary>
            <div className="faq-answer">
              {item.a.map((block, i) => renderAnswerBlock(block, i))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )

  return (
    <section className="faq-page">
      <div className="faq-shell">
        <p className="eyebrow verification-eyebrow">FAQ</p>
        <h1>Frequently Asked Questions</h1>
        {renderQAGroup(clientFaq, 'For Clients')}
        {renderQAGroup(proFaq, 'For Professionals')}
      </div>
    </section>
  )
}

function PricingPage() {
  usePageMeta(
    'EUCA Match | Pricing & Fees',
    'Transparent pricing for EUCA Match clients and professionals across 8 industries.',
    '/pricing'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${SITE_URL}/pricing` },
    ],
  }

  type TierFeature = {
    key: string
    label: string
    description: string
  }

  type TierCategory = {
    key: string
    title: string
    features: TierFeature[]
  }

  const clientFeatureCategories: TierCategory[] = [
    {
      key: 'discover',
      title: 'Discovery & Matching',
      features: [
        {
          key: 'verification-first',
          label: 'Verification-first marketplace',
          description: 'Professionals are reviewed before they are listed and can accept client requests.',
        },
        {
          key: 'standard-search',
          label: 'Standard search by profession and keywords',
          description: 'Clients can search by profession, specialty, and keywords across supported industries.',
        },
        {
          key: 'ai-search',
          label: 'AI-assisted search',
          description: 'Clients can describe needs in plain language and get suggested professionals.',
        },
        {
          key: 'match-and-request',
          label: 'Direct match and request workflow',
          description: 'Clients send requests directly and professionals choose which requests to accept.',
        },
      ],
    },
    {
      key: 'bookings',
      title: 'Booking & Confidence',
      features: [
        {
          key: 'profiles-pricing-availability',
          label: 'Profiles, pricing notes, and availability context',
          description: 'Client-facing profiles surface key service details for informed booking decisions.',
        },
        {
          key: 'booking-management',
          label: 'Reschedule, re-book, and cancellation flow',
          description: 'Booking management actions are supported in-platform without an EUCA Match platform fee.',
        },
        {
          key: 'reviews',
          label: 'Post-appointment review flow',
          description: 'Clients can leave reviews after appointments, helping build transparent trust signals.',
        },
      ],
    },
    {
      key: 'pricing',
      title: 'Pricing',
      features: [
        {
          key: 'no-platform-fees',
          label: 'No EUCA Match platform fees',
          description: 'EUCA Match platform fees are $0 for both clients and professionals.',
        },
      ],
    },
  ]

  const professionalFeatureCategories: TierCategory[] = [
    {
      key: 'profile-intake',
      title: 'Profile & Intake',
      features: [
        {
          key: 'verification-first-pro',
          label: 'Verification-first marketplace',
          description: 'Professionals are reviewed before listing and can accept requests after approval.',
        },
        {
          key: 'public-profile',
          label: 'Public, shareable professional profile',
          description: 'Professionals receive a profile page suitable for discovery and direct sharing.',
        },
        {
          key: 'accept-when-ready',
          label: 'Accept clients when it suits you',
          description: 'Professionals control acceptance timing and can manage intake around existing workload.',
        },
        {
          key: 'existing-clients',
          label: 'Workflows for EUCA Match and existing clients',
          description: 'The same tools can be used for marketplace clients and existing private clients.',
        },
      ],
    },
    {
      key: 'verification',
      title: 'Verification',
      features: [
        {
          key: 'gov-id-upload',
          label: 'Government-issued photo ID upload',
          description: 'Upload a passport, driver licence, or national ID card during registration.',
        },
        {
          key: 'professional-license',
          label: 'Professional licence or registration check',
          description: 'Provide your profession/country registration details and supporting credential evidence where applicable.',
        },
        {
          key: 'cv-supporting-docs',
          label: 'CV / resume and supporting documents',
          description: 'Upload your CV or resume and any supporting documents for profile review.',
        },
        {
          key: 'stripe-identity-optional',
          label: 'Optional Stripe Identity verification ($5)',
          description: 'Stripe verifies a live selfie and government-issued photo ID. Successful verification adds an ID verified badge. The $5 fee covers Stripe verification and processing fees.',
        },
      ],
    },
    {
      key: 'payments',
      title: 'Payments & Billing',
      features: [
        {
          key: 'stripe-payments',
          label: 'Stripe-powered secure online payments',
          description: 'Professionals can accept card payments through Stripe within the workflow.',
        },
        {
          key: 'auto-invoice',
          label: 'Automatic invoicing',
          description: 'Invoices are generated after successful payments with no manual invoice setup required.',
        },
        {
          key: 'stripe-fees',
          label: 'Clear Stripe processing fee disclosure',
          description: 'Standard Stripe processing fees apply (currently 1.7% + $0.30 per transaction) while EUCA Match adds no platform payment fee.',
        },
        {
          key: 'no-platform-fees-pro',
          label: 'No EUCA Match platform fees',
          description: 'EUCA Match does not charge professionals a platform fee to join or accept clients.',
        },
      ],
    },
    {
      key: 'scheduling-meetings',
      title: 'Scheduling & Meetings',
      features: [
        {
          key: 'calendar-sync',
          label: 'Google Calendar + Calendly sync',
          description: 'Availability stays aligned across EUCA Match, Google Calendar, and Calendly.',
        },
        {
          key: 'video-links',
          label: 'Zoom + Google Meet link integration',
          description: 'For accepted bookings, professionals can send video meeting links manually or automatically using Zoom or Google Meet.',
        },
        {
          key: 'shareable-links',
          label: 'Shareable availability and booking links',
          description: 'Professionals can share booking access through links and availability slots.',
        },
      ],
    },
    {
      key: 'records-ai',
      title: 'Records & AI',
      features: [
        {
          key: 'notes-attachments',
          label: 'Structured booking notes and attachments',
          description: 'Per-booking notes support files and documents for workflow continuity.',
        },
        {
          key: 'ai-notes',
          label: 'AI-assisted note drafting and summaries',
          description: 'Professionals can draft and summarize notes using AI tuned for workflow support.',
        },
      ],
    },
  ]

  const renderFeatureLabel = (feature: TierFeature) => (
    <span className="pricing-feature-text">
      <span>{feature.label}</span>
      <span
        className="pricing-info-chip"
        tabIndex={0}
        data-tip={feature.description}
        aria-label={`${feature.label}: ${feature.description}`}
      >
        <Info aria-hidden="true" size={14} />
      </span>
    </span>
  )
  const renderFeatureGroups = (categories: TierCategory[]) => (
    <div className="pricing-feature-groups">
      {categories.map((category) => (
        <section key={category.key} className="pricing-feature-group">
          <p className="pricing-feature-group-title">{category.title}</p>
          <ul className="pricing-feature-list">
            {category.features.map((feature) => (
              <li key={`${category.key}-${feature.key}`} className="pricing-feature-item">
                <Check aria-hidden="true" className="pricing-feature-check" />
                {renderFeatureLabel(feature)}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )

  return (
    <section className="info-page pricing-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="pricing-plan-grid">
        <article className="pricing-plan-card">
          <div className="pricing-plan-header">
            <p className="pricing-plan-name">Clients</p>
            <div className="pricing-plan-price-row">
              <p className="pricing-plan-price">$0</p>
              <span className="pricing-plan-pill">Always free</span>
            </div>
            <p className="pricing-plan-copy">Use EUCA Match to discover, match, and manage bookings with no platform fee.</p>
          </div>
          {renderFeatureGroups(clientFeatureCategories)}
          <div className="pricing-cta-row">
            <Link className="btn cta" to="/search">
              Find Verified Professionals
            </Link>
          </div>
        </article>

        <article className="pricing-plan-card featured">
          <div className="pricing-plan-header">
            <p className="pricing-plan-name">Professionals</p>
            <div className="pricing-plan-price-row">
              <p className="pricing-plan-price">$0</p>
              <span className="pricing-plan-pill">No Platform fee</span>
            </div>
            <p className="pricing-plan-copy">Join, get verified, and run workflows with no EUCA Match platform fee.</p>
          </div>
          {renderFeatureGroups(professionalFeatureCategories)}
          <p className="pricing-stripe-note">
            <strong>Stripe fees only:</strong> Standard Stripe processing fees apply to paid invoices. EUCA Match does not charge a platform payment fee.
          </p>
          <div className="pricing-cta-row">
            <a className="btn cta pro-cta" href={providerSignupUrl}>
              For Professionals
            </a>
          </div>
        </article>
      </div>
    </section>
  )
}

function DocsPage() {
  usePageMeta(
    'EUCA Match | Help Centre',
    'Help Centre guides for clients and professionals using EUCA Match.',
    '/help-centre'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Help Centre', item: `${SITE_URL}/help-centre` },
    ],
  }
  return (
    <section className="info-page docs-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header">
        <p className="eyebrow hero-eyebrow">Help Centre</p>
        <h2>EUCA Match Help Centre</h2>
        <p>Practical how-to guides for clients and professionals.</p>
      </div>
      <div className="docs-layout">
        <aside className="docs-toc">
          <div className="docs-toc-card">
            <h3>Contents</h3>
            <ul>
              <li><a href="#clients">Clients</a></li>
              <li><a href="#professionals">Professionals</a></li>
              <li><a href="#notes">Notes</a></li>
              <li><a href="#payments">Payments</a></li>
              <li><a href="#zoom">Zoom meetings</a></li>
              <li><a href="#google-calendar">Google Calendar sync</a></li>
              <li><a href="#calendly">Calendly sync</a></li>
              <li><a href="#support">Support</a></li>
            </ul>
          </div>
        </aside>
        <div className="docs-content">
          <section className="docs-section">
            <h3 id="clients"><a className="docs-anchor" href="#clients">Clients</a></h3>
            <p>Start here if you are looking for a verified professional.</p>
            <h4 id="client-requirements"><a className="docs-anchor" href="#client-requirements">Requirements</a></h4>
            <ul>
              <li>EUCA Match client account.</li>
              <li>Email, Google, or Apple sign-in.</li>
            </ul>
            <h4 id="client-login"><a className="docs-anchor" href="#client-login">Login as a client</a></h4>
            <ol>
              <li>Go to <a href="https://app.eucamatch.com/login?exp=client">app.eucamatch.com/login</a>.</li>
              <li>Select Client.</li>
              <li>Sign in with Google, Apple, or email.</li>
            </ol>
            <h4 id="client-search"><a className="docs-anchor" href="#client-search">Find a professional</a></h4>
            <ol>
              <li>Marketing site: search at <a href="https://eucamatch.com/search">eucamatch.com/search</a>.</li>
              <li>Open a professional profile to view details.</li>
              <li>App: use the Clients dashboard to search and open a profile.</li>
            </ol>
            <h4 id="client-book"><a className="docs-anchor" href="#client-book">Request a booking</a></h4>
            <ol>
              <li>From a profile, click Request booking.</li>
              <li>Enter your contact details and submit.</li>
              <li>Once a professional accepts, coordinate the time directly.</li>
            </ol>
            <h4 id="client-manage"><a className="docs-anchor" href="#client-manage">Manage your bookings</a></h4>
            <ul>
              <li>Track request status in the app.</li>
              <li>If an invoice is issued, the payment link will be delivered by email.</li>
            </ul>
            <h4 id="client-troubleshoot"><a className="docs-anchor" href="#client-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>No response:</strong> professionals respond manually; check again later.</li>
              <li><strong>Can’t log in:</strong> use the same email you booked with.</li>
              <li><strong>Payment link missing:</strong> invoices are sent by the professional via Stripe.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="professionals"><a className="docs-anchor" href="#professionals">Professionals</a></h3>
            <p>Use these steps to manage requests, bookings, and clients.</p>
            <h4 id="pro-requirements"><a className="docs-anchor" href="#pro-requirements">Requirements</a></h4>
            <ul>
              <li>EUCA Match professional account.</li>
              <li>Profile submitted and approved.</li>
            </ul>
            <h4 id="pro-login"><a className="docs-anchor" href="#pro-login">Login and approval</a></h4>
            <ol>
              <li>Go to <a href="https://app.eucamatch.com/login?exp=provider">app.eucamatch.com/login</a>.</li>
              <li>Complete your professional profile.</li>
              <li>Submit for approval to start receiving requests.</li>
            </ol>
            <h4 id="pro-dashboard"><a className="docs-anchor" href="#pro-dashboard">Dashboard overview</a></h4>
            <ul>
              <li><strong>Bookings:</strong> Pending and accepted requests in one list. Click a client name to view details.</li>
              <li><strong>Clients:</strong> A consolidated list of every client you have interacted with.</li>
            </ul>
            <h4 id="pro-accept"><a className="docs-anchor" href="#pro-accept">Accept or decline requests</a></h4>
            <ol>
              <li>Open a client in Bookings.</li>
              <li>Review details and accept to confirm the booking.</li>
              <li>Declined requests will no longer appear in Bookings.</li>
            </ol>
            <h4 id="pro-add-booking"><a className="docs-anchor" href="#pro-add-booking">Add a booking manually</a></h4>
            <ol>
              <li>Click Add Booking in Dashboard or Calendar.</li>
              <li>Enter client name, email, phone, and optional time.</li>
              <li>Submit to create a confirmed booking.</li>
              <li>If a time is set, it appears in Calendar immediately.</li>
            </ol>
            <h4 id="pro-confirmed"><a className="docs-anchor" href="#pro-confirmed">Confirmed actions</a></h4>
            <ul>
              <li>Notes: store booking notes per client and per session.</li>
              <li>Payment: issue Stripe invoices and track paid status.</li>
              <li>Meeting: generate Zoom links and resend as needed.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="notes"><a className="docs-anchor" href="#notes">Notes</a></h3>
            <p>Notes are saved per booking and only visible to the professional who created them.</p>
            <h4 id="notes-open"><a className="docs-anchor" href="#notes-open">Open Notes</a></h4>
            <ol>
              <li>Go to a confirmed booking in Dashboard.</li>
              <li>Click Notes.</li>
              <li>Select the booking on the left to view its notes.</li>
            </ol>
            <h4 id="notes-edit"><a className="docs-anchor" href="#notes-edit">Write and save</a></h4>
            <ul>
              <li>Notes are private to the professional and saved to the EUCA Match backend.</li>
              <li>Notes autosave as you type; use Save for a manual snapshot.</li>
              <li>Publish locks notes into a read-only view; Unpublish returns to edit mode.</li>
              <li>Use the formatting toolbar to add rich text, links, images, and attachments.</li>
              <li>Drag to resize embedded images once inserted.</li>
            </ul>
            <h4 id="notes-troubleshoot"><a className="docs-anchor" href="#notes-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>Can’t see notes:</strong> make sure you selected the correct booking on the left.</li>
              <li><strong>Notes look locked:</strong> click Unpublish to edit.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="payments"><a className="docs-anchor" href="#payments">Payments (Stripe Connect)</a></h3>
            <p>EUCA Match does not charge platform fees. Stripe processing fees apply and are paid by the connected professional account.</p>
            <h4 id="payments-requirements"><a className="docs-anchor" href="#payments-requirements">Requirements</a></h4>
            <ul>
              <li>Confirmed booking.</li>
              <li>Stripe Connect account completed (Finish setup if required).</li>
            </ul>
            <h4 id="payments-setup"><a className="docs-anchor" href="#payments-setup">Set up Stripe</a></h4>
            <ol>
              <li>Open a confirmed booking.</li>
              <li>Click Set up payments (or Finish setup).</li>
              <li>Complete Stripe onboarding and return to EUCA Match.</li>
            </ol>
            <h4 id="payments-issue"><a className="docs-anchor" href="#payments-issue">Issue an invoice</a></h4>
            <ol>
              <li>Open a confirmed booking and click Payment.</li>
              <li>Enter the amount, add a memo, and set an optional due date.</li>
              <li>Optional: enable GST (10%).</li>
              <li>Optional: pass Stripe fees to the client to keep your net amount unchanged.</li>
              <li>Send the invoice. Stripe emails the client the payment link automatically.</li>
            </ol>
            <h4 id="payments-status"><a className="docs-anchor" href="#payments-status">Invoice status</a></h4>
            <ul>
              <li>Invoice sent appears in amber; Paid appears in green.</li>
              <li>Use View to open the hosted invoice or PDF.</li>
              <li>You can resend or issue a new invoice as needed.</li>
            </ul>
            <h4 id="payments-troubleshoot"><a className="docs-anchor" href="#payments-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>Payment button disabled:</strong> Stripe is not connected or onboarding is incomplete.</li>
              <li><strong>No payment email:</strong> Stripe sends the invoice email; resend from the Payment panel.</li>
              <li><strong>GST/fees:</strong> toggle options before sending to include them on the invoice.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="zoom"><a className="docs-anchor" href="#zoom">Zoom meetings</a></h3>
            <p>Connect Zoom to generate meeting links for confirmed bookings and share them with clients in one click.</p>
            <h4 id="zoom-requirements"><a className="docs-anchor" href="#zoom-requirements">Requirements</a></h4>
            <ul>
              <li>EUCA Match professional account (approved).</li>
              <li>Zoom account with permission to create meetings.</li>
            </ul>
            <h4 id="zoom-connect"><a className="docs-anchor" href="#zoom-connect">Connect Zoom</a></h4>
            <ol>
              <li>Log in to the EUCA Match app.</li>
              <li>Open Dashboard and select a confirmed booking.</li>
              <li>Click Meeting and choose Connect Zoom.</li>
              <li>Authorize EUCA Match in Zoom and return to the booking.</li>
            </ol>
            <h4 id="zoom-use"><a className="docs-anchor" href="#zoom-use">Create and send links</a></h4>
            <ul>
              <li>Confirm or enter the meeting time (based on your calendar time zone).</li>
              <li>Click Create meeting link to email the client and save the link.</li>
              <li>Use Resend to send the link again.</li>
            </ul>
            <h4 id="zoom-remove"><a className="docs-anchor" href="#zoom-remove">Remove the app</a></h4>
            <ul>
              <li>Disconnect Zoom from the Meeting panel to stop access.</li>
              <li>Optional: revoke access from your Zoom account settings.</li>
            </ul>
            <h4 id="zoom-troubleshoot"><a className="docs-anchor" href="#zoom-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>Can’t connect:</strong> check that your Zoom account allows meeting creation.</li>
              <li><strong>Meeting time missing:</strong> enter or confirm the time before creating the link.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="google-calendar"><a className="docs-anchor" href="#google-calendar">Google Calendar sync</a></h3>
            <p>Sync Google Calendar to block busy times and add new EUCA Match bookings to your calendar.</p>
            <h4 id="google-connect"><a className="docs-anchor" href="#google-connect">Connect Google Calendar</a></h4>
            <ol>
              <li>Go to Calendar in the EUCA Match app.</li>
              <li>Click Connect Google Calendar.</li>
              <li>Select the calendars that should block availability.</li>
              <li>Choose the write calendar for EUCA Match bookings.</li>
            </ol>
            <h4 id="google-sync"><a className="docs-anchor" href="#google-sync">How sync works</a></h4>
            <ul>
              <li>Two-way sync: Google busy times block EUCA Match, and EUCA bookings are added to Google.</li>
              <li>Cancel or reschedule in EUCA Match to update the Google event.</li>
              <li>You can disconnect at any time from the Calendar page.</li>
            </ul>
            <h4 id="google-troubleshoot"><a className="docs-anchor" href="#google-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>No calendars:</strong> verify the Google account has calendars and is authorized.</li>
              <li><strong>Busy times missing:</strong> reconnect and ensure calendars are selected.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="calendly"><a className="docs-anchor" href="#calendly">Calendly sync</a></h3>
            <p>Sync Calendly to block EUCA Match availability when you have scheduled Calendly events.</p>
            <h4 id="calendly-connect"><a className="docs-anchor" href="#calendly-connect">Connect Calendly</a></h4>
            <ol>
              <li>Go to Calendar in the EUCA Match app.</li>
              <li>Click Connect Calendly and authorize access.</li>
              <li>Optional: select specific event types to block.</li>
            </ol>
            <h4 id="calendly-sync"><a className="docs-anchor" href="#calendly-sync">How sync works</a></h4>
            <ul>
              <li>One-way sync: Calendly events block availability in EUCA Match.</li>
              <li>EUCA Match does not create or edit Calendly events.</li>
              <li>Disconnect any time from the Calendar page.</li>
            </ul>
            <h4 id="calendly-troubleshoot"><a className="docs-anchor" href="#calendly-troubleshoot">Troubleshooting</a></h4>
            <ul>
              <li><strong>No events:</strong> ensure Calendly has upcoming scheduled events.</li>
              <li><strong>Wrong event types:</strong> update your selection in the Calendly settings.</li>
            </ul>
          </section>

          <section className="docs-section">
            <h3 id="support"><a className="docs-anchor" href="#support">Support</a></h3>
            <p>We are here to help with account, booking, payment, and sync issues.</p>
            <h4 id="support-contact"><a className="docs-anchor" href="#support-contact">Contact us</a></h4>
            <ul>
              <li>Email <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>.</li>
              <li>Include your account email, booking ID (if relevant), and screenshots.</li>
            </ul>
            <h4 id="support-response"><a className="docs-anchor" href="#support-response">What to expect</a></h4>
            <ul>
              <li>We respond as quickly as possible during Australian business hours.</li>
              <li>If the issue involves Stripe, Google, Calendly, or Zoom, we may request additional details.</li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  )
}

function PrivacyPage() {
  usePageMeta(
    'EUCA Match | Privacy Policy',
    'Read the EUCA Match privacy policy covering how we collect, use, and protect your data.',
    '/privacypolicy'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacypolicy` },
    ],
  }
  return (
    <section className="info-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header">
        <p className="eyebrow hero-eyebrow">About</p>
        <h2>Privacy</h2>
        <p>How EUCA Match collects, stores, and protects your data</p>
        <p>Last updated: 3 February 2026</p>
        <p><strong>EUCA MATCH PTY LTD (ABN 40 692 614 341)</strong> operates EUCA Match and is the controller of personal information collected through this site.</p>
      </div>
      <div className="info-content">
        <h3>1. Collection of personal information</h3>
        <p>We collect only what is necessary to create and operate your EUCA Match account and to connect clients with verified professionals. If you sign in with Google, we receive only your Google user ID and primary email for authentication. If you sign in with Apple, we receive only the Apple user identifier and the email you choose to share (which may be a private relay). If you sign in with email, we use your email for authentication and necessary communications. We do not request or store friends lists, posts, messages, photos, events, or other Google or Apple data.</p>

        <h3>2. Use of personal information</h3>
        <p>We use your information to authenticate you, operate the platform, notify relevant professionals, and meet our legal obligations. We do not sell your personal information. We share your contact details only with the professional who accepts your match so you can coordinate directly.</p>

        <h3>3. Google Calendar sync (optional for professionals)</h3>
        <p>Professionals can optionally connect Google Calendar to keep their availability accurate and to create EUCA Match booking events in their calendar. If you choose to connect, we request access to your calendar list metadata (calendar IDs, names, and access level) and free/busy time ranges, and permission to create, update, and cancel EUCA Match booking events in the calendar you select.</p>
        <p>We do not read or store your existing event content (titles, descriptions, attendees) from your personal calendar. We only create and manage EUCA Match booking events and use free/busy time ranges to block conflicts.</p>
        <p>We store only what is necessary to maintain the connection and sync (refresh token, selected calendar IDs, write‑calendar ID, sync timestamps, and EUCA Match event IDs). We do not use Google Calendar data for advertising, do not sell it, and do not share it with third parties except service providers that help us run the platform under confidentiality obligations.</p>
        <p>You can disconnect at any time in EUCA Match, which removes stored Google Calendar tokens and sync settings and stops access. You can also revoke access from your Google Account Security settings.</p>
        <p>EUCA Match’s use and transfer of information received from Google APIs will comply with the Google API Services User Data Policy, including the Limited Use requirements.</p>

        <h3>4. Zoom video links (optional for professionals)</h3>
        <p>Professionals may optionally connect Zoom so we can generate meeting links for bookings. If you connect Zoom, we may receive your Zoom user ID and basic account details (such as name and email), and we store the OAuth tokens required to create, update, or cancel meetings on your behalf.</p>
        <p>We store only meeting metadata needed to run the service (for example, meeting ID, scheduled time, host, join URL, and meeting status) and share the join link with the client and professional for the booking. We do not access or store meeting content such as audio, video, chat, or recordings; those are processed by Zoom under its own policies and settings you control.</p>
        <p>We do not use Zoom data for advertising, do not sell it, and do not share it with third parties except service providers that help us run the platform under confidentiality obligations. Use of Zoom data is limited to providing and operating the Zoom link feature.</p>
        <p>You can disconnect Zoom at any time in EUCA Match, which removes stored tokens and stops access. You can also revoke access from your Zoom account settings.</p>

        <h3>5. Payments and invoicing (Stripe)</h3>
        <p>EUCA Match uses Stripe to process payments and issue invoices on behalf of professionals. When you pay an invoice, your payment details are handled directly by Stripe. EUCA Match does not store full card numbers or bank account details.</p>
        <p>We may store limited payment and invoice data necessary to operate the platform, such as invoice amounts, currency, payment status, client name/email, and a Stripe invoice or transaction ID. This information is used for billing records, dispute handling, fraud prevention, and compliance.</p>
        <p>Professionals who use payments connect a Stripe account (Stripe Connect). Stripe may collect additional identity and business information to comply with financial regulations. That information is processed by Stripe under its own privacy policy. Professionals can manage their payment details and payout settings in Stripe.</p>

        <h3>6. Public professional profiles</h3>
        <p>When a professional is verified, we publish their profile on our website and app so clients can view and request them. Professionals may request removal or updates to their published information at any time by emailing <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>; verified requests are actioned within two business days.</p>

        <h3>7. Storage and security</h3>
        <p>Your information is stored on secure AWS infrastructure in Australia with access restricted to authorised EUCA MATCH PTY LTD personnel. Data is encrypted at rest and protected in transit via TLS/SSL. Backups are taken daily. Infrastructure is configured with least-privilege access and industry-standard controls. Professional credentials are reviewed before listing, and we apply reasonable administrative, technical, and physical safeguards to protect your information.</p>
        <p>AWS data centres use layered physical controls (human and video surveillance, intrusion detection, controlled access). AWS holds globally recognised certifications, including PCI DSS Level 1, ISO 27001, SOC 1/2/3, and FIPS 140-2 validated modules for cryptographic operations.</p>

        <h3>8. Disclosure</h3>
        <p>We may disclose information to service providers that support our platform (for example, cloud hosting and payment processors) under confidentiality obligations, and as required by law or to protect legal rights. We do not disclose more than is necessary for the relevant purpose.</p>

        <h3>9. Retention and deletion</h3>
        <p>We retain personal information only for as long as needed to provide the service or meet legal requirements. You may request deletion of your account and associated data — including Google and Apple authentication data — by emailing <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>. We will action verified deletion requests within two business days.</p>

        <h3>10. Your rights and contact</h3>
        <p>You may request access to, or correction of, the personal information we hold about you, and you may withdraw consent for non-essential uses. To exercise your rights or raise a concern, contact <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>. If you are dissatisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner.</p>
      </div>
    </section>
  )
}


function TermsPage() {
  usePageMeta(
    'EUCA Match | Terms of Use',
    'Review the EUCA Match terms of use covering your obligations as a client or professional.',
    '/terms'
  )
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Terms of Use', item: `${SITE_URL}/terms` },
    ],
  }
  return (
    <section className="info-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <div className="section-header">
        <p className="eyebrow hero-eyebrow">Terms</p>
        <h2>Terms of Use</h2>
        <p>Last updated: 3 February 2026</p>
        <p><strong>EUCA MATCH PTY LTD (ABN 40 692 614 341)</strong> operates EUCA Match. These Terms govern your use of the platform.</p>
      </div>
      <div className="info-content">
        <p><strong>Welcome to EUCA Match.</strong> By accessing or using eucamatch.com (the “Platform”), you agree to these Terms of Use. If you do not agree, do not use the Platform.</p>

        <h3>1. Operator</h3>
        <p>The Platform is owned and operated by <strong>EUCA MATCH PTY LTD (ABN 40 692 614 341)</strong> (“EUCA Match”, “we”, “us”).</p>

        <h3>2. Nature of the service</h3>
        <p>The Platform matches clients with independent professionals. EUCA Match does not employ, supervise, or guarantee the services of professionals and does not provide professional advice. Any engagement is solely between the client and the professional.</p>
        <p>When you authenticate using Google or Apple, we use the credentials solely to sign you in. For Apple, you may choose to share a private relay email; we will use the email Apple provides to operate your account and send necessary communications.</p>

        <h3>3. Eligibility and accounts</h3>
        <p>You must provide accurate information, maintain the security of your credentials, and are responsible for all activity under your account. We may suspend or terminate accounts that breach these Terms or pose risk to the Platform.</p>

        <h3>4. Authentication</h3>
        <p>If you use Google Sign-In, we receive only your Google user ID and primary email for authentication. If you use Apple Sign In, we receive only your Apple user identifier and the email Apple provides (which may be a private relay). If you sign in with email, we use your email for authentication and necessary communications. We do not request friends list, posts, messages, photos, events, or other Google or Apple data.</p>

        <h3>5. Google Calendar sync (optional)</h3>
        <p>Professionals may optionally connect Google Calendar to sync availability and create EUCA Match booking events. If you connect, you authorise EUCA Match to access your calendar list metadata, read free/busy time ranges, and create/update/cancel EUCA Match booking events in the calendar you select.</p>
        <p>You can disconnect at any time. Sync depends on third-party services (Google) and may be delayed or unavailable. EUCA Match does not guarantee that calendar sync will always be accurate or uninterrupted, and you remain responsible for reviewing your availability and bookings.</p>

        <h3>6. Zoom video links (optional)</h3>
        <p>Professionals may optionally connect Zoom so EUCA Match can create and manage meeting links for bookings. If you connect Zoom, you authorise EUCA Match to create, update, and cancel Zoom meetings on your behalf and to share the join link with the client and professional for the relevant booking.</p>
        <p>Zoom is a third-party service. Your use of Zoom is governed by Zoom’s terms and privacy policy. You are responsible for any required consents, professional obligations, and the content of meetings, recordings, or messages. EUCA Match does not provide video conferencing services and is not responsible for Zoom’s availability, performance, or any issues arising from use of Zoom.</p>
        <p>We may suspend or disable the Zoom integration if required for safety, compliance, or technical reasons.</p>

        <h3>7. Fees</h3>
        <p>There is no fee for clients or professionals to request or accept bookings.</p>

        <h3>8. Professional verification</h3>
        <p>We review professional credentials provided to us, but we do not warrant their accuracy or completeness. Professionals remain solely responsible for their qualifications, representations, and services.</p>

        <h3>9. Publication of professional profiles</h3>
        <p>Verified professionals are displayed on our website and app so clients can view and request them. Professionals may request removal or updates to their published information at any time by emailing <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>; we action verified requests within two business days.</p>

        <h3>10. Payments (Stripe)</h3>
        <p>Payments and invoices are processed by Stripe. By using payments, you agree to Stripe’s applicable terms and policies. EUCA Match is not a bank or payment processor and does not control Stripe’s services.</p>
        <p>Clients authorise Stripe to process payments for invoices issued by professionals. Stripe may charge processing fees and may delay, reverse, or dispute transactions in accordance with its rules. Refunds, chargebacks, and disputes are handled under Stripe policies and applicable law.</p>

        <h3>11. Stripe Connect (professionals)</h3>
        <p>Professionals who accept payments connect a Stripe account (Stripe Connect). Stripe may require identity or business information and may restrict or suspend accounts to comply with legal or risk requirements. EUCA Match does not guarantee account approval or continued eligibility.</p>
        <p>Professionals are responsible for taxes (including GST where applicable), invoicing requirements, and compliance with any professional or regulatory obligations.</p>

        <h3>12. User content and conduct</h3>
        <p>You must not misuse the Platform. Content you submit must be accurate, lawful, and not infringe third-party rights. We may remove content or restrict access where we reasonably believe there is a breach or risk to users.</p>

        <h3>13. Reviews</h3>
        <p>Reviews must reflect genuine experiences. We may remove reviews that are false, misleading, harmful, or inconsistent with these Terms. Average ratings shown are simple averages of published reviews for that professional.</p>
        <p>We may add reviews from publicly available sources (such as Google or Facebook) to EUCA Match professional profiles.</p>

        <h3>14. Privacy</h3>
        <p>Your personal information is handled in accordance with our Privacy Policy. By using the Platform, you consent to the collection and use of information as described in that policy.</p>

        <h3>15. Account and data deletion</h3>
        <p>You may request deletion of your account and associated data — including Google and Apple authentication data — by emailing <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a>. We will action verified deletion requests within two business days.</p>

        <h3>16. Disclaimers and limitation of liability</h3>
        <p>To the maximum extent permitted by law, the Platform is provided “as is” and “as available”. We do not guarantee uninterrupted access or any particular outcome. EUCA Match is not liable for any loss, damage, or dispute arising from interactions between clients and professionals.</p>

        <h3>17. Changes</h3>
        <p>We may update these Terms from time to time. Continued use after an update constitutes acceptance of the revised Terms.</p>

        <h3>18. Governing law</h3>
        <p>These Terms are governed by the laws of New South Wales, Australia. You submit to the exclusive jurisdiction of the courts of New South Wales.</p>

        <h3>19. Contact</h3>
        <p>For questions about these Terms, contact: <a href="mailto:contact@eucamatch.com">contact@eucamatch.com</a></p>

        <h3>20. Platform Role and Service Providers</h3>
        <p>EUCA Match is a technology platform operated by EUCA Match Pty Ltd.</p>
        <p>The platform operates under the EUCA MD® framework, which is licensed from Dr Lily Vu Pty Ltd.</p>
        <p>EUCA Match Pty Ltd provides technology, booking, and matching services only and does not provide medical, allied health, legal, financial, or other professional services unless expressly stated.</p>
        <p>All medical services (including services provided by doctors, medical specialists, and allied health practitioners) are provided independently by verified healthcare professionals listed on the platform.</p>
        <p>All non-medical services are provided independently by third-party professionals.</p>
        <p>Professional services are provided under the responsibility of the individual practitioner or provider. EUCA Match Pty Ltd and Dr Lily Vu Pty Ltd do not supervise, direct, or control the professional services provided, except where expressly stated.</p>
        <p>Professional registration and regulatory status are verified at the time of onboarding and may vary by jurisdiction.</p>
      </div>
    </section>
  )
}
